    (function () {
      // Custom booking flow on Boulevard's headless Client API — replaces
      // the iframe embed entirely. The iframe had no way to change location
      // once deep-linked (a real incident) and blocked automated testing
      // past its first screen; this is our own DOM, so both problems are
      // gone by construction. Header now carries a persistent studio pill
      // (once a studio is chosen) so switching is available from every
      // screen, not just via back-navigation to the start.
      //
      // Deliberately its own Netlify site, isolated from creator-gifting-site
      // — that site's own automated jobs (e.g. its scheduled snapshot
      // refresh) commit to git and trigger a full rebuild there, which
      // silently deleted this function when it lived in that repo
      // (confirmed live 2026-08-29, ~7 hours of broken bookings). This
      // site has no git remote and nothing auto-deploys it — only a
      // deliberate `netlify deploy --prod` from ~/clients/wthn/booking-widget-site.
      var API_URL = 'https://wthn-booking-widget.netlify.app/.netlify/functions/booking';

      var overlay, body, backBtn, closeBtn, pillBtn, switcherEl, defaultBanner, modalEl;
      var state = {};
      // Incremented whenever the screen's assumptions stop holding, so a
      // response still in flight knows it is talking to a page that has gone.
      // Declared HERE, next to state, and not beside resetState: `var` hoists
      // but the assignment does not, and one resetState() before that line ran
      // would make this NaN -- and NaN !== NaN would strand every request the
      // widget ever makes.
      var navGeneration = 0;
      var cachedLocations = null;

      // Boulevard has no browser-side UTM capture of its own for a headless
      // integration like this — so we capture it here and pass it through as
      // Boulevard's own `referralSource` field (shows on the client/cart
      // record in Boulevard's dashboard), alongside GA4's automatic
      // session-based attribution. sessionStorage (not just the current
      // URL) so a visitor who lands on a campaign link, then browses to a
      // different page before opening the widget, still gets credited.
      var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
      function captureUtm() {
        try {
          var params = new URLSearchParams(window.location.search);
          var found = {};
          UTM_KEYS.forEach(function (k) { var v = params.get(k); if (v) found[k] = v; });
          if (Object.keys(found).length) {
            sessionStorage.setItem('blvd_utm', JSON.stringify(found));
          }
          var stored = sessionStorage.getItem('blvd_utm');
          return stored ? JSON.parse(stored) : null;
        } catch (e) {
          return null;
        }
      }
      // Shared by every entry point that can carry a #blvd?location=&bucket=
      // target — a clicked on-page link and the page's own incoming URL
      // hash (see init()) both parse it the same way.
      function parseBlvdTarget(hashOrHref) {
        var str = hashOrHref || '';
        var qIndex = str.indexOf('?');
        var params = qIndex >= 0 ? new URLSearchParams(str.slice(qIndex + 1)) : null;
        return {
          locationId: params && params.get('location'),
          bucketKey: params && params.get('bucket'),
          treatment: params && params.get('treatment'),
          itemId: params && params.get('item'),
          staffId: params && params.get('staff'),
          // Sixth deep-link dimension — not a Boulevard concept at all, just
          // a plain-text tag for which marketing page sent this visitor in
          // (e.g. "Pain Package" from the Pain What We Treat page). Carried
          // into referralSource() below so it lands on the client/
          // appointment record in Boulevard's dashboard, same mechanism as
          // UTM capture (2026-09-02, on request).
          condition: params && params.get('condition'),
        };
      }

      function referralSource() {
        var utm = captureUtm();
        var parts = utm ? Object.keys(utm).map(function (k) { return k + '=' + utm[k]; }) : [];
        // state.deepLinkCondition (set in openModal — see its comment) is
        // consumed once per modal open, same lifetime as the other deep-
        // link dimensions, so it's still set here even though showMenu()
        // (this function's only caller) runs a step later.
        if (state.deepLinkCondition) parts.push(state.deepLinkCondition);
        if (!parts.length) return undefined;
        return parts.join(' | ');
      }
      // Capture on every page load (not just when the widget opens) — a
      // visitor can land on a campaign URL, then browse a few pages before
      // ever clicking BOOK NOW, by which point the UTM params are gone from
      // the current URL.
      captureUtm();

      // Remembers the client's own contact details between visits so a
      // returning client doesn't retype their name, email and phone every
      // single time. The old Boulevard widget solved this with an in-flow
      // "Returning Clients: log in" step; this widget has no in-flow login,
      // so returning clients were re-entering all four fields on every
      // booking. Measured 2026-09-04: self-service bookings by RETURNING
      // clients fell 39% after launch while FIRST-TIME held flat (+0.7%),
      // which is what pointed at this. This is a stopgap for that gap, not
      // a replacement for real login — it is per-device, per-browser, and
      // holds no card and no account.
      var CONTACT_KEY = 'wthn_blvd_contact';
      // REMEMBERED SESSION. Raised from in-memory-only on 2026-09-24: members
      // were entering a code on every single visit, which is the complaint we
      // actually got, and Boulevard has no password login we can drive (see
      // the 24 Sep investigation). The handle is AES-GCM sealed and carries
      // its own 30-day expiry, so the browser holding it cannot forge or
      // extend it -- the worst a stolen copy does is what a stolen phone
      // already does.
      //
      // The name is stored ALONGSIDE it purely so the widget can say who it
      // remembered. A month is long enough that a silently-resumed session on
      // a shared device is the real risk here, and the answer to that is
      // showing it rather than hiding it.
      var SESSION_KEY = 'wthn_blvd_session';
      function loadSavedSession() {
        try {
          var raw = window.localStorage.getItem(SESSION_KEY);
          if (!raw) return null;
          var v = JSON.parse(raw);
          if (!v || typeof v.sessionId !== 'string' || !v.sessionId) return null;
          // Client-side expiry is a courtesy for the UI; the server rejects a
          // stale handle regardless, so this can never be the only check.
          if (typeof v.exp === 'number' && Date.now() > v.exp) { clearSavedSession(); return null; }
          return { sessionId: v.sessionId, firstName: typeof v.firstName === 'string' ? v.firstName : '' };
        } catch (e) { return null; }
      }
      function saveSession(sessionId, firstName) {
        try {
          window.localStorage.setItem(SESSION_KEY, JSON.stringify({
            sessionId: sessionId,
            firstName: firstName || '',
            exp: Date.now() + 30 * 24 * 60 * 60 * 1000,
          }));
        } catch (e) { /* private mode, quota: remembering is a nicety */ }
      }
      function clearSavedSession() {
        try { window.localStorage.removeItem(SESSION_KEY); } catch (e) {}
      }
      function loadSavedContact() {
        try {
          var raw = window.localStorage.getItem(CONTACT_KEY);
          if (!raw) return {};
          var c = JSON.parse(raw);
          // only the four booking fields, never anything else that may have
          // been written into this key by an older/newer build
          return {
            firstName: typeof c.firstName === 'string' ? c.firstName : '',
            lastName: typeof c.lastName === 'string' ? c.lastName : '',
            email: typeof c.email === 'string' ? c.email : '',
            phoneNumber: typeof c.phoneNumber === 'string' ? c.phoneNumber : '',
          };
        } catch (e) {
          // private mode, blocked storage, or malformed JSON — a returning
          // client just gets the empty form they'd have had anyway
          return {};
        }
      }
      function saveContact(c) {
        try {
          window.localStorage.setItem(CONTACT_KEY, JSON.stringify({
            firstName: c.firstName || '', lastName: c.lastName || '',
            email: c.email || '', phoneNumber: c.phoneNumber || '',
          }));
        } catch (e) { /* never let storage failure block a booking */ }
      }

      // Survives resetState so closing and reopening the modal doesn't throw
      // away a login the client already completed on this page. Memory only,
      // so it still dies with the tab.
      // cartOwned is NOT the same as loggedIn, and the difference is the whole
      // reason a remembered session is safe to keep for a month. The saved card
      // and the membership voucher live on the CART the login code took
      // ownership of, and that code is single-use (proven in production 19-20
      // Sep) -- so a session restored on a later visit can prove who you are,
      // but it cannot reach the card. Everything card-shaped gates on
      // cartOwned; everything identity-shaped gates on loggedIn.
      var loginSession = { sessionId: null, savedCards: [], loggedIn: false, verifiedAt: 0, rePrompted: false, firstName: '', cartOwned: false };
      (function restoreRememberedSession() {
        var saved = loadSavedSession();
        if (!saved) return;
        loginSession.sessionId = saved.sessionId;
        loginSession.firstName = saved.firstName;
        loginSession.loggedIn = true;
        // cartOwned stays false: the handle proves identity, not a live cart.
      })();

      // A session that lapses after a while genuinely deserves a re-login. A
      // session Boulevard rejects SECONDS after issuing it does not -- sending
      // that person back to the code form just resets it, and they type the
      // code again, and again. Spencer hit exactly that loop on 2026-09-10:
      // login-verify succeeded (real client, 236-char token, dobRequired
      // false), then my-appointments came back "redirect 302 to /login", and
      // the catch below re-prompted with onSuccess pointing at the very call
      // that had just failed.
      //
      // So: re-prompt at most ONCE, and never when the token is less than a
      // minute old. Anything else lands on the honest dead-end screen with a
      // route to Boulevard's own portal, where their appointments really are.
      function sessionRejectedIsRetryable() {
        if (loginSession.rePrompted) return false;
        if (loginSession.verifiedAt && Date.now() - loginSession.verifiedAt < 60000) return false;
        return true;
      }

      function showAccountUnavailable(title) {
        setBack(showLocations);
        body.innerHTML =
          '<h2 class="blvd-title">' + escapeHtml(title || 'Your appointments') + '</h2>' +
          '<p class="blvd-subtitle">We can\u2019t load your account here right now. ' +
          'You can still see and manage your appointments, and the card you have on file, ' +
          'in your WTHN account.</p>' +
          '<a class="blvd-btn blvd-btn--primary" href="https://blvd.app/@wthn/login" target="_blank" rel="noopener">Open your account</a>' +
          '<p class="blvd-note" style="margin-top:14px">Booking a new session works normally \u2014 ' +
          '<button type="button" class="blvd-linkbtn" id="blvd-acct-book">book a session</button>.</p>';
        var b = document.getElementById('blvd-acct-book');
        if (b) b.addEventListener('click', showLocations);
      }

      // Boulevard cannot evaluate an offer's NEW_CLIENT restriction while the
      // cart has no client attached to it. WELCOME20 ("20% off your first
      // session", restricted to NEW_CLIENT) therefore discounted nothing, and
      // the widget cheerfully reported "Code applied." beside an unchanged
      // $120 total — while /pages/acupuncture, a paid-search landing page,
      // promised $96. Measured 2026-09-09: identical cart and code, $0
      // discount with no client attached, $24.00 with a brand-new one.
      //
      // updateCartClientInfo is idempotent and confirm/checkout call it again
      // at the end regardless, so this only ever moves the call EARLIER. Done
      // here, immediately before a code is applied, rather than when the
      // contact fields blur: it removes any race between typing and clicking
      // Apply, and costs one request instead of one per keystroke.
      var clientAttachedToCart = false;
      function ensureClientOnCart() {
        var c = state.contact;
        if (clientAttachedToCart) return Promise.resolve();
        if (!c || !c.email || !c.firstName || !c.lastName) return Promise.resolve();
        return api('update-client-info', {
          cartId: state.cartId, email: c.email, firstName: c.firstName,
          lastName: c.lastName, phoneNumber: c.phoneNumber,
        })
          .then(function (data) {
            clientAttachedToCart = true;
            // Rides back on this same call -- no extra round trip.
            state.membership = (data && data.membership) || null;
          })
          // Deliberately swallowed. A failure here means the offer gets
          // evaluated without a client — the old behaviour — and must never
          // turn applying a code into a dead end.
          .catch(function () {});
      }

      // A code the customer entered that could not be judged yet. A first-visit
      // offer cannot be evaluated until Boulevard knows who is booking, and on
      // the merged screen the details are usually typed AFTER the code. The
      // first version of this refused to even try and told people to go and
      // fill in fields first, which is work we can do for them: apply the
      // code, and if the answer was inconclusive, ask again quietly the moment
      // we know who they are. Nothing is said unless there is something useful
      // to say.
      var pendingPromoCode = null;

      // Boulevard reports only that an offer is "not applied", never why, and
      // two very different situations look identical over the API — measured
      // 2026-09-09, both return recognised:true / applied:false / $0:
      //   - the code does not cover this session type (e.g. a first-visit code
      //     on a Returning session), and
      //   - it does cover it, but this person is not eligible.
      // The widget knows which session was chosen, so it can tell them apart.
      // Returns null to mean "inconclusive — say nothing yet".
      function promoFailureMessage(data, haveClient) {
        if (!data.offerRecognised) return 'That code isn’t valid.';
        // A package, membership or gift card is not a session at all, so the
        // first-visit reasoning below cannot apply. It used to fall through
        // to it and tell someone buying a 3-pack that their code "only covers
        // first-time sessions" -- which sends a genuine first-time client off
        // to look for a mistake that is not there. No claim about WHY beyond
        // what is certain: it did not apply to this.
        if (state.item && state.item.kind && state.item.kind !== 'bookable') {
          return 'This code doesn’t apply to this purchase.';
        }
        var svcName = (state.itemDetail && state.itemDetail.name) ||
                      (state.service && state.service.name) || '';
        // WTHN's first-visit services are all named "First Time WTHN Client
        // ...". Checked against the booking BEFORE the person, so someone on a
        // Returning session is told immediately and never asked for details
        // that would not have helped.
        if (!/first\s*time/i.test(svcName)) {
          return 'This code only covers first-time sessions.';
        }
        if (!haveClient) return null;
        // Hedged deliberately: this is the most likely reason by far, but the
        // API does not confirm it, and a wrong flat assertion about someone's
        // own history reads badly.
        return 'This code isn’t applying — first-visit codes are one per client, so if you’ve booked with us before, that’s why.';
      }

      // Writes the form's contact details into state when all four are valid,
      // so ensureClientOnCart has something to attach. Returns whether we now
      // know who is booking.
      function syncContactFromForm(form) {
        if (!form || !form.firstName) return !!(state.contact && state.contact.email);
        if (firstCheckoutProblem(form, true, true)) return false;
        state.contact = {
          firstName: form.firstName.value.trim(),
          lastName: form.lastName.value.trim(),
          email: form.email.value.trim(),
          phoneNumber: form.phoneNumber.value.trim(),
        };
        return true;
      }

      // The member line, rendered inside the total block so it sits with the
      // number it qualifies rather than floating elsewhere on the screen.
      // Server-provided copy: it is the only thing that knows which plan and
      // how many, and it is deliberately conservative about what it claims.
      function membershipNoteHtml() {
        if (!MEMBER_ACK) return '';
        // Two sources, in order of how much they actually know.
        //
        // A logged-in member's cart carries a real voucher, and the voucher
        // carries availableCount -- how many sessions the plan has LEFT this
        // term. That is the honest, specific line, and I had previously
        // called it unknowable from the API.
        //
        // Falling back to the committed member index gives the vaguer "your
        // rate is applied at the studio", which is all we can say about
        // someone who has not logged in. Deliberately falls back rather than
        // guessing a number: telling a member they have a session left when
        // they have spent them is worse than saying nothing specific.
        var v = (state.cartVouchers || []).filter(function (x) {
          return x && typeof x.remaining === 'number' && x.remaining > 0;
        })[0];
        if (v) {
          var plan = membershipDisplayName(v.name);
          var line = v.remaining === 1
            ? '1 session remaining on your ' + plan + '.'
            : v.remaining + ' sessions remaining on your ' + plan + '.';
          return '<dd class="blvd-member-note">' + escapeHtml(line) + '</dd>';
        }
        var m = state.membership;
        if (!m || !m.note) return '';
        return '<dd class="blvd-member-note">' + escapeHtml(m.note) + '</dd>';
      }

      // Same cleanup the server applies: Boulevard's plan names carry
      // "(LEGACY)" and billing suffixes a customer should never be shown, and
      // a leading "The" collides with "your".
      function membershipDisplayName(plan) {
        return String(plan || '')
          .replace(/\s*\([^)]*\)\s*/g, ' ')
          .replace(/:\s.*$/, '')
          .replace(/^the\s+/i, '')
          .replace(/\s+/g, ' ')
          .trim();
      }

      // Ask the server who this is as soon as the form holds a usable email,
      // then redraw the total so the line appears at the moment it matters --
      // while they are looking at the price and reaching for a card.
      function refreshMembership(form, redrawTotal) {
        if (!MEMBER_ACK) return;
        if (form && !syncContactFromForm(form)) return;
        ensureClientOnCart().then(function () { if (redrawTotal) redrawTotal(); });
      }

      // redrawTotal is passed in rather than called directly: each screen
      // builds its own totalRowHtml() as a local, so this shared helper cannot
      // see either of them. Calling it here threw "totalRowHtml is not
      // defined" straight into the promo message, where a customer would have
      // read it.
      function renderPromoResult(data, haveClient, code, result, redrawTotal) {
        var discount = (data.summary && data.summary.discountAmount) || 0;
        state.appliedDiscount = discount;
        if (redrawTotal) redrawTotal();
        if (discount) {
          pendingPromoCode = null;
          result.className = 'blvd-promo__result blvd-promo__result--ok';
          // A code could be applied but never taken off, so a wrong one was
          // stuck until the customer restarted the whole booking. The offer id
          // comes back on the apply response, which is what removal needs.
          // apply-promo-code returns the offer's own id alongside the summary.
          var appliedId = data.offerId || null;
          result.textContent = '';
          result.appendChild(document.createTextNode('Code applied — saved ' + formatPrice(discount) + '. '));
          if (appliedId) {
            var undo = document.createElement('button');
            undo.type = 'button';
            undo.className = 'blvd-linkbtn';
            undo.textContent = 'Remove';
            undo.addEventListener('click', function () {
              undo.disabled = true;
              api('remove-promo-code', { cartId: state.cartId, offerId: appliedId })
                .then(function (res) {
                  state.appliedDiscount = (res.summary && res.summary.discountAmount) || 0;
                  if (redrawTotal) redrawTotal();
                  result.className = 'blvd-promo__result';
                  result.textContent = 'Code removed.';
                  var input = document.getElementById('blvd-promo-code');
                  if (input) input.value = '';
                })
                .catch(function (err) {
                  undo.disabled = false;
                  result.className = 'blvd-promo__result blvd-promo__result--error';
                  result.textContent = err.message || 'Couldn’t remove that code.';
                });
            });
            result.appendChild(undo);
          }
          return;
        }
        var msg = promoFailureMessage(data, haveClient);
        if (msg === null) {
          // Keep it and retry silently once the details are in. Showing
          // "Applying…" forever would be worse than showing nothing.
          pendingPromoCode = code;
          result.className = 'blvd-promo__result';
          result.textContent = '';
          return;
        }
        pendingPromoCode = null;
        result.className = 'blvd-promo__result blvd-promo__result--error';
        result.textContent = msg;
      }

      function resetState() {
        navGeneration++;
        // A fresh cart has no client on it again.
        clientAttachedToCart = false;
        state = {
          cartId: null, location: null, buckets: null, bucket: null,
          sessionId: loginSession.sessionId, savedCards: loginSession.savedCards, forceManualCard: false,
          service: null, date: null, times: null, time: null, contact: loadSavedContact(),
          item: null, itemDetail: null, giftCard: null, priceCents: null,
          deepLinkBucket: null, deepLinkTreatment: null, deepLinkItemId: null, deepLinkItemName: null, deepLinkStaffId: null, deepLinkCondition: null, selectedStaffVariantId: null,
          // merged session screen: the cart's selected-item id (for the
          // in-place professional edit) and the local add-on toggles
          selectedItemId: null, sessionAddonPicks: {},
          locationWasDefaulted: false, paymentSubmitting: false, cartCompleted: false, pendingLogin: false,
          availableAddons: [], selectedAddons: [], appliedDiscount: 0,
        };
      }

      // Points the deep-link-defaulted-studio callout at the pill above it,
      // once, the first time a bucket screen actually renders. Auto-hides
      // itself — it's a one-time "here's a real choice" nudge, not a
      // permanent fixture that would just become wallpaper.
      function showDefaultBanner() {
        defaultBanner.textContent = 'Showing ' + state.location.name + ' — tap above to switch studios';
        defaultBanner.hidden = false;
        clearTimeout(state.defaultBannerTimer);
        state.defaultBannerTimer = setTimeout(function () { defaultBanner.hidden = true; }, 6000);
      }

      function formatPrice(cents) {
        return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }

      // Compact price label for a list row — a single item shows its exact
      // price; a group of items (a treatment with multiple tiers) shows the
      // cheapest as "From $X" rather than a full range, since the point on
      // a list row is a quick anchor, not a precise quote (that's what the
      // detail screen is for).
      function priceLabelForItems(items) {
        var prices = items.map(function (i) { return i.priceRange && i.priceRange.min; }).filter(function (p) { return p != null; });
        if (!prices.length) return '';
        var min = Math.min.apply(null, prices);
        return items.length > 1 ? 'From ' + formatPrice(min) : formatPrice(min);
      }

      // A single item's compareAt (see booking.js's COMPARE_AT_OVERRIDES) —
      // Boulevard itself only knows the real, already-discounted price, so
      // the "was" price is carried alongside it for exactly this display.
      // Only meaningful for a single, non-grouped item, unlike
      // priceLabelForItems' "From $X" — a promo price is never the min of a
      // range.
      function priceLabelWithCompareAt(item) {
        var priceLabel = priceLabelForItems([item]);
        if (!item.compareAt || !priceLabel) return escapeHtml(priceLabel);
        return '<s>' + escapeHtml(formatPrice(item.compareAt)) + '</s> ' + escapeHtml(priceLabel);
      }

      function optionRowHtml(name, priceLabel, note) {
        return '<span class="blvd-option__row">' +
          '<span class="blvd-option__name">' + escapeHtml(name) + '</span>' +
          (priceLabel ? '<span class="blvd-option__price">' + priceLabel + '</span>' : '') +
          '</span>' +
          // Both .blvd-option and .blvd-option__body are column flexboxes, so
          // the sub-line sits under the row in either render context without
          // any new CSS. Same class the staff bios use.
          (note ? '<span class="blvd-option__sub">' + escapeHtml(note) + '</span>' : '');
      }

      // GA4/GTM tracking — pushes to the theme's existing dataLayer (already
      // present sitewide via Shopify's Google Analytics integration,
      // confirmed live 2026-08-27: G-8TF3X6L2Y6, forwarded through GTM).
      // No manual UTM handling needed — GA4 attributes any event fired
      // later in the same session back to whatever campaign/source landed
      // the visitor, using its own existing session tracking.
      //
      // Two different event shapes, deliberately not both "purchase":
      // - book_appointment: a confirmed booking with an ESTIMATED value
      //   (the service's list price) — no money has actually moved, since
      //   WTHN collects payment on-site. Using GA4's real "purchase" event
      //   name here would mix estimated, unrealized value into the same
      //   bucket as actual completed transactions from real Shopify orders
      //   on this same site — a real data-integrity problem for whoever
      //   reads GA4 revenue reports later.
      // - purchase: only fired once a real charge has actually gone through
      //   (packages/memberships/gift cards) — real money, real event.
      function trackGA4Event(name, ecommerce) {
        if (!window.dataLayer) return;
        window.dataLayer.push({ event: name, ecommerce: ecommerce });
        // A real booking or purchase, tagged with the arm that produced it —
        // the one number the split exists to compare. Done here rather than
        // at each of the six call sites, and deliberately NOT in showSuccess,
        // which also fires for a cancellation.
        if (name === 'book_appointment' || name === 'purchase') {
          trackBookingStep('done_' + FLOW_ARM);
          // Paired with addons_kept below, this is the whole point of the
          // add-on instrumentation: it lets completion be compared between
          // people who took an add-on (and so saw a thinner calendar) and
          // everyone else.
          if (state.selectedAddons && state.selectedAddons.length) {
            trackBookingStep('done_with_addons');
          }
        }
      }

      // Per-screen funnel instrumentation. Between book_now_click and
      // book_appointment there were ~10 screens and no visibility into any
      // of them: GA4 could show clicks in and bookings out, but not which
      // screen people left on. Bookings/1k sessions fell 52.5 -> 33.9 after
      // the widget replaced the Boulevard iframe (2026-08-29) with traffic
      // flat, so the loss is somewhere in here.
      //
      // ONE event name carrying the screen as a parameter, not one event
      // name per screen: GTM needs a tag+trigger per custom event, so
      // distinct names would mean a GTM change every time a screen is
      // added. This way the container never has to change again.
      //
      // Deduped per modal open (see openModal's stepsSeen reset) so a back
      // button doesn't inflate a step's count — each step counts at most
      // once per booking attempt, which is what makes the numbers read as
      // a funnel. Errors are exempt: hitting one three times is the signal.
      var stepsSeen = {};


      // Collapse the taxonomy: put the three session treatments and the
      // other visit types on ONE screen, the way Boulevard's own widget did
      // (its screen one carried the studio list AND "SELECT AN OPTION:
      // Individual / Memberships / Packages / Gift card" together).
      //
      // Ours split that same decision across buckets -> treatment, so a
      // session booker needed 7 screens before seeing a time where
      // Boulevard needed 3. Measured 8-9 Sep: 441 users reached the menu and
      // 297 reached an item detail -- a third lost inside the taxonomy,
      // before anyone saw a practitioner or a time.
      //
      var TAXONOMY_MERGE = true;

      // Acknowledge an active member at the moment they are asked for a card.
      // 43% of online bookings are made by an active member (1,539 clients),
      // and today every one of them is shown the full list price and a card
      // form with no sign WTHN knows who they are -- while their phone
      // bookings rise and their online share fell from 46% to 32%.
      //
      // The server answers this on update-client-info; it deliberately never
      // claims a session is free or covered, because Boulevard exposes what a
      // plan INCLUDES but not what has been USED this term. Ships dark.
      var MEMBER_ACK = true;

      // "Your appointments": Boulevard's own logged-in area, or ours.
      //
      // Ours works -- the list reads across every client record a login
      // proves (WTHN has a lot of duplicate people, so one customer can hold
      // three), and cancel was fixed on 2026-09-11 after being broken in
      // production. But reschedule and cancel are not wanted natively yet, so
      // the entry point goes to Boulevard and the native screens stay behind
      // this flag rather than being deleted. Flip to true to bring them back;
      // nothing else needs to change.
      //
      // The URL is Boulevard's own -- it is exactly where their hosted
      // widget's "Your appointments" points.
      var ACCOUNT_NATIVE = true;
      // Seeing your appointments and CHANGING them are separate decisions.
      //
      // Clients could not get into their account at all: "Your appointments"
      // opened Boulevard's hosted login, which asks for a PHONE first, and
      // this business has ~10.6% duplicate phone records -- so people landed
      // on a login that did not know them, or got in and saw an empty list.
      // The front desk reported clients reaching their account only through
      // the magic link in an email receipt (BJ Hoppe, 2026-09-14).
      //
      // Our own login does not have that problem: it verifies one contact
      // method and then hops to the person's OTHER records by the field they
      // did not verify (findSiblingClientIds), so one customer holding three
      // records sees one list.
      //
      // Reschedule and cancel are NATIVE again (2026-09-15). They had been
      // held back on the understanding that WTHN did not want them yet; that
      // was revisited once the read-only version was seen -- a client who can
      // look at an appointment but not move it just phones the studio, which
      // is the cost the front desk was already paying.
      //
      // Boulevard still decides per appointment: inside the notice window it
      // marks one unchangeable, and the screen then names the studio and the
      // $50 fee rather than offering a control that would fail.
      var ACCOUNT_CHANGES_NATIVE = true;
      var BLVD_ACCOUNT_URL = 'https://blvd.app/@wthn/login';
      // The checkout A/B is over: the merged arm is the only flow, and the
      // screens the old arm owned are gone. FLOW_ARM is kept as a constant
      // because it still labels GA4 funnel events and is what tells the
      // backend to tag the appointment in Boulevard -- the only marker that
      // identifies a booking as ours. Re-running a split would mean bringing
      // back a second arm, not just this variable.
      var FLOW_ARM = 'new';
      // The last screen the customer reached before this call. An error
      // message on its own ("Something went wrong") does not say which step
      // produced it, and all 18 error paths share one renderError.
      var lastStep = null;

      // KLAVIYO. The onsite klaviyo.js is already sitewide on wthn.com
      // (company Mt869d), and it identifies the visitor by itself: from the
      // _kx token Klaviyo puts on every email link, and from its own cookie
      // after that. So we never touch an email address here -- we describe
      // what happened and Klaviyo decides whose profile it belongs to. On a
      // browser it cannot identify, the event is simply dropped, which is why
      // this works for the email audience and not for cold organic traffic.
      //
      // window.klaviyo is the current object and _learnq the legacy queue;
      // both exist once the script loads, and either may be the one present
      // if the Shopify integration is re-installed. Wrapped because an
      // analytics call must never be the thing that breaks a booking.
      function klaviyoPush(args) {
        try {
          var q = window.klaviyo || window._learnq;
          if (q && typeof q.push === 'function') q.push(args);
        } catch (e) { /* never surface */ }
      }
      function klaviyoTrack(metric, props) { klaviyoPush(['track', metric, props || {}]); }
      // Profile PROPERTIES, not an event. Klaviyo campaigns can only read
      // profile properties -- event properties are available to flows alone --
      // so without these a one-off "come finish booking" campaign could not
      // name the studio or the treatment the person actually looked at.
      function klaviyoIdentify(props) { klaviyoPush(['identify', props || {}]); }

      // A link straight back to where they stopped. The widget already parses
      // these on load (see parseBlvdTarget), so a recovery email can reopen
      // the exact studio and service instead of dropping them on the homepage.
      function bookingResumeUrl() {
        try {
          var u = 'https://wthn.com/?';
          var parts = [];
          if (state.location) parts.push('location=' + encodeURIComponent(state.location.id));
          if (state.bucket && state.bucket.key) parts.push('bucket=' + encodeURIComponent(state.bucket.key));
          if (state.item && state.item.id) parts.push('item=' + encodeURIComponent(state.item.id));
          return parts.length ? u + parts.join('&') + '#blvd' : 'https://wthn.com/#blvd';
        } catch (e) { return 'https://wthn.com/#blvd'; }
      }

      function trackBookingStep(step, extra) {
        if (step !== 'error') lastStep = step;
        if (!window.dataLayer) return;
        if (step !== 'error') {
          if (stepsSeen[step]) return;
          stepsSeen[step] = true;
        }
        var payload = { event: 'booking_step', booking_step: step };
        if (extra) { for (var k in extra) { if (extra.hasOwnProperty(k)) payload[k] = extra[k]; } }
        window.dataLayer.push(payload);
      }

      // Add-on attach is completely unmeasured today, and that matters more
      // than it looks: adding Cupping to an acupuncture session removes 38%
      // of the day's bookable slots (29 -> 18 at Flatiron, measured live
      // 2026-09-09), because the appointment runs longer and the later slots
      // stop fitting before close. So an add-on is not just upsell revenue —
      // it thins the calendar the customer then has to choose from, and
      // nobody knows whether that costs bookings. These events answer three
      // questions without needing GA4 custom dimensions (there are none
      // registered): which add-ons get picked, how often anyone picks one at
      // all, and whether the people who do still go on to book.
      //
      // The add-on's name rides in the EVENT NAME, not a parameter, because
      // the GA4 tag builds its event name from the step and parameters are
      // not queryable here. Slug capped so the finished name stays inside
      // GA4's 40-character event-name limit.
      function trackAddon(name) {
        var slug = String(name || '').toLowerCase()
          .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 18);
        if (slug) trackBookingStep('addon_' + slug);
      }

      // Actions that only ever read from or start a cart, never commit
      // money/a reservation — safe to silently retry even on a real HTTP
      // error response, since nothing irreversible could have happened
      // server-side. 'confirm'/'checkout'/'reserve...' etc. are
      // deliberately excluded: on those, a lost response doesn't mean the
      // booking/charge didn't go through, so auto-retrying risks a
      // duplicate booking or double charge — better to surface the error
      // and let the person retry deliberately.
      var SAFE_TO_RETRY_ON_HTTP_ERROR = { locations: true, menu: true, 'item-detail': true, times: true, 'staff-for-time': true };

      // text/plain, NOT application/json: json is not CORS-safelisted, so the
      // browser preflighted EVERY call. 2026-09-12 a customer failed 4x in 21s
      // with "Failed to fetch" while their text/plain error beacons -- same
      // domain -- arrived fine: their proxy was dropping OPTIONS. Safe because
      // booking.js parses the body with JSON.parse and ignores Content-Type.
      // Also drops a 189-290ms round trip from each of a booking's 8+ calls.
      function apiOnce(action, extra) {
        return fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify(Object.assign({ action: action }, extra || {})),
        }).then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) {
              var httpErr = new Error(data.error || 'Something went wrong. Please try again.');
              httpErr.gotResponse = true; // reached our server and got a real (error) response
              httpErr.code = data.errorCode || null;
              throw httpErr;
            }
            return data;
          });
        }, function (netErr) {
          // The request never reached us. Browsers word this for developers,
          // not customers: Safari says "Load failed", Chrome "Failed to
          // fetch", and renderError puts that string on screen verbatim. One
          // of them landed on a customer on 2026-09-23, on a paid Facebook
          // landing page, on an iPhone -- the worst place to be told nothing.
          // Say what happened and what to do; the error screen already
          // carries a Try again button.
          var friendly = new Error('We couldn\u2019t reach the booking system. Check your connection and try again.');
          friendly.network = true;
          friendly.rawMessage = (netErr && netErr.message) || String(netErr);
          throw friendly;
        });
      }

      // One retry after a short delay — covers a cold/idle serverless
      // function dropping the first connection (root cause of a real
      // early-morning booking gap, 2026-08-29/30). A network-level failure
      // (fetch itself rejects, so the request never reached the server and
      // nothing can have happened) is retried for EVERY action; a real HTTP
      // error response is retried only for the read-only actions above.
      // The cart's own subtotal, whenever Boulevard tells us one.
      //
      // The confirm screens used to price the booking locally, as service +
      // add-ons, which silently ignored anything ELSE on the cart. Adding a
      // guest made that visible and wrong: the cart was $240 and the screen
      // said $120. A customer being quoted half the real figure is the worst
      // class of bug on this screen, so the server's number wins.
      //
      // `subtotal`, not `total`: subtotal is pre-discount and the screens
      // subtract the discount themselves, so `total` would double-subtract.
      // The discount is re-read on EVERY response because it is a share of
      // the cart, not a fixed number -- captured once, 20% off a $120 session
      // then switching to the $200 facial showed $176 against a $160 cart.
      function captureCartSummary(data) {
        if (data && data.summary && typeof data.summary.subtotal === 'number') {
          state.serverSubtotal = data.summary.subtotal;
        }
        if (data && data.summary && typeof data.summary.discountAmount === 'number') {
          state.appliedDiscount = data.summary.discountAmount;
        }
        return data;
      }

      // ABANDONED REQUESTS MUST NOT RENDER. Reported live 2026-09-24 6:54pm
      // from an iPhone: "null is not an object (evaluating
      // 'state.location.name')" with a live cart and location null. The menu
      // request was in flight, the customer tapped BACK, showLocations() nulled
      // state.location, and then the response landed in showBuckets() -- which
      // builds its title from state.location.name. The TypeError was caught by
      // the same chain's .catch, so it rendered as "something went wrong" and
      // paged the channel, when all the person had done was press back on a
      // slow connection.
      //
      // Fixed at api() rather than in each handler: every screen's .then reads
      // state it assumed was still current, so guarding one call site would
      // leave every sibling broken. A response that arrives after the screen
      // has moved on simply never settles, so no handler runs at all.
      //
      // ponytail: a never-settling promise strands its own chain. Bounded by
      // one per abandoned navigation and collected with the closure; if that
      // ever shows up in a heap profile, swap it for an AbortController on the
      // fetch inside apiOnce.
      function api(action, extra) {
        var gen = navGeneration;
        return apiOnce(action, extra).then(captureCartSummary).catch(function (err) {
          if (err && err.gotResponse && !SAFE_TO_RETRY_ON_HTTP_ERROR[action]) throw err;
          return new Promise(function (resolve) { setTimeout(resolve, 600); }).then(function () {
            return apiOnce(action, extra).then(captureCartSummary).catch(function (err2) {
              // A SECOND network retry, and ONLY for the read-only actions.
              // 600ms after a dropped mobile request the connection is often
              // still down -- the 2026-09-23 customer failed twice that fast
              // and was dead ended -- so one more attempt is worth it for a
              // menu or a times lookup.
              //
              // Deliberately NOT for confirm/checkout, even though the first
              // retry covers them: a rejected fetch does not actually prove
              // the server never processed the request, only that the browser
              // never got the answer. Every extra attempt is another chance at
              // a duplicate booking or a double charge, and a customer seeing
              // an error they can retry themselves is much cheaper than that.
              // An HTTP error is not retried again either: the server answered,
              // so asking a third time changes nothing.
              if (!(err2 && err2.network && SAFE_TO_RETRY_ON_HTTP_ERROR[action])) throw err2;
              return new Promise(function (r) { setTimeout(r, 1800); }).then(function () {
                return apiOnce(action, extra).then(captureCartSummary);
              });
            });
          });
        }).then(function (data) {
          if (gen !== navGeneration) return new Promise(function () {});
          return data;
        }, function (err) {
          if (gen !== navGeneration) return new Promise(function () {});
          throw err;
        });
      }

      function getLocationsCached() {
        if (cachedLocations) return Promise.resolve(cachedLocations);
        return api('locations').then(function (data) {
          cachedLocations = data.locations;
          return cachedLocations;
        });
      }

      function escapeHtml(str) {
        var div = document.createElement('div');
        div.textContent = str == null ? '' : String(str);
        return div.innerHTML;
      }

      function renderLoading() {
        body.innerHTML = '<p class="blvd-loading">Loading…</p>';
      }

      // Send the error text somewhere we can actually read it.
      //
      // renderError has always attached the message to its GA4 event, but an
      // event PARAMETER is only queryable once someone registers it as a
      // custom dimension in GA4 Admin, and our GA4 token is read-only. So on
      // 2026-09-11 three customers were shown an error screen and there was
      // no way to find out what any of them said. This puts the text in the
      // Netlify function log and on Slack, next to every other backend error.
      //
      // sendBeacon, because the customer may close the tab straight after
      // seeing an error -- a normal fetch dies with the page, a beacon does
      // not. The Blob is deliberately text/plain: application/json would make
      // it a preflighted request, and sendBeacon cannot preflight, so it
      // would silently never send. The handler parses the body regardless of
      // content type.
      //
      // Wrapped so that reporting an error can never itself throw and take
      // out the error screen the customer is looking at.
      // kind: 'error' (default) is a screen the customer got stuck on and
      // still alerts Slack. 'validation' is a form field refusing to accept
      // what they typed -- far more common and not individually actionable,
      // so the backend logs it and stays quiet. Without this the expiry bug
      // would have paged the channel roughly 50 times a day.
      function reportClientError(message, kind) {
        // Not from an automated browser. Claude's browser pane carries
        // "Claude/<version>" in its user agent and is what the widget gets
        // driven by during testing -- a blackholed-endpoint test on
        // 2026-09-23 paged the on-call channel with an error nobody had. A
        // real customer never carries this token, and this suppresses only
        // the REPORT: the error screen, the retry and the GA4 step all still
        // happen, so a test still shows whether the widget behaves.
        try {
          // Claude's DESKTOP pane carries "Claude/<version>". Its mobile
          // emulation does not -- it presents as a clean Pixel 8 with
          // navigator.webdriver false, so there is nothing to detect. Hence
          // the explicit opt-out too: any test URL can carry blvdnoreport=1.
          // A customer never has either, and both suppress only the REPORT --
          // the error screen, the retries and the GA4 step still happen, so a
          // test still shows whether the widget behaves.
          if (/\bClaude\//.test(navigator.userAgent || '')) return;
          if (/[?&]blvdnoreport=1\b/.test(location.search || '')) return;
        } catch (e) { /* if neither can be read, report as normal */ }
        try {
          var payload = JSON.stringify({
            action: 'client-error',
            kind: kind || 'error',
            message: String(message).slice(0, 300),
            step: lastStep,
            locationName: (state.location && state.location.name) || null,
            cartId: state.cartId || null,
            href: String(location.href).slice(0, 200),
            userAgent: String(navigator.userAgent).slice(0, 200),
          });
          if (navigator.sendBeacon) {
            navigator.sendBeacon(API_URL, new Blob([payload], { type: 'text/plain' }));
            return;
          }
          fetch(API_URL, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: payload, keepalive: true })
            .catch(function () {});
        } catch (e) { /* never let reporting break the screen */ }
      }

      // WHICH error, as an event NAME. The message has always gone into a GA4
      // event PARAMETER (booking_error), and a parameter cannot be queried
      // until someone registers a custom dimension -- which needs analytics
      // write access nobody here has. So for weeks we could count that
      // customers hit errors and never once say WHICH. 16 of them in the 19:00
      // hour on 2026-09-23 and the only reason we know what they were is that
      // a platform incident was being watched live at the time.
      //
      // Names are countable tomorrow morning with no permissions at all. Kept
      // to a bounded set and matched on the message, so no call site changes:
      // renderError is the one place all 19 of them pass through.
      //
      // 27 characters max after the booking_step_ prefix, or GA4 truncates
      // the name at 40 and two categories become the same unreadable stub.
      function classifyError(message) {
        var m = String(message || '');
        if (/reach the booking system|Load failed|Failed to fetch/i.test(m)) return 'err_network';
        if (/API limit exceeded|rate limit/i.test(m)) return 'err_rate_limit';
        if (/no longer available|already been booked|pick a different time/i.test(m)) return 'err_slot_taken';
        if (/payment details|declined|could.?n.?t be charged/i.test(m)) return 'err_card_declined';
        if (/card on file|need a card|card is required/i.test(m)) return 'err_needs_payment';
        if (/log ?in again|session (has )?expired/i.test(m)) return 'err_session_expired';
        if (/Nothing is bookable/i.test(m)) return 'err_nothing_bookable';
        if (/can.?t be booked at this studio/i.test(m)) return 'err_service_unavailable';
        if (/email address or mobile number/i.test(m)) return 'err_bad_identifier';
        if (/already confirmed/i.test(m)) return 'err_already_booked';
        return 'err_unknown';
      }

      // Deliberately a DENY list of runtime-error shapes, not "anything that
      // classifies as err_unknown". Plenty of messages we write on purpose --
      // "We couldn't load times just then. Please pick your studio again." --
      // also fall through to err_unknown, and replacing those with a generic
      // line would throw away the specific instruction that makes them useful.
      // These four shapes are engine text from a browser, in every engine's
      // wording: nothing in this widget's copy reads like them.
      function looksLikeThrownJs(message) {
        return /is not an object|cannot read propert|is not a function|undefined is not/i.test(String(message || ''));
      }

      function renderError(message, retry) {
        trackBookingStep('error', { booking_error: String(message).slice(0, 100) });
        // Alongside the total, never instead of it, so the existing trend line
        // stays comparable across this change.
        trackBookingStep(classifyError(message));
        reportClientError(message);
        // NEVER show a raw JavaScript message to a customer. On 24 Sep someone
        // pressed back on a slow connection and the whole screen became
        // "Cannot read properties of null (reading 'name')" -- which tells them
        // nothing, looks broken, and is the last thing they see before leaving.
        // Every message the widget writes deliberately is prose; anything that
        // classifies as err_unknown is a thrown Error that was never meant for
        // a customer, so it is replaced here. The raw text still goes to the
        // alert and to reportClientError above, which is where it is useful.
        var shown = looksLikeThrownJs(message)
          ? 'Something went wrong on our end. Please try again.'
          : message;
        // SOME ERRORS CANNOT BE RETRIED, and offering Try again for those is
        // the dead end the standing rule is about. On 2026-10-15 at 1:09am a
        // customer hit CART_SERVICE_UNAVAILABLE and tapped six times in 25
        // seconds: retrying the same service on the same cart could never
        // have worked, and the only other thing on the screen was Boulevard's
        // own wording. Send them back to the treatment list, which IS a way
        // out, and keep Try again for the errors a retry can actually fix.
        var cls = classifyError(message);
        var pickAnother = cls === 'err_service_unavailable' && state.location;
        body.innerHTML =
          '<div class="blvd-error"><p>' + escapeHtml(shown) + '</p>' +
          '<button type="button" class="blvd-btn blvd-btn--primary" style="width:auto">' +
          (pickAnother ? 'Choose another treatment' : 'Try again') + '</button>' +
          errorPhoneHtml() +
          '</div>';
        body.querySelector('.blvd-btn').addEventListener('click', pickAnother ? showMenu : retry);
      }

      // A PHONE NUMBER ON EVERY ERROR SCREEN. Whatever went wrong, the studios
      // can still take the booking, and an error screen whose only option is
      // Try again quietly treats a failed booking as the customer's problem.
      // Shown on all of them, not just the unretryable ones.
      //
      // The studio's own number once a studio is known, which is every screen
      // from the menu onward. Before that there is nothing to name -- WTHN has
      // five studio lines and no general number (checked 2026-10-15) -- so the
      // contact page carries all five rather than this screen guessing one.
      function errorPhoneHtml() {
        if (state.location && state.location.phoneNumber) {
          return '<p class="blvd-note">Or call ' + escapeHtml(state.location.name) + ' on ' +
            '<a href="tel:' + escapeHtml(state.location.phoneNumber) + '">' +
            escapeHtml(formatPhone(state.location.phoneNumber)) + '</a>.</p>';
        }
        return '<p class="blvd-note">Or <a href="https://wthn.com/pages/contact">call a studio</a> ' +
          'and we\u2019ll book it for you.</p>';
      }

      function setBack(handler) {
        backBtn.hidden = !handler;
        backBtn.onclick = handler || null;
      }

      function todayInTz(tz) {
        // en-CA formats as YYYY-MM-DD, which is exactly what the times
        // endpoint expects and what <input type="date"> needs as a value.
        return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
      }

      // --- Header pill / studio switcher ---
      function renderPill() {
        if (!state.location) {
          pillBtn.hidden = true;
          closeSwitcher();
          return;
        }
        pillBtn.hidden = false;
        pillBtn.innerHTML =
          '<span class="blvd-header__pill-name">' + escapeHtml(state.location.name) + '</span>' +
          '<span class="blvd-header__pill-chevron">&#8964;</span>';
      }

      function closeSwitcher() {
        switcherEl.hidden = true;
        switcherEl.innerHTML = '';
      }

      function toggleSwitcher() {
        // The banner's whole job is getting someone to look up here — once
        // they have, it's done, regardless of whether they actually switch.
        defaultBanner.hidden = true;
        if (!switcherEl.hidden) {
          closeSwitcher();
          return;
        }
        getLocationsCached().then(renderSwitcherList).catch(function (err) {
          switcherEl.hidden = false;
          switcherEl.innerHTML = '<p class="blvd-switcher__confirm">' + escapeHtml(err.message) + '</p>';
        });
      }

      function renderSwitcherList(locations) {
        // getLocationsCached() is async — if the screen navigated away from
        // a specific studio (e.g. back to "Choose a studio", which nulls
        // state.location) while this fetch was still in flight, don't
        // crash on state.location.id and don't reopen a dropdown that has
        // nothing to switch away from.
        if (!state.location) { closeSwitcher(); return; }
        var others = locations.filter(function (l) { return l.id !== state.location.id; });
        var html = '';
        others.forEach(function (l) {
          var studioColor = STUDIO_COLORS[l.name] || { bg: 'transparent', text: '#2B445F' };
          html += '<button type="button" class="blvd-switcher__item" data-id="' + escapeHtml(l.id) + '" style="background:' + studioColor.bg + ';color:' + studioColor.text + '">' +
            '<span class="blvd-switcher__pin">' + PIN_ICON + '</span>' + escapeHtml(l.name) + '</button>';
        });
        switcherEl.innerHTML = html;
        switcherEl.hidden = false;
        switcherEl.querySelectorAll('.blvd-switcher__item').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var loc = others.filter(function (l) { return l.id === btn.getAttribute('data-id'); })[0];
            doSwitch(loc);
          });
        });
      }

      function doSwitch(loc) {
        closeSwitcher();
        var contact = state.contact;
        // Carry the current bucket across the switch as a deep link into
        // the new studio's own menu — showMenu() already knows how to land
        // straight on a bucket (or fall back to the bucket list if this
        // studio doesn't offer it), the same logic a page's own deep-link
        // CTA uses. Only the bucket, not the specific treatment/item within
        // it — a chosen time/item is still silently dropped on switch (no
        // confirmation dialog for that anymore, on request).
        var bucketKey = state.bucket ? state.bucket.key : null;
        // ...and the specific item they were looking at, BY NAME. Item ids are
        // per-location in Boulevard -- "First Time Client Healing Acupuncture
        // 3-Pack" has a different id at every studio (verified 2026-09-16) --
        // so deepLinkItemId cannot survive a switch and would silently fall
        // through to the bucket list, which is exactly the reset being
        // complained about. The full name carries treatment AND tier, so it
        // lands on the same screen at the new studio, or falls back to the
        // bucket list if that studio doesn't offer it.
        var itemName = state.item ? state.item.name : null;
        // A logged-in customer's cart carries their ownership, and with it
        // their saved card. resetState() nulls the cart id, so before this
        // every studio switch quietly threw that away and they were back to
        // typing a card (found in testing 2026-09-12). The backend moves the
        // cart to the new studio and empties it, so the previous studio's
        // service cannot ride along.
        var ownedCart = loginSession.cartOwned ? state.cartId : null;
        resetState();
        state.location = loc;
        if (ownedCart) state.cartId = ownedCart;
        // Deliberately kept — no reason to make someone retype their
        // name/email just because they picked a different studio.
        state.contact = contact;
        state.deepLinkBucket = bucketKey;
        state.deepLinkItemName = itemName;
        showMenu();
      }

      // --- Screen: Locations ---
      var PIN_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 21s7-6.5 7-12a7 7 0 0 0-14 0c0 5.5 7 12 7 12z"/><circle cx="12" cy="9" r="2.5"/></svg>';
      // One accent per studio in the switcher dropdown — the row itself is
      // tinted with the studio's color (not the tiny pin icon, too subtle
      // to register at 14px, confirmed live 2026-08-28), same light-tint +
      // dark-accent pairing already used for bucket icons
      // (.blvd-icon--heal/transform/release). Flatiron/NoHo/Upper West
      // Side reuse those exact pairs; Upper East Side and Williamsburg get
      // two more pairs built the same way from colors already used
      // elsewhere in this widget (terracotta price accent, steel-blue
      // button hover).
      var STUDIO_COLORS = {
        'Flatiron': { bg: '#9FB8DA', text: '#2B445F' },
        'NoHo': { bg: '#E2AF7F', text: '#89392F' },
        'Upper East Side': { bg: '#F3DBD1', text: '#BD664B' },
        'Upper West Side': { bg: '#D4F19F', text: '#506E57' },
        'Williamsburg': { bg: '#D7E3EC', text: '#4A7299' },
      };
      var PHONE_ICON = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 2 .7 2.9a2 2 0 0 1-.5 2.1L8 10a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.9.6 2.9.7a2 2 0 0 1 1.7 2z"/></svg>';

      function formatPhone(e164) {
        var m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164 || '');
        return m ? '(' + m[1] + ') ' + m[2] + '-' + m[3] : e164;
      }

      function showLocations() {
        trackBookingStep('studio');
        navGeneration++;
        state.location = null;
        renderPill();
        setBack(null);
        renderLoading();
        getLocationsCached()
          .then(function (locations) {
            // Returning-client login, ABOVE the studio list -- where
            // Boulevard's own widget put it ("Returning Clients: log in to
            // your personalized booking experience"), and where it is still
            // a shortcut rather than a gate: guest booking stays the default
            // and this is hidden once we already know who they are.
            //
            // A button rather than the 14px underlined link this used to be,
            // matching the saved-card prompt at checkout. It sits above the
            // list, not below it, because below the five studio cards a
            // bordered full-width button reads as a sixth studio.
            var html = '<h2 class="blvd-title">Choose a studio</h2>' +
              '<p class="blvd-subtitle">' + locations.length + ' studios across NYC</p>' +
              returningPromptHtml() +
              '<ul class="blvd-list">';
            locations.forEach(function (loc) {
              html += '<li><button type="button" class="blvd-option blvd-option--bucket" data-id="' + escapeHtml(loc.id) + '">' +
                '<span class="blvd-option__icon blvd-icon--heal">' + PIN_ICON + '</span>' +
                '<span class="blvd-option__body">' +
                '<span class="blvd-option__name">' + escapeHtml(loc.name) +
                (loc.name === 'Upper East Side' ? ' <span class="blvd-badge-new">NEW</span>' : '') + '</span>' +
                '<span class="blvd-option__sub">' + escapeHtml(loc.address.line1) + ', ' + escapeHtml(loc.address.city) + '</span>' +
                (loc.phoneNumber
                  ? '<span class="blvd-option__phone">' + PHONE_ICON + ' ' + escapeHtml(formatPhone(loc.phoneNumber)) + '</span>'
                  : '') +
                '</span>' +
                '</button></li>';
            });
            html += '</ul>';
            body.innerHTML = html;
            body.querySelectorAll('.blvd-option').forEach(function (btn) {
              btn.addEventListener('click', function () {
                state.location = locations.filter(function (l) { return l.id === btn.getAttribute('data-id'); })[0];
                showMenu();
              });
            });
            wireReturningPrompt(showLocations);
          })
          .catch(function (err) { renderError(err.message, showLocations); });
      }

      // --- Screens: the client's own appointments -------------------------
      // Restores what Boulevard's hosted widget had behind login. Not logged
      // in? Send them through login first, then land here.
      function showMyAppointments() {
        // A login that succeeded but returned no session handle means the
        // server couldn't set one up. Sending them back to login would loop
        // forever on a form that just appears to reset — which is exactly
        // what a misconfigured session store did on 2026-09-04. Say so and
        // offer the Boulevard portal instead.
        if (!state.sessionId && loginSession.loggedIn) {
          setBack(showLocations);
          body.innerHTML =
            '<h2 class="blvd-title">Your appointments</h2>' +
            '<p class="blvd-subtitle">We can’t load your appointments here right now. ' +
            'You can still see and manage them in your WTHN account.</p>' +
            '<a class="blvd-btn blvd-btn--primary" href="https://blvd.app/@wthn/login" target="_blank" rel="noopener">Open your account</a>';
          return;
        }
        if (!state.sessionId) {
          return showLogin(showLocations, showMyAppointments);
        }
        setBack(showLocations);
        renderLoading();
        api('my-appointments', { sessionId: state.sessionId })
          .then(function (data) {
            var appts = (data.appointments || []).filter(function (a) { return !a.cancelled; });

            // PAST VISITS. The server already returns the last five and the
            // screen used to throw them away, so someone with nothing booked
            // saw "Nothing booked right now" despite a history of visits --
            // and "when was I last in, and who did I see" is a large part of
            // why anyone opens this screen at all. Costs no extra call.
            //
            // Not clickable: the detail screen exists to change a booking,
            // and a past visit cannot be changed. Naming the practitioner is
            // the useful half -- it is what someone rebooking wants.
            function pastHtml() {
              var past = (data.past || []).filter(function (a) { return !a.cancelled; })
                .slice()
                .sort(function (a, b) { return String(b.startAt).localeCompare(String(a.startAt)); });
              if (!past.length) return '';
              var out = '<h3 class="blvd-subhead">Recent visits</h3><ul class="blvd-list blvd-list--past">';
              past.forEach(function (a) {
                var when = new Date(a.startAt).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric',
                  timeZone: (a.location && a.location.tz) || 'America/New_York',
                });
                var svcs = (a.appointmentServices || []);
                var svc = svcs.map(function (x) { return x.service && x.service.name; })
                  .filter(Boolean).join(', ');
                var who = '';
                for (var i = 0; i < svcs.length; i++) {
                  if (svcs[i].staff && svcs[i].staff.displayName) { who = svcs[i].staff.displayName; break; }
                }
                out += '<li class="blvd-past"><span class="blvd-past__when">' + escapeHtml(when) + '</span>' +
                  '<span class="blvd-past__what">' + escapeHtml(svc || 'Appointment') +
                  (a.location ? ' · ' + escapeHtml(a.location.name) : '') +
                  (who ? '<br>with ' + escapeHtml(who) : '') + '</span></li>';
              });
              return out + '</ul>';
            }

            if (!appts.length) {
              body.innerHTML =
                '<h2 class="blvd-title">Your appointments</h2>' +
                '<p class="blvd-subtitle">Nothing booked right now.</p>' +
                '<button type="button" class="blvd-btn blvd-btn--primary" id="blvd-book-something">Book a session</button>' +
                pastHtml();
              document.getElementById('blvd-book-something').addEventListener('click', showLocations);
              return;
            }
            var html = '<h2 class="blvd-title">Your appointments</h2><ul class="blvd-list">';
            appts.forEach(function (a, i) {
              var when = new Date(a.startAt).toLocaleString('en-US', {
                weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                timeZone: (a.location && a.location.tz) || 'America/New_York',
              });
              var svc = (a.appointmentServices || []).map(function (s) { return s.service.name; }).join(', ');
              html += '<li><button type="button" class="blvd-option blvd-option--bucket" data-appt="' + i + '">' +
                '<span class="blvd-option__body">' +
                '<span class="blvd-option__name">' + escapeHtml(when) + '</span>' +
                '<span class="blvd-option__sub">' + escapeHtml(svc || 'Appointment') +
                (a.location ? ' · ' + escapeHtml(a.location.name) : '') + '</span>' +
                '</span></button></li>';
            });
            html += '</ul>';
            html += pastHtml();
            body.innerHTML = html;
            body.querySelectorAll('[data-appt]').forEach(function (btn) {
              btn.addEventListener('click', function () {
                showAppointmentDetail(appts[Number(btn.getAttribute('data-appt'))]);
              });
            });
          })
          .catch(function (err) {
            // Session expired mid-flow — send them back through login rather
            // than showing a raw error.
            if (/log in again/i.test(err.message || '')) {
              state.sessionId = loginSession.sessionId = null;
              clearSavedSession();
              if (!sessionRejectedIsRetryable()) return showAccountUnavailable('Your appointments');
              loginSession.rePrompted = true;
              return showLogin(showLocations, showMyAppointments);
            }
            renderError(err.message, showMyAppointments);
          });
      }

      function showAppointmentDetail(appt) {
        setBack(showMyAppointments);
        var when = new Date(appt.startAt).toLocaleString('en-US', {
          weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
          timeZone: (appt.location && appt.location.tz) || 'America/New_York',
        });
        var svc = (appt.appointmentServices || []).map(function (s) { return s.service.name; }).join(', ');
        // Boulevard decides what's allowed (inside the cancellation window,
        // already started, etc). Honour its flags rather than guessing, and
        // show its own reason when it says no.
        // The server derives whether a self-serve change is allowed, from the
        // same policy stated at checkout: free before 24 hours, $50 after.
        // Inside that window we deliberately do NOT offer a one-tap change --
        // only the studio can waive the fee, so it belongs in their hands.
        var actions = '';
        var blocked = '';
        var studioPhone = (appt.location && appt.location.phoneNumber) ? formatPhone(appt.location.phoneNumber) : '';
        var callTheStudio = 'call the studio' + (studioPhone ? ' on ' + escapeHtml(studioPhone) : '');
        if (appt.changeable) {
          var deadlineNote = '';
          if (appt.changeDeadline) {
            var dl = new Date(appt.changeDeadline);
            var tzx = (appt.location && appt.location.tz) || 'America/New_York';
            deadlineNote = 'Free to change until ' +
              escapeHtml(dl.toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: tzx })) + ' at ' +
              escapeHtml(dl.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tzx, timeZoneName: 'short' })) + '.';
          }
          if (ACCOUNT_CHANGES_NATIVE) {
            actions += '<button type="button" class="blvd-btn blvd-btn--primary" id="blvd-appt-move">Reschedule</button>' +
              '<button type="button" class="blvd-btn blvd-btn--secondary" id="blvd-appt-cancel">Cancel appointment</button>';
            if (deadlineNote) blocked = '<p class="blvd-note">' + deadlineNote + '</p>';
          } else {
            // Read-only. Say who to ask rather than showing a control that is
            // not there -- and still give them the deadline, because it is
            // the thing that decides whether the change is free.
            blocked = '<p class="blvd-note">To reschedule or cancel, ' + callTheStudio + '.' +
              (deadlineNote ? ' ' + deadlineNote : '') + '</p>';
          }
        } else if (appt.changeReason === 'inside-notice-window') {
          blocked = '<p class="blvd-note">This appointment is within ' + (appt.noticeHours || 24) +
            ' hours, so changes are handled by the studio' +
            (studioPhone ? ' — call ' + escapeHtml(studioPhone) : '') + '. ' +
            'Changing it now carries a $50 fee.</p>';
        } else {
          blocked = '<p class="blvd-note">This appointment can’t be changed online — please call the studio.</p>';
        }
        body.innerHTML =
          '<h2 class="blvd-title">' + escapeHtml(svc || 'Appointment') + '</h2>' +
          '<div class="blvd-summary-box"><dl class="blvd-summary">' +
          '<dt>When</dt><dd>' + escapeHtml(when) + '</dd>' +
          (appt.location ? '<dt>Studio</dt><dd>' + escapeHtml(appt.location.name) + '</dd>' : '') +
          '</dl></div>' + blocked + actions +
          '<p class="blvd-promo__result" id="blvd-appt-err"></p>';

        var moveBtn = document.getElementById('blvd-appt-move');
        if (moveBtn) {
          moveBtn.addEventListener('click', function () { showRescheduleDates(appt, when); });
        }

        var cancelBtn = document.getElementById('blvd-appt-cancel');
        if (cancelBtn) {
          cancelBtn.addEventListener('click', function () {
            // Cancelling is irreversible and frees the slot — confirm first.
            if (!window.confirm('Cancel your ' + when + ' appointment?')) return;
            cancelBtn.disabled = true;
            var errEl = document.getElementById('blvd-appt-err');
            errEl.className = 'blvd-promo__result';
            errEl.textContent = 'Cancelling…';
            api('cancel-appointment', { sessionId: state.sessionId, appointmentId: appt.id })
              .then(function () {
                showSuccess('Appointment cancelled', 'You’ll get a confirmation by email.');
              })
              .catch(function (err) {
                cancelBtn.disabled = false;
                errEl.className = 'blvd-promo__result blvd-promo__result--error';
                errEl.textContent = err.message || 'Couldn’t cancel that — please call the studio.';
              });
          });
        }
      }

      // --- Screens: optional returning-client login ---------------------
      // Two steps: ask for email or mobile, then the 6-digit code Boulevard
      // sends. On success we keep only the client's contact details (the
      // server never returns the session token — nothing here needs it, and
      // holding a bearer credential in the page only widens XSS impact).
      // `backTo` is where the back button and "continue as guest" go;
      // `onSuccess` is where a completed login lands. They differ when login
      // was entered from "Your appointments": skipping must NOT return to the
      // appointments screen, which would just bounce straight back to login.
      // --- Reschedule: pick a day, then a time --------------------------
      // Boulevard answers reschedule availability per-appointment, and only
      // for days that can actually take it -- so this offers real days rather
      // than a date box that can land on a closed one. Both steps run
      // server-side against the session's verified client id.
      function showRescheduleDates(appt, whenLabel) {
        setBack(function () { showAppointmentDetail(appt); });
        renderLoading();
        api('reschedule-dates', { sessionId: state.sessionId, appointmentId: appt.id, days: 21 })
          .then(function (data) {
            var dates = data.dates || [];
            if (!dates.length) {
              body.innerHTML =
                '<h2 class="blvd-title">Reschedule</h2>' +
                '<p class="blvd-subtitle">There is nothing open in the next three weeks for this appointment. ' +
                'Please call the studio and we will find you a time.</p>';
              return;
            }
            var html = '<h2 class="blvd-title">Reschedule</h2>' +
              '<p class="blvd-subtitle">Currently ' + escapeHtml(whenLabel) + '. Pick a new day.</p>' +
              '<ul class="blvd-list">';
            dates.forEach(function (d, i) {
              html += '<li><button type="button" class="blvd-option" data-i="' + i + '">' +
                '<span class="blvd-option__name">' + escapeHtml(formatDateLabel(d)) + '</span></button></li>';
            });
            html += '</ul>';
            body.innerHTML = html;
            body.querySelectorAll('.blvd-option').forEach(function (btn) {
              btn.addEventListener('click', function () {
                showRescheduleTimes(appt, whenLabel, dates[Number(btn.getAttribute('data-i'))]);
              });
            });
          })
          .catch(function (err) { renderError(err.message, function () { showRescheduleDates(appt, whenLabel); }); });
      }

      function showRescheduleTimes(appt, whenLabel, date) {
        setBack(function () { showRescheduleDates(appt, whenLabel); });
        renderLoading();
        api('reschedule-times', { sessionId: state.sessionId, appointmentId: appt.id, date: date })
          .then(function (data) {
            var times = data.times || [];
            var tz = (appt.location && appt.location.tz) || 'America/New_York';
            var html = '<h2 class="blvd-title">' + escapeHtml(formatDateLabel(date)) + '</h2>';
            if (!times.length) {
              html += '<p class="blvd-subtitle">Nothing left on that day — try another.</p>';
              body.innerHTML = html;
              return;
            }
            html += '<p class="blvd-subtitle">Pick a new time.</p><div class="blvd-slots">';
            times.forEach(function (t, i) {
              var label = new Date(t.startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz });
              html += '<button type="button" class="blvd-slot" data-i="' + i + '">' + label + '</button>';
            });
            html += '</div><p class="blvd-promo__result" id="blvd-move-err"></p>';
            body.innerHTML = html;
            body.querySelectorAll('.blvd-slot').forEach(function (btn) {
              btn.addEventListener('click', function () {
                var t = times[Number(btn.getAttribute('data-i'))];
                var label = new Date(t.startTime).toLocaleString('en-US', {
                  weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz,
                });
                // Moving frees the old slot for someone else, so confirm the
                // swap in words before doing it.
                if (!window.confirm('Move your appointment to ' + label + '?')) return;
                var errEl = document.getElementById('blvd-move-err');
                errEl.className = 'blvd-promo__result';
                errEl.textContent = 'Moving…';
                body.querySelectorAll('.blvd-slot').forEach(function (b) { b.disabled = true; });
                api('reschedule-appointment', {
                  sessionId: state.sessionId, appointmentId: appt.id, bookableTimeId: t.bookableTimeId,
                })
                  .then(function () {
                    showSuccess('Appointment moved', 'You are now booked for ' + label + '. A confirmation is on its way.');
                  })
                  .catch(function (err) {
                    body.querySelectorAll('.blvd-slot').forEach(function (b) { b.disabled = false; });
                    errEl.className = 'blvd-promo__result blvd-promo__result--error';
                    errEl.textContent = err.message || 'Couldn’t move that — please call the studio.';
                  });
              });
            });
          })
          .catch(function (err) { renderError(err.message, function () { showRescheduleTimes(appt, whenLabel, date); }); });
      }

      function showLogin(backTo, onSuccess) {
        var skipTo = backTo || showLocations;
        var doneTo = onSuccess || backTo || showLocations;
        backTo = skipTo;
        setBack(skipTo);
        // REMEMBERED DEVICE: skip the identifier step entirely. The handle
        // already proved who they are, so asking them to retype an email
        // address we hold is pure friction -- and friction is the complaint.
        // A code is still unavoidable: the saved card and membership voucher
        // are only ever addressable on a cart the single-use code took
        // ownership of (the admin API has no cart mutations, and its
        // CreditCard type has no id -- probed 24 Sep). So the code stays and
        // everything around it goes. Paired with autocomplete="one-time-code"
        // on the next screen, a returning member taps twice.
        if (loginSession.loggedIn && loginSession.sessionId && !loginSession.cartOwned) {
          renderLoading();
          trackBookingStep('login_resend_auto');
          api('login-resend', { sessionId: loginSession.sessionId })
            .then(function (data) {
              trackBookingStep('login_code_sent');
              showLoginCode(data.requestId, data.channel, skipTo, doneTo, data.masked);
            })
            .catch(function () {
              // Expired, forged, or a client record with no contact details.
              // Never a dead end: fall back to typing an identifier, and stop
              // claiming to remember someone the server will not vouch for.
              trackBookingStep('login_resend_failed');
              loginSession.loggedIn = false;
              loginSession.sessionId = null;
              loginSession.firstName = '';
              state.sessionId = null;
              clearSavedSession();
              showLogin(skipTo, doneTo);
            });
          return;
        }
        body.innerHTML =
          '<h2 class="blvd-title">Welcome back</h2>' +
          '<p class="blvd-subtitle">Enter the email or mobile number on your WTHN account and we’ll send you a code.</p>' +
          '<form class="blvd-form" id="blvd-login-form">' +
          '<label>Email or mobile number<input type="text" name="identifier" required placeholder="jane@example.com" autocomplete="username"></label>' +
          '<button type="submit" class="blvd-btn blvd-btn--primary">Send code</button>' +
          '</form>' +
          '<p class="blvd-note"><button type="button" class="blvd-linkbtn" id="blvd-login-skip">Continue as guest instead</button></p>' +
          '<p class="blvd-promo__result" id="blvd-login-err"></p>';
        document.getElementById('blvd-login-skip').addEventListener('click', function () {
          trackBookingStep('login_abandoned_identifier');
          backTo();
        });
        document.getElementById('blvd-login-form').addEventListener('submit', function (e) {
          e.preventDefault();
          var id = e.target.identifier.value.trim();
          if (!id) return;
          var errEl = document.getElementById('blvd-login-err');
          // Say so here rather than letting Boulevard say it. An identifier
          // that is neither an address nor a number used to be sent anyway,
          // came back a bare 400, and the customer was told "Something went
          // wrong. Please continue as guest." -- which reads as our fault and
          // pushes them out of the login the saved card depends on. It also
          // paged the team three times on 2026-09-17 for one person's typo.
          // Same rule as the server's isValidLoginIdentifier; permissive on
          // phone length so an international number still gets through.
          var digitCount = id.replace(/\D/g, '').length;
          var looksValid = id.indexOf('@') > -1
            ? /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(id)
            : digitCount >= 10;
          if (!looksValid) {
            trackBookingStep('login_identifier_invalid');
            errEl.className = 'blvd-promo__result blvd-promo__result--error';
            errEl.textContent = 'That doesn\u2019t look like an email address or mobile number.';
            return;
          }
          var submitBtn = e.target.querySelector('button[type=submit]');
          submitBtn.disabled = true;
          errEl.className = 'blvd-promo__result';
          errEl.textContent = 'Sending…';
          trackBookingStep('login_identifier_submitted');
          api('login-start', { identifier: id })
            .then(function (data) {
              // Deliberately NOT split by whether an account matched:
              // login-start answers the same way either way so the widget
              // cannot be used to test whether an email is a WTHN client.
              trackBookingStep('login_code_sent');
              showLoginCode(data.requestId, data.channel, skipTo, doneTo);
            })
            .catch(function (err) {
              trackBookingStep('login_send_failed');
              submitBtn.disabled = false;
              errEl.className = 'blvd-promo__result blvd-promo__result--error';
              errEl.textContent = err.message || 'Something went wrong. Please continue as guest.';
            });
        });
      }

      function showLoginCode(requestId, channel, backTo, onSuccess, masked) {
        var doneTo = onSuccess || backTo;
        setBack(function () { showLogin(backTo, doneTo); });
        body.innerHTML =
          '<h2 class="blvd-title">Enter your code</h2>' +
          // With a masked destination we are NOT guessing: the remembered
          // handle already proved who this is, so naming where the code went
          // is not a disclosure and saves them checking the wrong inbox.
          // Without one the wording stays deliberately conditional, because
          // login-start must not reveal whether an account exists.
          '<p class="blvd-subtitle">' + (masked
            ? 'We ' + (channel === 'sms' ? 'texted' : 'emailed') + ' a 6-digit code to ' + escapeHtml(masked) + '.'
            : 'If that ' + (channel === 'sms' ? 'number' : 'email') +
              ' matches a WTHN account, a 6-digit code is on its way.') + '</p>' +
          '<form class="blvd-form" id="blvd-code-form">' +
          '<label>Code<input type="text" name="code" required inputmode="numeric" autocomplete="one-time-code" placeholder="123456"></label>' +
          '<button type="submit" class="blvd-btn blvd-btn--primary">Log in</button>' +
          '</form>' +
          '<p class="blvd-note"><button type="button" class="blvd-linkbtn" id="blvd-code-skip">Continue as guest instead</button></p>' +
          '<p class="blvd-promo__result" id="blvd-code-err"></p>';
        document.getElementById('blvd-code-skip').addEventListener('click', function () {
          // Abandoning at the CODE screen is the expensive one -- they asked
          // for a code and still did not get in.
          trackBookingStep('login_abandoned_code');
          backTo();
        });
        document.getElementById('blvd-code-form').addEventListener('submit', function (e) {
          e.preventDefault();
          var errEl = document.getElementById('blvd-code-err');
          var submitBtn = e.target.querySelector('button[type=submit]');
          if (!requestId) {
            // No account matched on the previous step. Deliberately not said
            // out loud until now, and still not attributed to the identifier
            // — see login-start's enumeration note.
            errEl.className = 'blvd-promo__result blvd-promo__result--error';
            errEl.textContent = 'That code didn’t work. Please try again or continue as guest.';
            return;
          }
          submitBtn.disabled = true;
          errEl.className = 'blvd-promo__result';
          errEl.textContent = 'Checking…';
          api('login-verify', {
            requestId: requestId, code: e.target.code.value,
            cartId: state.cartId || undefined,
            locationId: (state.location && state.location.id) || undefined,
          })
            .then(function (data) {
              // ONE login now yields all of it: the account session, the saved
              // card, the membership voucher and the client's own details.
              // Previously the account area and the card screens ran two
              // different logins against two different Boulevard mechanisms.
              if (data.contact) {
                state.contact = data.contact;
                saveContact(data.contact);
              }
              // Ownership was taken on a cart. If the login made one of its own
              // -- started from "Your appointments", before any cart existed --
              // adopt it, or the saved card would sit on a cart nobody uses.
              if (data.cartId && !state.cartId) state.cartId = data.cartId;
              state.cartVouchers = data.vouchers || [];
              // Kept in memory only — deliberately NOT in localStorage. It's
              // an opaque handle to a server-side Boulevard session, and not
              // persisting it means it dies with the tab rather than sitting
              // on disk for whatever runs on this origin next.
              loginSession.loggedIn = true;
              loginSession.verifiedAt = Date.now();
              state.sessionId = loginSession.sessionId = data.sessionId || null;
              loginSession.firstName = (data.contact && data.contact.firstName) || '';
              loginSession.cartOwned = true;
              if (data.sessionId) saveSession(data.sessionId, loginSession.firstName);
              state.savedCards = loginSession.savedCards = data.savedCards || [];
              state.forceManualCard = false;
              // The two numbers this whole change exists to move: how many
              // people get in, and how many of those arrive with a card
              // already on file so checkout has nothing to type.
              trackBookingStep('login_success');
              // NO with/without-saved-card event here any more. Saved cards
              // live on the cart and are readable only once a service is on
              // it, so at login the cart is deliberately empty and this
              // always answered "none" -- on 12 Sep it reported 6 when 22
              // cards were actually found moments later, mislabelling at
              // least 16 logins. saved_cards_found_on_cart, fired after
              // select-service, is the honest signal and the only one now.
              doneTo();
            })
            .catch(function (err) {
              submitBtn.disabled = false;
              errEl.className = 'blvd-promo__result blvd-promo__result--error';
              trackBookingStep('login_code_rejected');
              errEl.textContent = err.message || 'That code didn’t work.';
            });
        });
      }

      // --- Screen: Menu (create cart, then the 5 curated buckets) ---
      function showMenu() {
        // BOOKING STARTED. Fired here and not at the modal opening: by this
        // point they have chosen a studio and are looking at real services,
        // which is a booking attempt rather than a stray tap on BOOK NOW.
        // trackBookingStep already dedupes per modal open via stepsSeen, so
        // this reads that same guard instead of adding a second one -- one
        // event per attempt, however many times they switch studio.
        if (!stepsSeen.menu) {
          klaviyoTrack('Booking Started', {
            Studio: (state.location && state.location.name) || null,
            Source: referralSource() || 'direct',
            'Resume Url': bookingResumeUrl(),
          });
        }
        trackBookingStep('menu');
        renderPill();
        setBack(showLocations);
        renderLoading();
        // Hand back the cart the login took ownership of, if there is one.
        // Saved cards are readable only on an owned cart, and ownership is
        // taken once against one cart with a code that cannot be replayed --
        // so a fresh cart here would cost a logged-in customer the card entry
        // they just logged in to avoid. The backend moves that cart to this
        // studio rather than replacing it.
        var sentOwnedCartId = (loginSession.cartOwned && state.cartId) || null;
        api('menu', {
          locationId: state.location.id,
          referralSource: referralSource(),
          cartId: sentOwnedCartId || undefined,
          sessionId: (loginSession.loggedIn && state.sessionId) || undefined,
        })
          .then(function (data) {
            // We asked the backend to KEEP the cart the login owned, and got
            // a different one back: the move to this studio failed and the
            // saved card went with it. The backend logs why to a console
            // nobody can read after the fact, so count it here where it is
            // queryable tomorrow morning.
            if (sentOwnedCartId && data.cartId !== sentOwnedCartId) {
              trackBookingStep('owned_cart_replaced');
            }
            state.cartId = data.cartId;
            state.buckets = data.buckets;
            // They asked to log in before choosing a studio. NOW there is a
            // cart at the studio they picked, so the login code can own the
            // right one.
            if (state.pendingLogin) {
              state.pendingLogin = false;
              showLogin(showMenu, showMenu);
              return;
            }
            if (state.buckets.length === 0) {
              renderError('Nothing is bookable online at this studio right now. Please call to book.', showLocations);
              return;
            }
            // A page's BOOK NOW/JOIN NOW CTA can carry a target bucket (see
            // init()'s data-blvd-bucket) — e.g. /pages/membership should
            // land straight on "Become a Member" once a studio is picked,
            // not the generic 5-option menu. Consumed once so switching
            // studios afterward shows the normal picker, not a repeat jump.
            var deepBucket = state.deepLinkBucket;
            state.deepLinkBucket = null;
            var target = deepBucket && state.buckets.filter(function (b) { return b.key === deepBucket; })[0];
            if (target) {
              enterBucket(target);
            } else if (TAXONOMY_MERGE) {
              showStart();
            } else {
              showBuckets();
            }
            if (state.locationWasDefaulted) {
              state.locationWasDefaulted = false;
              showDefaultBanner();
            }
          })
          .catch(function (err) { renderError(err.message, showMenu); });
      }

      // One icon + brand color per bucket — real visual variety on the
      // most-viewed screen, not a repeated single accent everywhere.
      // One icon per treatment, drawn in the same stroke style and palette as
      // the bucket icons below, so the three session rows and the four other
      // options read as one list rather than two unrelated groups.
      // --- WTHN x Tata Harper, Oct 2026 --------------------------------
      // Presentation-only overlay. The Boulevard service names are NOT
      // renamed: parseSessionItem() in booking.js derives the treatment and
      // the first-time/returning tier by matching the "First Time WTHN
      // Client ..." / "Returning ..." prefixes on the raw names, and the
      // $1,480 Facial Acupuncture 8-Pack, the $480 First Time 3-Pack, the
      // referral offer and every creator comp code are pinned to those two
      // service ids. So the branding is applied here, at render time, and
      // the internal treatment key stays 'Facial Acupuncture' -- which is
      // what TREATMENT_ICONS, the ?treatment= deep link and the GA4 step
      // names all key off.
      //
      // Deliberately no end date in the note: the run is "this month" for
      // now and is expected to extend into November, so a hard date here
      // would have to be corrected rather than simply left alone.
      // Remove both entries when the partnership ends.
      var TATA_NOTE = 'This month only. Every session is paired with Tata Harper skincare.';
      var TREATMENT_LABELS = {
        'Facial Acupuncture': 'Tata Harper Facial Acupuncture',
      };
      var TREATMENT_NOTES = {
        'Facial Acupuncture': TATA_NOTE,
      };
      // The session detail screen titles itself from Boulevard's raw service
      // name (state.itemDetail.name, fetched separately from the bucket item),
      // so it needs its own mapping. Keeps the tier in the title -- it is the
      // only confirmation on that screen of which one the customer picked.
      var TATA_ITEM_LABELS = {
        'First Time WTHN Client Facial Acupuncture Session': 'Tata Harper Facial Acupuncture \u00b7 First Time at WTHN',
        'Returning Facial Acupuncture Session': 'Tata Harper Facial Acupuncture \u00b7 Returning',
      };
      function treatmentLabel(t) { return TREATMENT_LABELS[t] || t; }

      var TREATMENT_ICONS = {
        'Acupuncture': { cls: 'heal', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 20l11-11"/><circle cx="17.5" cy="6.5" r="2.5"/></svg>' },
        'Facial Acupuncture': { cls: 'transform', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M9 10h.01M15 10h.01M9.5 15c1.2.9 3.8.9 5 0"/></svg>' },
        'Cupping': { cls: 'release', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M7 4h10v6a5 5 0 0 1-10 0z"/><path d="M12 15v5"/></svg>' },
      };

      var BUCKET_ICONS = {
        'book-a-session': { cls: 'heal', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>' },
        'buy-a-package': { cls: 'transform', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="7" width="16" height="13" rx="1"/><path d="M4 11h16M12 7v4"/></svg>' },
        'become-a-member': { cls: 'release', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20s-6.5-4.35-9-8.5C1 8 2.5 4.5 6 4.5c2.2 0 3.6 1.3 4 2 .4-.7 1.8-2 4-2 3.5 0 5 3.5 3 7C18.5 15.65 12 20 12 20z"/></svg>' },
        'first-time-deals': { cls: 'transform', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12,2 14.9,8.6 22,9.3 16.5,13.9 18.2,21 12,17.3 5.8,21 7.5,13.9 2,9.3 9.1,8.6"/></svg>' },
        'gift-cards': { cls: 'heal', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="8" width="16" height="12" rx="1"/><path d="M4 8h16M12 8v12"/><path d="M12 8c-1.2 0-3-.8-3-2.3S10.5 4 12 5.5 13.2 8 12 8zM12 8c1.2 0 3-.8 3-2.3S13.5 4 12 5.5 10.8 8 12 8z"/></svg>' },
      };

      function showBuckets() {
        trackBookingStep('category');
        // Same reasoning as showStart: backing out to the bucket list means
        // nothing is chosen, and leaving state behind makes the studio
        // switcher carry it.
        state.bucket = null;
        state.service = null;
        state.item = null;
        state.selectedStaffVariantId = null;
        state.selectedAddons = [];
        state.time = null;
        setBack(showLocations);
        var html = '<h2 class="blvd-title">' + escapeHtml(state.location.name) + '</h2>' +
          '<p class="blvd-subtitle">What would you like to do?</p><ul class="blvd-list">';
        state.buckets.forEach(function (b, i) {
          var icon = BUCKET_ICONS[b.key];
          html += '<li><button type="button" class="blvd-option blvd-option--bucket" data-i="' + i + '">' +
            (icon ? '<span class="blvd-option__icon blvd-icon--' + icon.cls + '">' + icon.svg + '</span>' : '') +
            '<span class="blvd-option__name">' + escapeHtml(b.label) + '</span></button></li>';
        });
        html += '</ul>';
        body.innerHTML = html;
        body.querySelectorAll('.blvd-option').forEach(function (btn) {
          btn.addEventListener('click', function () {
            enterBucket(state.buckets[Number(btn.getAttribute('data-i'))]);
          });
        });
      }

      // --- Screen: one choice instead of two ------------------------------
      // Replaces buckets -> treatment for the session path. The three real
      // treatments lead, because sessions are ~99% of online bookings; the
      // other visit types keep their own rows underneath, so nothing becomes
      // unreachable and every deep link still resolves through enterBucket.
      //
      // Falls back to showBuckets whenever the assumption behind it does not
      // hold -- no Book a Session bucket at this studio, or its items are not
      // all tiered -- rather than rendering a screen with nothing on it.
      // The one answer to "which screen is the menu" -- so every back target
      // and every fallback agrees, whichever way the flag is set.
      function menuScreen() {
        return TAXONOMY_MERGE ? showStart : showBuckets;
      }

      function sessionBucket() {
        return state.buckets.filter(function (b) { return b.key === 'book-a-session'; })[0];
      }

      function canMergeTaxonomy() {
        var sb = sessionBucket();
        if (!sb || !sb.items || !sb.items.length) return false;
        // showTierPicker is what we hand the treatment to, and it only makes
        // sense when every item carries a tier -- same test enterBucket uses.
        return sb.items.every(function (i) { return i.tier !== 'any'; });
      }

      function showStart() {
        if (!canMergeTaxonomy()) { showBuckets(); return; }
        // This screen IS the "nothing chosen yet" state, so it must say so.
        // state.bucket was sticky: entering First-Time Client Deals and then
        // pressing back left it set, and the studio switcher deliberately
        // carries the current bucket across a switch -- so changing studio
        // from this screen dropped the customer into a bucket they had
        // already backed out of. Reported live 2026-09-10.
        state.bucket = null;
        state.service = null;
        state.item = null;
        state.selectedStaffVariantId = null;
        state.selectedAddons = [];
        state.time = null;
        // Same funnel position as the screen it replaces (the first choice
        // after the studio), so the 'category' step stays continuous either
        // side of the flip. 'treatment' simply stops firing for sessions --
        // that drop IS the intended effect, and is how we measure it.
        trackBookingStep('category');
        setBack(showLocations);
        var sb = sessionBucket();
        var byTreatment = {};
        sb.items.forEach(function (item) {
          (byTreatment[item.treatment] = byTreatment[item.treatment] || []).push(item);
        });
        var all = Object.keys(byTreatment);
        var main = MAIN_TREATMENTS.filter(function (t) { return all.indexOf(t) !== -1; });
        // Anything Boulevard adds that isn't one of the three known mains
        // still gets a row -- never silently dropped.
        var rest = all.filter(function (t) { return main.indexOf(t) === -1; });
        var treatments = main.concat(rest);

        // TREATMENT FIRST, tier second.
        //
        // This screen briefly listed all seven services flat, grouped under
        // treatment headings. Spencer flagged it and he was right: it inverts
        // the hierarchy. The real decision -- Acupuncture vs Facial vs
        // Cupping -- became a small grey heading, while the QUALIFIER ("First
        // time" / "Returning") became the bold text, repeated seven times. The
        // eye scans seven near-identical rows instead of three distinct ones,
        // and First-Time Client Deals ends up below the fold, which is exactly
        // the wrong thing to bury for new-client traffic like the UES flyers.
        //
        // The win was never flattening treatment x tier -- it was deleting the
        // "What would you like to do?" bucket screen. That stays deleted. The
        // tier question gets its own screen again, where it reads as one clear
        // question with three answers.
        //
        // Worth noting the tier never changes the price ($120 across all three
        // acupuncture tiers, $200 across both facial), so it is genuinely a
        // qualifier and not something the customer should be pricing.
        var html = '<h2 class="blvd-title">' + escapeHtml(state.location.name) + '</h2>' +
          '<p class="blvd-subtitle">What would you like to book?</p>' +
          returningPromptHtml() +
          '<ul class="blvd-list">';
        treatments.forEach(function (t) {
          var ti = TREATMENT_ICONS[t];
          html += '<li><button type="button" class="blvd-option blvd-option--bucket" data-treatment="' + escapeHtml(t) + '">' +
            (ti ? '<span class="blvd-option__icon blvd-icon--' + ti.cls + '">' + ti.svg + '</span>' : '') +
            '<span class="blvd-option__body">' +
            optionRowHtml(treatmentLabel(t), priceLabelForItems(byTreatment[t]), TREATMENT_NOTES[t]) +
            '</span></button></li>';
        });
        html += '</ul>';

        var others = state.buckets.filter(function (b) { return b.key !== 'book-a-session'; });
        if (others.length) {
          // A real divider rather than the hairline that was there before --
          // without it the deals read as a fourth treatment. Labelled, because
          // an unexplained rule is just a gap; "Or" read as an afterthought
          // above a new-client conversion path.
          html += '<p class="blvd-divider"><span>More ways to book</span></p>' +
            '<ul class="blvd-list">';
          others.forEach(function (b, i) {
            var icon = BUCKET_ICONS[b.key];
            html += '<li><button type="button" class="blvd-option blvd-option--bucket" data-bucket="' + i + '">' +
              (icon ? '<span class="blvd-option__icon blvd-icon--' + icon.cls + '">' + icon.svg + '</span>' : '') +
              '<span class="blvd-option__name">' + escapeHtml(b.label) + '</span></button></li>';
          });
          html += '</ul>';
        }
        body.innerHTML = html;
        wireReturningPrompt(showStart);
        body.querySelectorAll('[data-treatment]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            // Entering the session bucket by hand, so state.bucket must be set
            // exactly as enterBucket would -- showTierPicker and chooseItem
            // both read it.
            state.bucket = sb;
            var t = btn.getAttribute('data-treatment');
            showTierPicker(t, byTreatment[t], showStart);
          });
        });
        body.querySelectorAll('[data-bucket]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            enterBucket(others[Number(btn.getAttribute('data-bucket'))]);
          });
        });
      }

      // Shared bucket-entry routing — used by a manual click on showBuckets()
      // and by a bucket-targeted deep link (showMenu()) alike, so both paths
      // land in the same place.
      function enterBucket(bucket) {
        state.bucket = bucket;
        if (bucket.key === 'gift-cards') { showGiftCardAmount(); return; }
        // Third and fourth deep-link dimensions, alongside location/bucket:
        // a specific treatment (e.g. a page about Facial Acupuncture should
        // land right on Facial Acupuncture's tier picker, not the full Book
        // a Session treatment list) or a specific item by id — needed for a
        // bucket that isn't treatment-grouped at all (a flat list like
        // First-Time Client Deals), where "treatment" has nothing to match
        // against. Both consumed once, same as deepLinkBucket — a studio
        // switch afterward shows the normal list. Item id is checked first
        // and works in either kind of bucket; it's the only way to point at
        // one specific item in a flat list. Both silently fall through to
        // the bucket's normal listing if nothing matches, same as an
        // unrecognized bucket/location falls back a step.
        var deepTreatment = state.deepLinkTreatment;
        var deepItemId = state.deepLinkItemId;
        var deepItemName = state.deepLinkItemName;
        state.deepLinkTreatment = null;
        state.deepLinkItemId = null;
        state.deepLinkItemName = null;
        // Group into treatment + first-time/returning qualifier whenever
        // every item in the bucket actually has a tier to qualify — Book a
        // Session and Buy a Package both look like this (Boulevard's raw
        // catalog flattens "4 fertility programs x 2 client-history tiers"
        // into 8-10 confusingly-named rows). A bucket with no tiered items
        // (or only some) stays a flat list — forcing the qualifier flow on
        // untiered items would add a question with nothing to answer.
        var isTiered = bucket.items.every(function (i) { return i.tier !== 'any'; });
        var backTo = isTiered ? showTreatments : showPurchasableList;
        if (deepItemId) {
          var directItem = bucket.items.filter(function (i) { return i.id === deepItemId; })[0];
          if (directItem) { chooseItem(directItem, backTo); return; }
        }
        // Name match, for a studio switch (see doSwitch) -- ids don't survive
        // one. Falls through to the normal listing when the new studio
        // doesn't carry that item.
        if (deepItemName) {
          var namedItem = bucket.items.filter(function (i) { return i.name === deepItemName; })[0];
          if (namedItem) { chooseItem(namedItem, backTo); return; }
        }
        if (isTiered) {
          // With the taxonomy merged, the session bucket's treatment list is
          // already the top half of the start screen -- so a treatment deep
          // link goes back THERE rather than through a second screen listing
          // the same three treatments.
          var tierBack = (TAXONOMY_MERGE && bucket.key === 'book-a-session') ? showStart : showTreatments;
          if (deepTreatment) {
            var byTreatment = groupBucketItems();
            if (byTreatment[deepTreatment]) {
              showTierPicker(deepTreatment, byTreatment[deepTreatment], tierBack);
              return;
            }
          }
          // Same reasoning for a bucket deep link with no treatment: the
          // merged screen leads with exactly this bucket's treatments.
          if (TAXONOMY_MERGE && bucket.key === 'book-a-session') { showStart(); return; }
          showTreatments();
        }
        else { showPurchasableList(); }
      }

      // --- Treatment, then a first-time/returning qualifier — used by any
      // bucket where every item has one (Book a Session, Buy a Package),
      // instead of Boulevard's flat, confusingly-named rows. Book a
      // Session's 3 main treatments get their own group, separate from
      // Fertility Support's 4 — no such "main" set exists for other
      // buckets, so they just list every treatment flat.
      var TIER_LABELS = { 'first-time': 'First time at WTHN', 'returning': 'Returning (within the past year)', 'returning-1yr': 'Returning (after 1+ year away)' };
      var MAIN_TREATMENTS = ['Acupuncture', 'Facial Acupuncture', 'Cupping'];
      // Buy a Package mixes the general packs with Fertility Support's four
      // programs, and Boulevard returns fertility first — so a client arriving
      // from pain / sleep / digestion / skin / anxiety saw four fertility
      // programs above the two packs that actually applied to them. Same
      // main/rest split Book a Session already uses, so both buckets read the
      // same way. Prefix-matched, not a hardcoded list, so a future pack size
      // joins the general group automatically.
      var GENERAL_PACKAGE_PREFIX = 'Healing Acupuncture';

      function mainTreatmentsFor(bucketKey, all) {
        if (bucketKey === 'book-a-session') {
          return MAIN_TREATMENTS.filter(function (t) { return all.indexOf(t) !== -1; });
        }
        if (bucketKey === 'buy-a-package') {
          var general = all.filter(function (t) { return t.indexOf(GENERAL_PACKAGE_PREFIX) === 0; });
          // If the packs are ever renamed, fall back to the old flat list
          // rather than silently hiding every package behind a subheading.
          return general.length ? general : all;
        }
        return all;
      }

      function groupBucketItems() {
        var byTreatment = {};
        state.bucket.items.forEach(function (item) {
          (byTreatment[item.treatment] = byTreatment[item.treatment] || []).push(item);
        });
        return byTreatment;
      }

      function showTreatments() {
        trackBookingStep('treatment');
        setBack(menuScreen());
        var byTreatment = groupBucketItems();
        var allTreatments = Object.keys(byTreatment);
        var main = mainTreatmentsFor(state.bucket.key, allTreatments);
        var rest = allTreatments.filter(function (t) { return main.indexOf(t) === -1; });

        var html = '<h2 class="blvd-title">' + escapeHtml(state.bucket.label) + '</h2><ul class="blvd-list">';
        main.forEach(function (t) {
          html += '<li><button type="button" class="blvd-option" data-treatment="' + escapeHtml(t) + '">' +
            optionRowHtml(treatmentLabel(t), priceLabelForItems(byTreatment[t]), TREATMENT_NOTES[t]) + '</button></li>';
        });
        html += '</ul>';
        if (rest.length) {
          html += '<p class="blvd-subtitle" style="margin-top:20px">Fertility Support</p><ul class="blvd-list">';
          rest.forEach(function (t) {
            html += '<li><button type="button" class="blvd-option" data-treatment="' + escapeHtml(t) + '">' +
              optionRowHtml(treatmentLabel(t), priceLabelForItems(byTreatment[t]), TREATMENT_NOTES[t]) + '</button></li>';
          });
          html += '</ul>';
        }
        body.innerHTML = html;
        body.querySelectorAll('.blvd-option').forEach(function (btn) {
          btn.addEventListener('click', function () {
            showTierPicker(btn.getAttribute('data-treatment'), byTreatment[btn.getAttribute('data-treatment')]);
          });
        });
      }

      // Routes a chosen item to the right next step by its real type — a
      // bookable Service goes straight into the time picker (no payment,
      // like every other Book a Session item); a purchasable item goes to
      // the item-detail/price screen, same as anything in Buy a Package.
      // `backTo` is whatever screen actually showed this item (the flat
      // list or the tier picker) — showItemDetail uses it instead of
      // assuming one fixed origin, since items reach it two different ways.
      function chooseItem(item, backTo) {
        state.itemBackTo = backTo;
        state.item = item;
        // Bookable items get the merged session screen; purchasables keep
        // the plain detail screen, having neither a professional nor add-ons.
        if (item.kind === 'bookable') { showSessionScreen(); return; }
        showItemDetail();
      }

      function showTierPicker(treatment, items, backTo) {
        trackBookingStep('tier');
        // Reached from the merged start screen as well as the treatment list,
        // so the back target is passed in rather than assumed.
        setBack(backTo || showTreatments);
        // Display order: first-time, then returning within the past year,
        // then returning after 1+ year away — independent of whatever
        // order Boulevard's catalog happens to list the items in.
        var TIER_ORDER = { 'first-time': 0, 'returning': 1, 'returning-1yr': 2 };
        items = items.slice().sort(function (a, b) {
          return (TIER_ORDER[a.tier] != null ? TIER_ORDER[a.tier] : 99) -
            (TIER_ORDER[b.tier] != null ? TIER_ORDER[b.tier] : 99);
        });
        var backHere = function () { showTierPicker(treatment, items, backTo); };
        // A treatment with only one tier (or none) skips the question
        // entirely — no point qualifying when there's nothing to qualify.
        if (items.length === 1) {
          chooseItem(items[0], backTo || showTreatments);
          return;
        }
        var tierNote = TREATMENT_NOTES[treatment];
        var html = '<h2 class="blvd-title">' + escapeHtml(treatmentLabel(treatment)) + '</h2>' +
          // A ?treatment= deep link lands on this screen directly (see
          // enterBucket), so anyone arriving from the Tata Harper landing
          // page never passes the start screen's copy of this note.
          (tierNote ? '<p class="blvd-note">' + escapeHtml(tierNote) + '</p>' : '') +
          '<p class="blvd-subtitle">Have you been to WTHN before?</p><ul class="blvd-list">';
        items.forEach(function (item, i) {
          html += '<li><button type="button" class="blvd-option" data-i="' + i + '">' +
            optionRowHtml(TIER_LABELS[item.tier] || item.name, priceLabelForItems([item])) + '</button></li>';
        });
        html += '</ul>';
        body.innerHTML = html;
        body.querySelectorAll('.blvd-option').forEach(function (btn) {
          btn.addEventListener('click', function () {
            chooseItem(items[Number(btn.getAttribute('data-i'))], backHere);
          });
        });
      }


      // A retail item reached a booking call. The customer picked something
      // real — the $270 Acupuncture 3-Pack, say — so send them to the screen
      // that can actually sell it instead of showing them an error. Live
      // 2026-09-08 this dead-ended: four customers hit an opaque Boulevard
      // NOT_FOUND and could not proceed. The path that gets them here is
      // still unidentified, which is exactly why the recovery is worth
      // having rather than relying on having found every route in.
      function recoverIfNotBookable(err) {
        if (!err || err.code !== 'NOT_A_BOOKABLE_ITEM') return false;
        showItemDetail();
        return true;
      }


      // --- Screen: Add-On Services — optional extras (Cupping, Ear Seed,
      // Body Balance Magnets etc.) riding along on the same appointment as
      // the just-selected service. Purely local/toggleable until "Continue"
      // — nothing is actually added to the cart until then, so a stray tap
      // costs nothing to undo. Only shown when the service has any (most
      // don't); otherwise selectServiceAndShowTimes skips straight to
      // showTimes.

      // --- Screen: Date/Time ---
      function showTimes() {
        // The new arm has no standalone practitioner screen — its picker is on
        // this screen now — so back belongs at the session screen it came
        // from, not at a screen that arm never shows.
        setBack(renderSessionScreen);
        // A DATE IS THIS SCREEN'S OWN PRECONDITION, not the caller's job.
        // Three callers reach here without setting one: "Pick a different
        // time" after someone else takes the slot, the waitlist's manual-card
        // path, and the waitlist error retry. They all assume a date survives
        // from earlier in the flow -- and resetState() nulls it on a studio
        // switch and on reopening the modal, so it does not always. When it
        // did not, `searchDate: undefined` reached the server and the customer
        // was shown the raw validation string "cartId, locationId, searchDate
        // are required" with nothing to do about it. Reported live 2026-09-17
        // from an Instagram in-app browser at Upper East Side.
        //
        // Guarded here, where every caller routes through, rather than at each
        // of the three: the fourth caller should not be able to reintroduce it.
        if (!state.location) { showLocations(); return; }
        if (!state.date) state.date = todayInTz(state.location.tz);
        renderLoading();
        api('times', {
          cartId: state.cartId, locationId: state.location.id, searchDate: state.date, tz: state.location.tz,
          staffVariantIds: state.selectedStaffVariantId ? [state.selectedStaffVariantId] : undefined,
        })
          .then(function (data) {
            state.times = data.times;
            state.date = data.resolvedDate;
            renderTimes(data);
          })
          .catch(function (err) { renderError(err.message, showTimes); });
      }

      // Parsed as a local calendar date, not UTC midnight — `new
      // Date("2026-09-25")` shifts a day backward in negative UTC-offset
      // browsers, which would make this message name the wrong date.
      // The date input has a min of today but no max, so a mistyped year is
      // reachable: typing 2027 landed on a screen reading "Tue, Jun 1" over
      // an offer to join the waitlist for it, with nothing saying the date
      // was nine months out. Show the year whenever it isn't the current
      // one -- and only then, so the everyday case stays "Thu, Sep 11".
      function yearSuffix(d) {
        return d.getFullYear() === new Date().getFullYear() ? '' : ', ' + d.getFullYear();
      }

      function formatDateLabel(dateStr) {
        var p = dateStr.split('-');
        var d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + yearSuffix(d);
      }

      // With the weekday: "Thu, Sep 11" answers what people actually ask of
      // a booking date, without making them count forward.
      function formatDateLabelLong(dateStr) {
        var p = dateStr.split('-');
        var d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
        return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) + yearSuffix(d);
      }

      // Was a bare grey box that read as a disabled field. Same labelled row
      // as the practitioner picker below it, with the native input laid
      // transparently over it so a tap anywhere opens the OS picker.
      // How far ahead Boulevard will actually book. Measured 2026-09-11 by
      // walking cartBookableDates forward in 30-day windows: availability
      // runs to exactly 2027-03-11 and then stops dead -- 6 months to the
      // day -- and all five studios return that same last date, so it is a
      // business-wide rolling policy rather than however far each studio has
      // published its schedules. Neither the Client nor the Admin API
      // exposes the setting as a field (both introspected), so it lives here.
      //
      // If WTHN changes "how far in advance can clients book" in Boulevard,
      // change this number. Setting it SHORTER than the real window is the
      // safe direction to be wrong -- those dates just answer "no openings",
      // as every date past the window did before this cap existed. Setting
      // it longer than the real window is the harmful one.
      var BOOKING_WINDOW_MONTHS = 6;

      function bookingWindowEnd(tz) {
        var p = todayInTz(tz).split('-');
        var d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
        // setMonth clamps a day that the target month is too short for
        // (Aug 31 + 6 -> Feb 31 -> Mar 3), which would offer three days
        // Boulevard will not book. Pin to the last valid day instead.
        var day = d.getDate();
        d.setDate(1);
        d.setMonth(d.getMonth() + BOOKING_WINDOW_MONTHS);
        d.setDate(Math.min(day, new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()));
        return d.getFullYear() + '-' +
          String(d.getMonth() + 1).padStart(2, '0') + '-' +
          String(d.getDate()).padStart(2, '0');
      }

      function datePickerHtml(resolvedDate) {
        return '<div class="blvd-picker blvd-picker--date">' +
          '<label class="blvd-picker__row blvd-picker__row--date">' +
          '<span class="blvd-picker__label">Date</span>' +
          '<span class="blvd-picker__value">' + escapeHtml(formatDateLabelLong(resolvedDate)) + '</span>' +
          '<span class="blvd-picker__action">Change</span>' +
          '<input type="date" class="blvd-date-input" value="' + resolvedDate +
          '" min="' + todayInTz(state.location.tz) +
          '" max="' + bookingWindowEnd(state.location.tz) + '" aria-label="Date">' +
          '</label></div>';
      }

      function slotLabel(t) {
        return new Date(t.startTime).toLocaleTimeString('en-US', {
          hour: 'numeric', minute: '2-digit', timeZone: state.location.tz,
        });
      }

      function renderTimes(data) {
        trackBookingStep('times', { booking_slot_count: (data.times || []).length });
        // Whose availability this is. Without it a customer who deliberately
        // chose a practitioner lost every trace of that choice here — the name
        // appeared nowhere on this screen (audited 2026-09-09).
        var who = selectedStaffFirstName();
        // Order: practitioner, then date, then the times themselves. Who you
        // see constrains which days have anything, and the day constrains the
        // times -- so the screen reads in the order the choices actually
        // narrow. Each picker owns the rule under it.
        var html = '<h2 class="blvd-title">Pick a time</h2>';
        html += staffPickerHtml();
        html += datePickerHtml(data.resolvedDate);
        // Why the date shown is not the one they asked for. Sits against the
        // date itself; under the practitioner row it read as a remark about
        // the practitioner.
        if (data.resolvedDate !== data.requestedDate) {
          html += '<p class="blvd-note blvd-note--date">' +
            (who ? escapeHtml(who) + ' has nothing on ' + formatDateLabel(data.requestedDate)
                 : 'Nothing open on ' + formatDateLabel(data.requestedDate)) +
            ' — showing the next available day, ' + formatDateLabel(data.resolvedDate) + '.</p>';
        }
        if (!data.times || data.times.length === 0) {
          html += '<p class="blvd-note">' +
            (who ? escapeHtml(who) + ' has no openings in the next 30 days.'
                 : 'No openings in the next 30 days.') +
            ' Try a different date.</p>';
        }
        // Every slot the customer can click on this screen, across every day
        // shown. One index space so the click handler stays the one it always
        // was -- the alternate days are just more slots, not a second
        // mechanism that could drift out of step with the first.
        var pickable = [];
        if (data.times && data.times.length) {
          html += '<h3 class="blvd-subhead">Times</h3>';
          html += '<div class="blvd-slots">';
          data.times.forEach(function (t) {
            html += '<button type="button" class="blvd-slot" data-i="' + pickable.length + '">' + slotLabel(t) + '</button>';
            pickable.push({ time: t, date: data.resolvedDate });
          });
          html += '</div>';

          // ALTERNATE DAYS. 47% of everyone who reached this screen left
          // without going on to checkout (measured 5-11 Sep) -- one day at a
          // time makes someone whose day does not suit guess at another and
          // pay a round trip to find out. Showing the next open days is how
          // every booking site people already use behaves.
          (data.alternates || []).forEach(function (alt) {
            html += '<h3 class="blvd-subhead blvd-subhead--alt">' + escapeHtml(formatDateLabelLong(alt.date)) + '</h3>';
            html += '<div class="blvd-slots">';
            alt.times.forEach(function (t) {
              html += '<button type="button" class="blvd-slot" data-i="' + pickable.length + '">' + slotLabel(t) + '</button>';
              pickable.push({ time: t, date: alt.date });
            });
            html += '</div>';
            // Only when there is genuinely more behind it, so the link never
            // promises a fuller day than exists.
            if (alt.total > alt.times.length) {
              html += '<button type="button" class="blvd-moreon blvd-moreon--alt" data-date="' + escapeHtml(alt.date) + '">' +
                'See all ' + alt.total + ' times on ' + escapeHtml(formatDateLabel(alt.date)) + ' \u2192</button>';
            }
          });
        }
        // A thin day keeps its own slots above; this simply says a fuller
        // one exists, rather than leaving 4-7 scattered times looking like
        // everything WTHN has. Only rendered when the next day is genuinely
        // better — the server withholds it otherwise.
        if (data.moreOn) {
          html += '<button type="button" class="blvd-moreon" id="blvd-more-on">' +
            'More times on ' + escapeHtml(formatDateLabel(data.moreOn.date)) +
            ' \u2014 ' + data.moreOn.count + ' available \u2192</button>';
        }

        // THE WAITLIST, on a thin day as well as an empty one.
        //
        // It used to render only when a day came back with zero times -- and
        // the server's fall-forward means a day almost never does, so in the
        // 16 days the widget has been live waitlist_joined never fired once
        // against 1,327 views of this screen. The dead end it was built for
        // is not the empty day at all: it is Upper East Side, which returned
        // 4, 7, 6, 8, 6, 7, 7 slots over the next week (checked 2026-09-14).
        // Never empty, so no waitlist; never a materially fuller day either,
        // so no moreOn. Someone none of those four times suit was shown
        // nothing and left, or phoned the studio.
        //
        // Below the slots deliberately: on a thin day the times are still the
        // thing to pick, and this is the fallback under them.
        var noTimes = !data.times || data.times.length === 0;
        if (noTimes || data.thin) {
          var waitDay = escapeHtml(formatDateLabel(data.resolvedDate || state.date));
          html += '<button type="button" class="blvd-btn blvd-btn--secondary" id="blvd-waitlist">' +
            (noTimes ? 'Add me to the waitlist for ' + waitDay
                     : 'None of these work? Join the waitlist for ' + waitDay) + '</button>' +
            '<p class="blvd-promo__result" id="blvd-waitlist-msg"></p>';
        }
        body.innerHTML = html;
        // Changing practitioner refetches, because their availability differs.
        wireStaffPicker(showTimes);
        var dateInput = body.querySelector('.blvd-date-input');
        dateInput.addEventListener('change', function (e) {
          state.date = e.target.value;
          showTimes();
        });
        // The input is opacity:0 and laid over the whole row, with
        // -webkit-appearance:none stripping the native calendar indicator.
        // On a phone, tapping anywhere on a date input opens the picker, so
        // this worked -- and the CSS above only reasons about iOS. On DESKTOP
        // there is no indicator left to click and a click merely focuses an
        // invisible field, so "Change" looked like a dead control (reported
        // live 2026-09-16, Upper East Side). showPicker() is the only way to
        // open it programmatically. It must run inside a user gesture and
        // throws otherwise, hence the try/catch; focus() is the fallback for
        // anything that does not support it, which still lets the keyboard
        // change the date.
        dateInput.addEventListener('click', function (e) {
          if (typeof dateInput.showPicker !== 'function') return;
          try {
            dateInput.showPicker();
            // Only stop the label re-dispatching this click into the input
            // once the picker is actually open -- otherwise a browser that
            // throws would be left with neither the picker nor the default.
            e.preventDefault();
          } catch (err) {
            dateInput.focus();
          }
        });
        var waitBtn = document.getElementById('blvd-waitlist');
        if (waitBtn) {
          waitBtn.addEventListener('click', function () {
            var msg = document.getElementById('blvd-waitlist-msg');
            var day = data.resolvedDate || state.date;
            // Whatever slot was picked earlier, they have just said none of
            // these work. Holding it is wrong on its own terms, and it is
            // also the fuel for an accidental booking: showTimes never clears
            // state.time, so a customer who picked a slot, went back, and
            // then waitlisted was carrying a bookable time into a journey
            // that must never book one.
            state.time = null;
            // `msg` and `waitBtn` belong to the TIMES screen. If the join
            // needed a card, the customer is now on a card screen and those
            // two nodes are detached -- still writable, so nothing throws, but
            // nobody ever sees a word of it. Measured on live 2026-09-14: the
            // join returned {"ok":true} and the customer was left looking at
            // "Confirming…" indefinitely. A successful action that reports
            // nothing is the same dead end as a failed one.
            var onTimesScreen = function () {
              return msg && document.body.contains(msg);
            };
            var join = function () {
              if (onTimesScreen()) {
                waitBtn.disabled = true;
                msg.className = 'blvd-promo__result';
                msg.textContent = 'Adding you…';
              }
              api('add-to-waitlist', { cartId: state.cartId, date: day, tz: state.location.tz })
                .then(function () {
                  trackBookingStep('waitlist_joined');
                  if (onTimesScreen()) {
                    msg.className = 'blvd-promo__result blvd-promo__result--ok';
                    msg.textContent = 'You’re on the waitlist for ' + formatDateLabel(day) +
                      '. We’ll be in touch if something opens up.';
                    return;
                  }
                  showSuccess('You’re on the waitlist',
                    'We’ll be in touch if something opens up on ' + formatDateLabel(day) + '.');
                })
                .catch(function (err) {
                  if (onTimesScreen()) {
                    waitBtn.disabled = false;
                    msg.className = 'blvd-promo__result blvd-promo__result--error';
                    msg.textContent = err.message || 'Couldn’t add you just now.';
                    return;
                  }
                  renderError(err.message || 'Couldn’t add you to the waitlist just now.',
                    function () { showTimes(); });
                });
            };
            // Boulevard treats joining a waitlist as a checkout-grade action:
            // it needs contact details AND a payment method on every item
            // (CART_MISSING_CLIENT_INFORMATION, then
            // CART_MISSING_ITEM_PAYMENT_METHOD). That matches WTHN's own rule
            // that a card holds any appointment -- so this runs the real flow
            // rather than surfacing Boulevard's wording at a dead end.
            // Nothing below may fail silently. Measured 2026-09-14: clicking
            // this after switching studios ran update-client-info and
            // use-saved-card, the second returned NOT_FOUND, and because the
            // chain had no catch the customer got an empty message, a
            // re-enabled button and no idea anything had gone wrong. That is
            // the same invisible dead end the validation beacon exists for.
            var failed = function (err) {
              waitBtn.disabled = false;
              msg.className = 'blvd-promo__result blvd-promo__result--error';
              msg.textContent = (err && err.message) || 'Couldn’t add you just now.';
              reportClientError('waitlist join: ' + ((err && err.message) || 'unknown'), 'validation');
            };
            var manualCard = function () {
              showCardEntry(function () { showTimes(); }, true, {
                title: 'Card to hold your place',
                note: 'We only charge if a spot opens and you take it.',
                onAttached: join,
              });
            };
            var withCard = function () {
              // A returning client whose card is already on the cart skips
              // straight through; everyone else enters one once.
              if (state.savedCards && state.savedCards.length) {
                return api('use-saved-card', {
                  cartId: state.cartId, paymentMethodId: state.savedCards[0].id,
                }).then(join, function () {
                  // The card on file would not attach. Boulevard scopes a
                  // payment method to the SELECTED ITEM rather than to the
                  // client, so an id fetched before the cart moved studios is
                  // stale -- exactly what the studio switcher does. Drop it
                  // and let them enter one instead of stopping here.
                  state.savedCards = [];
                  manualCard();
                });
              }
              manualCard();
            };
            if (state.contact && state.contact.email && state.contact.firstName) {
              ensureClientOnCart().then(withCard).catch(failed);
            } else {
              showContact({
                title: 'Join the waitlist',
                backTo: showTimes,
                next: function () { ensureClientOnCart().then(withCard).catch(failed); },
              });
            }
          });
        }

        var moreOnBtn = document.getElementById('blvd-more-on');
        if (moreOnBtn) {
          moreOnBtn.addEventListener('click', function () {
            state.date = data.moreOn.date;
            showTimes();
          });
        }
        // "See all times on <day>" -- switch the picker to that day properly,
        // so the date row, the input and any later back-navigation agree.
        body.querySelectorAll('.blvd-moreon--alt').forEach(function (btn) {
          btn.addEventListener('click', function () {
            state.date = btn.getAttribute('data-date');
            trackBookingStep('times_alt_see_all');
            showTimes();
          });
        });
        body.querySelectorAll('.blvd-slot').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var pick = pickable[Number(btn.getAttribute('data-i'))];
            state.time = pick.time;
            // Booking keys off the slot's own id, so this is not needed to
            // book -- but the confirm screen and a back-step both read
            // state.date, and they must not still say the day the customer
            // arrived on.
            if (pick.date && pick.date !== state.date) {
              state.date = pick.date;
              trackBookingStep('times_alt_picked');
            }
            // A professional was already chosen upfront (times are already
            // filtered to just them) — nothing left to narrow. Otherwise,
            // show who's actually free at this exact time.
            if (state.selectedStaffVariantId) {
              goCheckout(showTimes, true);
            } else {
              showStaffForTime();
            }
          });
        });
      }

      // --- Screen: Contact (shared by session booking and every purchase
      // flow — same fields either way, only where it leads next differs).
      function showContact(opts) {
        trackBookingStep('contact');
        state.lastContactOpts = opts; // lets showCardEntry's back button return here
        setBack(opts.backTo);
        var c = state.contact;
        body.innerHTML =
          '<h2 class="blvd-title">' + escapeHtml(opts.title) + '</h2>' +
          '<form class="blvd-form">' +
          '<div class="blvd-form-row">' +
          '<label>First name<input type="text" name="firstName" autocomplete="given-name" required placeholder="Jane" value="' + escapeHtml(c.firstName || '') + '"></label>' +
          '<label>Last name<input type="text" name="lastName" autocomplete="family-name" required placeholder="Doe" value="' + escapeHtml(c.lastName || '') + '"></label>' +
          '</div>' +
          '<label>Email<input type="email" name="email" autocomplete="email" required placeholder="jane@example.com" value="' + escapeHtml(c.email || '') + '"></label>' +
          '<label>Phone<input type="tel" name="phoneNumber" autocomplete="tel" required placeholder="(555) 555-5555" value="' + escapeHtml(c.phoneNumber || '') + '"></label>' +
          '<button type="submit" class="blvd-btn blvd-btn--primary">Continue</button>' +
          '</form>';
        body.querySelector('.blvd-form').addEventListener('submit', function (e) {
          e.preventDefault();
          var form = e.target;
          state.contact = {
            firstName: form.firstName.value.trim(),
            lastName: form.lastName.value.trim(),
            email: form.email.value.trim(),
            phoneNumber: form.phoneNumber.value.trim(),
          };
          saveContact(state.contact);
          opts.next();
        });
      }

      // --- Screen: Confirm a session booking (free — paid for on-site) ---

      // --- Buy a Package / Become a Member / First-Time Client Deals ---
      // Structurally identical: pick an item, see real price/description,
      // agree to membership terms if required, then pay. A couple of items
      // here (the seasonal 3-Packs) are actually free single sessions
      // despite living in a "deals" bucket by customer meaning rather than
      // technical type — routed straight to the booking flow instead of
      // checkout, same as Book a Session.
      function showPurchasableList() {
        trackBookingStep('package_list');
        setBack(menuScreen());
        // Cheapest first, so someone comparing tiers reads them as a
        // natural ladder — for Become a Member this also covers the
        // comparison view below, which reads from this same (now-sorted)
        // array.
        if (state.bucket.key === 'become-a-member' || state.bucket.key === 'first-time-deals') {
          state.bucket.items.sort(function (a, b) {
            return (a.priceRange ? a.priceRange.min : 0) - (b.priceRange ? b.priceRange.min : 0);
          });
        }
        var canCompare = state.bucket.key === 'become-a-member' && state.bucket.items.length > 1;
        var html = '<h2 class="blvd-title">' + escapeHtml(state.bucket.label) + '</h2>' +
          (canCompare ? '<button type="button" class="blvd-compare-link">Compare all memberships</button>' : '') +
          '<ul class="blvd-list" style="margin-top:' + (canCompare ? '14px' : '0') + '">';
        state.bucket.items.forEach(function (item, i) {
          html += '<li><button type="button" class="blvd-option" data-i="' + i + '">' +
            optionRowHtml(item.name, priceLabelWithCompareAt(item)) + '</button></li>';
        });
        html += '</ul>';
        body.innerHTML = html;
        if (canCompare) {
          body.querySelector('.blvd-compare-link').addEventListener('click', showMembershipComparison);
        }
        body.querySelectorAll('.blvd-option').forEach(function (btn) {
          btn.addEventListener('click', function () {
            chooseItem(state.bucket.items[Number(btn.getAttribute('data-i'))], showPurchasableList);
          });
        });
      }

      // Real terms, side by side — each membership's actual price and full
      // description verbatim from Boulevard, not reinterpreted into a
      // feature-matrix. These are financial commitment terms (monthly
      // billing, cancellation policy); a hand-parsed checkmark grid risks
      // mis-summarizing them, so the safe version is just "make comparing
      // the real text easier" rather than "restate what's included."
      function showMembershipComparison() {
        setBack(showPurchasableList);
        renderLoading();
        Promise.all(state.bucket.items.map(function (item) {
          return api('item-detail', { cartId: state.cartId, itemId: item.id }).then(function (data) { return data.detail; });
        })).then(function (details) {
          var html = '<h2 class="blvd-title">Compare memberships</h2><div class="blvd-compare">';
          details.forEach(function (d, i) {
            html += '<div class="blvd-compare__card">' +
              '<h3 class="blvd-compare__name">' + escapeHtml(d.name) + '</h3>' +
              '<span class="blvd-price-badge">' + escapeHtml(formatPrice(d.listPriceRange.min)) + '</span>' +
              (d.description ? '<p class="blvd-note" style="white-space:pre-line">' + escapeHtml(d.description) + '</p>' : '') +
              // Somewhere to go having just decided. This screen exists to
              // persuade and had no CTA at all: a customer who read all four
              // plans and picked one had to press BACK and find it again in
              // the list, which is the wrong direction at the only moment
              // they are sure.
              '<button type="button" class="blvd-btn blvd-btn--primary blvd-compare__cta" data-i="' + i + '">' +
              'Choose ' + escapeHtml(d.name) + '</button>' +
              '</div>';
          });
          html += '</div>';
          body.innerHTML = html;
          body.querySelectorAll('.blvd-compare__cta').forEach(function (btn) {
            btn.addEventListener('click', function () {
              // Same route the list uses, so the membership agreement and
              // everything after it behave identically either way.
              chooseItem(state.bucket.items[Number(btn.getAttribute('data-i'))], showMembershipComparison);
            });
          });
        }).catch(function (err) { renderError(err.message, showMembershipComparison); });
      }

      function showItemDetail() {
        setBack(state.itemBackTo || menuScreen());
        renderLoading();
        api('item-detail', { cartId: state.cartId, itemId: state.item.id })
          .then(function (data) {
            state.itemDetail = data.detail;
            renderItemDetail();
          })
          .catch(function (err) { renderError(err.message, showItemDetail); });
      }

      function renderItemDetail() {
        trackBookingStep('item_detail');
        var d = state.itemDetail;
        var priceLabel = d.listPriceRange.variable
          ? formatPrice(d.listPriceRange.min) + '–' + formatPrice(d.listPriceRange.max)
          : formatPrice(d.listPriceRange.min);
        var comparePrefix = d.compareAt ? '<s>' + escapeHtml(formatPrice(d.compareAt)) + '</s> ' : '';
        var html = '<h2 class="blvd-title">' + escapeHtml(d.name) + '</h2>' +
          '<span class="blvd-price-badge">' + comparePrefix + escapeHtml(priceLabel) + '</span>' +
          (d.description ? '<p class="blvd-note" style="white-space:pre-line;margin-top:4px;margin-bottom:20px">' + escapeHtml(d.description) + '</p>' : '');
        if (d.membershipAgreementRequired) {
          // The gate on a recurring charge, and it was a 13px box in a 16px
          // row -- the smallest tap target in the widget sitting on the most
          // expensive decision in it. The label wraps the box so the text
          // toggles it too; this just makes the whole row big enough to hit.
          html += '<label class="blvd-agree-row">' +
            '<input type="checkbox" id="blvd-agree">' +
            '<span>I agree to the membership terms above.</span></label>' +
            '<p class="blvd-promo__result" id="blvd-agree-msg" style="margin-bottom:14px"></p>';
        }
        // Sticky: these descriptions run ~180 words (service copy plus
        // policies, insurance and the studio phone list), which on a phone
        // pushes Continue below the fold — measured 2026-09-04 at 92px below
        // on a 390x844 screen and 236px below on a 390x700 one. Pinning it
        // keeps the primary action reachable without cutting the copy.
        html += '<div class="blvd-sticky-cta"><button type="button" class="blvd-btn blvd-btn--primary">Continue</button></div>';
        body.innerHTML = html;
        var continueBtn = body.querySelector('.blvd-btn--primary');
        var agreeBox = body.querySelector('#blvd-agree');
        // Deliberately NOT disabled. A dimmed button that does nothing when
        // pressed reads as a broken widget -- the customer gets no reason and
        // nothing to act on, and memberships are the most expensive thing
        // here to abandon. It stays live and says what is missing.
        if (agreeBox) {
          agreeBox.addEventListener('change', function () {
            if (agreeBox.checked) document.getElementById('blvd-agree-msg').textContent = '';
          });
        }
        continueBtn.addEventListener('click', function () {
          if (agreeBox && !agreeBox.checked) {
            var am = document.getElementById('blvd-agree-msg');
            am.className = 'blvd-promo__result blvd-promo__result--error';
            am.textContent = 'Please agree to the membership terms to continue.';
            agreeBox.focus();
            return;
          }
          if (state.item.kind === 'bookable') {
            // A real appointment, same as everything else in Book a
            // Session — no payment collected here.
            state.service = state.item;
            showSessionScreen();
            return;
          }
          state.priceCents = d.listPriceRange.min;
          renderLoading();
          api('select-purchasable', { cartId: state.cartId, itemId: state.item.id })
            .then(function () {
              goCheckout(showItemDetail, false);
            })
            .catch(function (err) { renderError(err.message, renderItemDetail); });
        });
      }

      // --- Screen: choose a professional (bookable items only) — the
      // "pick someone and see their availability" path. "Any available"
      // skips straight to the normal unfiltered time picker; a specific
      // person filters it to just their own openings. Items with no staff
      // data at all (shouldn't happen, but Boulevard's own field is
      // optional) skip this screen entirely rather than show an empty list.
      function staffOptionRowHtml(staff) {
        var avatar = staff.avatar
          ? '<img src="' + escapeHtml(staff.avatar) + '" alt="">'
          : escapeHtml((staff.firstName || '?').charAt(0));
        var bio = staff.bio ? '<span class="blvd-option__sub">' + escapeHtml(staff.bio) + '</span>' : '';
        return '<span class="blvd-option__icon blvd-option__icon--staff">' + avatar + '</span>' +
          '<span class="blvd-option__body"><span class="blvd-option__name">' + escapeHtml(staff.displayName) + '</span>' + bio + '</span>';
      }


      // --- Screen: who's free at an already-picked time — the "pick a time
      // first" path's counterpart, shown only when no professional was
      // chosen upfront (a pre-chosen one already filtered the time list, so
      // there's nothing left to narrow).
      function showStaffForTime() {
        renderLoading();
        api('staff-for-time', {
          cartId: state.cartId, locationId: state.location.id,
          itemId: state.service.id, bookableTimeId: state.time.id,
        })
          .then(function (data) {
            // Boulevard reports nothing to disambiguate here (confirmed:
            // this business's current calendar never returns anyone for
            // this query, even though picking a professional first does
            // return real, filterable results — reported to Spencer,
            // not yet root-caused). Book straight through rather than show
            // a screen with a single dead-end "Any" option.
            if (!data.staffVariants || !data.staffVariants.length) {
              goCheckout(showTimes, true);
              return;
            }
            renderStaffForTime(data.staffVariants);
          })
          .catch(function (err) { renderError(err.message, showStaffForTime); });
      }

      function renderStaffForTime(staffVariants) {
        setBack(showTimes);
        var timeLabel = new Date(state.time.startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: state.location.tz });
        var html = '<h2 class="blvd-title">Choose a Professional</h2>' +
          '<p class="blvd-subtitle">Available at ' + timeLabel + '</p><ul class="blvd-list">';
        html += '<li><button type="button" class="blvd-option blvd-option--bucket" data-i="any">' +
          '<span class="blvd-option__name">Any available professional</span></button></li>';
        staffVariants.forEach(function (v, i) {
          html += '<li><button type="button" class="blvd-option blvd-option--bucket" data-i="' + i + '">' +
            staffOptionRowHtml(v.staff) + '</button></li>';
        });
        html += '</ul>';
        body.innerHTML = html;
        body.querySelectorAll('.blvd-option').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var i = btn.getAttribute('data-i');
            if (i === 'any') {
              goCheckout(showTimes, true);
              return;
            }
            var chosen = staffVariants[Number(i)];
            // Boulevard only accepts the staff choice at add-time, not
            // reserve-time — re-add the item now that we know who.
            renderLoading();
            api('select-service', { cartId: state.cartId, serviceId: state.service.id, itemStaffVariantId: chosen.id })
              .then(function () {
                state.selectedStaffVariantId = chosen.id;
                goCheckout(showTimes, true);
              })
              .catch(function (err) { renderError(err.message, function () { renderStaffForTime(staffVariants); }); });
          });
        });
      }

      // --- Gift Cards: amount, then who it's for ---
      function showGiftCardAmount() {
        setBack(menuScreen());
        renderLoading();
        api('item-detail', { cartId: state.cartId, itemId: 'GIFT_CARD' })
          .then(function (data) {
            renderGiftCardAmount(data.detail);
          })
          .catch(function (err) { renderError(err.message, showGiftCardAmount); });
      }

      function renderGiftCardAmount(d) {
        var html = '<h2 class="blvd-title">Gift Card</h2><p class="blvd-subtitle">Choose an amount</p><div class="blvd-slots">';
        d.pricePresets.forEach(function (cents, i) {
          html += '<button type="button" class="blvd-slot" data-cents="' + cents + '">' + formatPrice(cents) + '</button>';
        });
        html += '</div>';
        if (d.allowCustomAmounts) {
          html += '<label style="display:block;margin-top:16px">Custom amount (' + formatPrice(d.giftCardMin) + '–' + formatPrice(d.giftCardMax) + ')' +
            '<input type="number" class="blvd-date-input" id="blvd-gift-custom" min="' + (d.giftCardMin / 100) + '" max="' + (d.giftCardMax / 100) + '" step="1" style="margin-top:6px;width:100%"></label>' +
            '<button type="button" class="blvd-btn blvd-btn--primary" id="blvd-gift-custom-go">Continue</button>';
        }
        body.innerHTML = html;
        body.querySelectorAll('.blvd-slot').forEach(function (btn) {
          btn.addEventListener('click', function () { selectGiftCardAmount(Number(btn.getAttribute('data-cents'))); });
        });
        var customGo = body.querySelector('#blvd-gift-custom-go');
        if (customGo) {
          customGo.addEventListener('click', function () {
            var dollars = Number(body.querySelector('#blvd-gift-custom').value);
            if (!dollars || dollars < d.giftCardMin / 100 || dollars > d.giftCardMax / 100) return;
            selectGiftCardAmount(Math.round(dollars * 100));
          });
        }
      }

      function selectGiftCardAmount(cents) {
        state.priceCents = cents;
        setBack(showGiftCardAmount);
        renderLoading();
        api('select-gift-card', { cartId: state.cartId, itemPrice: cents })
          .then(function (data) {
            state.giftCard = { itemId: data.itemId };
            // Purchaser info first — Boulevard requires a sender_name on
            // the fulfillment step below, so we need state.contact filled
            // in before asking who the card is for, not after.
            showContact({ backTo: showGiftCardAmount, next: showGiftCardRecipient, title: 'Your info' });
          })
          .catch(function (err) { renderError(err.message, function () { selectGiftCardAmount(cents); }); });
      }

      function showGiftCardRecipient() {
        setBack(function () { showContact(state.lastContactOpts); });
        var g = state.giftCard;
        body.innerHTML =
          '<h2 class="blvd-title">Who’s it for?</h2>' +
          '<form class="blvd-form">' +
          '<label class="blvd-check"><input type="checkbox" id="blvd-gift-self" ' + (g.forSelf ? 'checked' : '') + '> This is for me</label>' +
          '<label>Recipient name<input type="text" name="recipientName" required value="' + escapeHtml(g.recipientName || '') + '"></label>' +
          '<label>Recipient email<input type="email" name="recipientEmail" required value="' + escapeHtml(g.recipientEmail || '') + '"></label>' +
          '<label>Message (optional)<input type="text" name="messageFromSender" value="' + escapeHtml(g.messageFromSender || '') + '"></label>' +
          '<button type="submit" class="blvd-btn blvd-btn--primary">Continue</button>' +
          '</form>';
        var form = body.querySelector('.blvd-form');
        body.querySelector('#blvd-gift-self').addEventListener('change', function (e) {
          if (e.target.checked && state.contact.firstName) {
            form.recipientName.value = state.contact.firstName + ' ' + state.contact.lastName;
            form.recipientEmail.value = state.contact.email || '';
          }
        });
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          state.giftCard = {
            itemId: g.itemId,
            forSelf: body.querySelector('#blvd-gift-self').checked,
            recipientName: form.recipientName.value.trim(),
            recipientEmail: form.recipientEmail.value.trim(),
            messageFromSender: form.messageFromSender.value.trim(),
          };
          renderLoading();
          api('gift-card-fulfillment', {
            cartId: state.cartId, itemId: g.itemId,
            recipientEmail: state.giftCard.recipientEmail,
            recipientName: state.giftCard.recipientName,
            senderName: (state.contact.firstName || '') + ' ' + (state.contact.lastName || ''),
            messageFromSender: state.giftCard.messageFromSender,
          }).then(function () { showCardEntry(showGiftCardRecipient); })
            .catch(function (err) { renderError(err.message, showGiftCardRecipient); });
        });
      }

      // --- Card entry, shared by every paid flow (packages, memberships,
      // gift cards). Tokenizes directly against Boulevard's own PCI-scoped
      // vault from the browser — this code never sends a raw card number
      // to our server, only the opaque token that comes back. See
      // developers.joinblvd.com's Tokenization API: sending card data
      // server-side yourself means taking on full PCI DSS Level 1
      // obligations, which this deliberately avoids.
      var TOKENIZE_URL = 'https://pci.boulevard.app/cards/tokenize';
      // Permanent production vault as of 2026-08-28. All four payment flows
      // (gift card, session, package, membership) were proven against real
      // charges before this switch — see the Sandbox CRO log for order
      // numbers/refund confirmations.

      // isSessionBooking: Boulevard rejects checkout on EVERY item — including
      // a $0-today, pay-in-studio session — without a card attached to the
      // cart (confirmed live 2026-08-28: CART_MISSING_ITEM_PAYMENT_METHOD on
      // a real production booking attempt, on two different session items).
      // The item's own description already said this ("your card will NOT
      // be charged until after your treatment") — the widget just never
      // actually collected one. Same card form either way; only the label,
      // price display, and what happens after the card is attached differ.
      // --- Screen: use a card already on file -----------------------------
      // Only reachable for a logged-in client whose Boulevard record has at
      // least one unexpired card. Everything here degrades to normal card
      // entry: an expired session, a rejected selection, or any error just
      // sends them to the form they'd have seen anyway.
      // opts.onAttached means the caller wants the card ON THE CART and
      // nothing more -- the waitlist. showCardEntry already refuses to route
      // such a caller here, so this is the second line of defence: if anything
      // ever reaches this screen carrying that intent, it must still not book.
      function showSavedCardChoice(backTo, isSessionBooking, opts) {
        opts = opts || {};
        trackBookingStep('payment_saved_card');
        setBack(backTo || function () { showContact(state.lastContactOpts); });
        var payLabel = isSessionBooking ? 'Reserve your spot' : ('Pay ' + (state.priceCents ? formatPrice(state.priceCents) : ''));
        var cardLines = state.savedCards.map(function (c, i) {
          return '<li><button type="button" class="blvd-option blvd-option--card" data-card-id="' + escapeHtml(c.id) + '">' +
            '<span class="blvd-option__body">' +
            '<span class="blvd-option__name">' + escapeHtml(c.brand || 'Card') + ' ending ' + escapeHtml(c.last4 || '••••') + '</span>' +
            '</span></button></li>';
        }).join('');
        body.innerHTML =
          '<h2 class="blvd-title">' +
            escapeHtml(opts.title || (isSessionBooking ? 'Confirm your card' : 'Payment')) + '</h2>' +
          '<p class="blvd-subtitle">Use a card already on your account.</p>' +
          '<ul class="blvd-list">' + cardLines + '</ul>' +
          '<p class="blvd-note">' +
          // An attach-only caller is not holding an appointment, so the
          // booking reassurance would be a lie. Use whatever it passed.
          (opts.note ? escapeHtml(opts.note)
            : (isSessionBooking ? 'Your card is held to confirm the appointment — you’re charged at the studio, not now.' : '')) +
          '</p>' +
          '<p class="blvd-promo__result" id="blvd-savedcard-err"></p>' +
          '<button type="button" class="blvd-btn blvd-btn--secondary" id="blvd-use-new-card">Use a different card</button>';

        document.getElementById('blvd-use-new-card').addEventListener('click', function () {
          state.forceManualCard = true;
          showCardEntry(backTo, isSessionBooking, opts);
        });

        body.querySelectorAll('.blvd-option--card').forEach(function (btn) {
          btn.addEventListener('click', function () {
            if (state.paymentSubmitting) return;
            state.paymentSubmitting = true;
            var errEl = document.getElementById('blvd-savedcard-err');
            errEl.className = 'blvd-promo__result';
            errEl.textContent = 'Confirming…';
            body.querySelectorAll('button').forEach(function (b) { b.disabled = true; });

            // use-saved-card, not select-saved-card: the card now lives on
            // the OWNED cart, so no client session token is involved.
            api('use-saved-card', {
              cartId: state.cartId,
              paymentMethodId: btn.getAttribute('data-card-id'),
            })
              .then(function () {
                // Before anything that books. A caller who only wanted the
                // card attached stops here.
                if (opts.onAttached) return opts.onAttached();
                if (isSessionBooking) {
                  return api('confirm', {
                    cartId: state.cartId,
                    bookableTimeId: state.time.id,
                    email: state.contact.email,
                    firstName: state.contact.firstName,
                    lastName: state.contact.lastName,
                    phoneNumber: state.contact.phoneNumber,
                    // Sent from every confirm path, so which arm gets tagged
                    // is decided in ONE place — the backend's flowArm check —
                    // rather than by which of these three functions ran.
                    flowArm: FLOW_ARM,
                  });
                }
                return api('checkout', {
                  cartId: state.cartId,
                  email: state.contact.email,
                  firstName: state.contact.firstName,
                  lastName: state.contact.lastName,
                  phoneNumber: state.contact.phoneNumber,
                });
              })
              .then(function () {
                state.paymentSubmitting = false;
                if (opts.onAttached) return;   // onAttached owns what happens next
                if (isSessionBooking) {
                  trackBookingComplete();
                  showSuccess('You’re booked!', 'A confirmation will be sent to ' + state.contact.email + '.');
                  return;
                }
                trackPurchaseComplete();
                showSuccess('All set!', 'A confirmation will be sent to ' + state.contact.email + '.');
              })
              .catch(function (err) {
                state.paymentSubmitting = false;
                // Any failure here — expired session, declined selection —
                // drops through to normal card entry rather than dead-ending
                // someone who is one step from booking.
                state.forceManualCard = true;
                showCardEntry(backTo, isSessionBooking, opts);
              });
          });
        });
      }

      // `opts.onAttached` stops after the card is on the cart instead of
      // booking -- used by the waitlist, which needs a payment method but has
      // no time to reserve.
      function showCardEntry(backTo, isSessionBooking, opts) {
        opts = opts || {};
        trackBookingStep('payment_card_entry');
        // A logged-in client whose card Boulevard already holds shouldn't be
        // retyping it. If login returned saved cards, offer those first and
        // keep manual entry as the escape hatch.
        // The !opts.onAttached exclusion that used to sit here existed because
        // the saved-card screen always booked. It no longer does -- it returns
        // through opts.onAttached before anything that reserves a time -- and
        // keeping the exclusion had a cost of its own: a logged-in customer
        // with a card on file was made to type one in to join a waitlist.
        if (state.savedCards && state.savedCards.length && !state.forceManualCard) {
          return showSavedCardChoice(backTo, isSessionBooking, opts);
        }
        setBack(backTo || function () { showContact(state.lastContactOpts); });
        var priceLine = state.priceCents ? '<div><span class="blvd-price-badge">' + formatPrice(state.priceCents) + '</span></div>' : '';
        var payLabel = isSessionBooking ? 'Reserve your spot' : ('Pay ' + (state.priceCents ? formatPrice(state.priceCents) : ''));
        var trustLine = opts.note
          ? opts.note
          : (isSessionBooking
            ? 'Your card is held to confirm the appointment — you’re charged at the studio, not now.'
            : 'Payment details go directly to Boulevard’s secure vault — never stored on our servers.');
        var lockIcon = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>';
        body.innerHTML =
          '<h2 class="blvd-title">' + escapeHtml(opts.title || (isSessionBooking ? 'Confirm your card' : 'Payment')) + '</h2>' +
          (opts.note ? '<p class="blvd-subtitle">' + escapeHtml(opts.note) + '</p>' : '') + priceLine +
          '<form class="blvd-form">' +
          // Above the fields, not under the button: the whole point is to be
          // seen BEFORE someone starts typing a card they don't need to type.
          savedCardPromptHtml() +
          '<div class="blvd-payment-box">' +
          '<label>Name on card<input type="text" name="name" autocomplete="cc-name" required placeholder="Jane Doe"></label>' +
          '<label>Card number<input type="text" name="number" inputmode="numeric" autocomplete="cc-number" placeholder="1234 1234 1234 1234" required></label>' +
          '<div class="blvd-form-row">' +
          '<label>Expiry<input type="text" name="exp" autocomplete="cc-exp" placeholder="MM/YY" inputmode="numeric" required></label>' +
          '<label>CVV<input type="text" name="cvv" inputmode="numeric" autocomplete="cc-csc" placeholder="123" required></label>' +
          '</div>' +
          '<label>Billing zip<input type="text" name="zip" autocomplete="postal-code" inputmode="numeric" placeholder="10012" required></label>' +
          '</div>' +
          '<p class="blvd-form-trust">' + lockIcon + ' ' + trustLine + '</p>' +
          '<button type="submit" class="blvd-btn blvd-btn--primary">' + escapeHtml(payLabel) + '</button>' +
          '</form>';
        // opts, NOT dropped. This callback re-renders after someone logs in
        // from the prompt above, and without opts the re-render loses
        // onAttached -- which both removes the guard that keeps a waitlist
        // journey off the saved-card screen AND makes that screen treat it as
        // a booking. Measured 2026-09-14: join the waitlist, log in at this
        // step, pick the card on file, and the widget called `confirm`. It
        // only failed to book because no time had been picked, so state.time
        // was undefined and it threw first.
        wireSavedCardPrompt(function () { showCardEntry(backTo, isSessionBooking, opts); });
        normalizeExpiryField(body.querySelector('[name="exp"]'));
        body.querySelector('.blvd-form').addEventListener('submit', function (e) {
          e.preventDefault();
          // A disabled submit button only blocks another *click* on that
          // button — it does nothing against a second real submit event
          // (Enter fired twice, a double-tap dispatching two events before
          // either handler finishes, etc). Confirmed live: three rapid
          // submits produced three separate tokenize calls and three
          // separate charge attempts. This flag is the actual guard —
          // checked before anything else runs, cleared only on failure so
          // a genuine retry after a real error still works.
          if (state.paymentSubmitting) return;
          var form = e.target;
          var btn = form.querySelector('.blvd-btn');
          // parseExpiry, not split('/') -- the same parser the merged checkout
          // validates with, so this screen accepts the four-digit year that
          // autocomplete="cc-exp" makes browsers autofill. The old split also
          // let "09/2030" through as TWO parts and then sent
          // exp_year = 2000 + 2030 = 4030 to the tokenizer.
          var exp = parseExpiry(form.exp.value);
          if (!exp || !(exp.mm >= 1 && exp.mm <= 12)) { renderCardError('Enter expiry as MM/YY.'); return; }
          state.paymentSubmitting = true;
          btn.disabled = true;
          btn.textContent = 'Processing…';
          fetch(TOKENIZE_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              card: {
                name: form.name.value.trim(),
                number: form.number.value.replace(/\s+/g, ''),
                cvv: form.cvv.value.trim(),
                exp_month: exp.mm,
                exp_year: 2000 + exp.yy,
                address_postal_code: form.zip.value.trim(),
              },
            }),
          }).then(function (res) { return res.json(); })
            .then(function (tokenData) {
              if (!tokenData.success) throw new Error('Card could not be processed. Please check your details.');
              return api('add-payment-method', { cartId: state.cartId, token: tokenData.token })
                // Boulevard's own error text here is a raw API message, not
                // something to show a customer — CART_PAYMENT_METHOD_FAILED
                // covers everything from a typo to an actual decline, none
                // of which this form can distinguish, so one plain retry
                // prompt covers all of them honestly.
                .catch(function () { throw new Error('That card couldn’t be charged. Please check your details and try again.'); });
            })
            .then(function () {
              // Caller wanted the card ATTACHED, not a booking -- the waitlist
              // needs a payment method but has no time to reserve.
              if (opts.onAttached) return opts.onAttached();
              // A session booking still goes through the same reserve+
              // checkout action as before — the only change is the cart
              // now has a card attached first, which is what Boulevard
              // actually requires. Everything else (no charge, estimated-
              // value tracking) stays exactly as designed.
              if (isSessionBooking) {
                return api('confirm', {
                  cartId: state.cartId,
                  bookableTimeId: state.time.id,
                  email: state.contact.email,
                  firstName: state.contact.firstName,
                  lastName: state.contact.lastName,
                  phoneNumber: state.contact.phoneNumber,
                  flowArm: FLOW_ARM,
                });
              }
              return api('checkout', {
                cartId: state.cartId,
                email: state.contact.email,
                firstName: state.contact.firstName,
                lastName: state.contact.lastName,
                phoneNumber: state.contact.phoneNumber,
              });
            })
            .then(function () {
              if (isSessionBooking) {
                  trackBookingComplete();
                showSuccess('You’re booked!', 'A confirmation will be sent to ' + state.contact.email + '.');
                return;
              }
              var itemName = state.item ? state.item.name : 'Gift Card';
              // Real money moved here — the standard GA4 "purchase" event,
              // not the estimated-value one used for free bookings.
              trackPurchaseComplete(itemName);
              showSuccess('You’re all set!', itemName + ' — a receipt will be sent to ' + state.contact.email + '.');
            })
            .catch(function (err) { renderCardError(err.message, isSessionBooking, err.code); });
        });
      }

      function renderCardError(message, isSessionBooking, code) {
        state.paymentSubmitting = false;
        var form = body.querySelector('.blvd-form');
        var existing = body.querySelector('.blvd-card-error');
        if (existing) existing.remove();
        var p = document.createElement('p');
        p.className = 'blvd-card-error';
        p.style.cssText = 'color:#89392F;font-size:13px;margin:-4px 0 0';
        p.textContent = message;
        form.insertBefore(p, form.firstChild);
        var btn = form.querySelector('.blvd-btn');
        if (code === 'CART_TIME_NOT_AVAILABLE') {
          // That exact slot was taken by someone else between selecting it
          // and confirming — retrying just resubmits the same dead time and
          // fails identically forever, so send them to fresh availability
          // instead of leaving them stuck on this screen.
          btn.style.display = 'none';
          var pickBtn = document.createElement('button');
          pickBtn.type = 'button';
          pickBtn.className = 'blvd-btn blvd-btn--primary';
          pickBtn.textContent = 'Pick a different time';
          pickBtn.addEventListener('click', function () {
            state.time = null;
            showTimes();
          });
          form.insertBefore(pickBtn, p.nextSibling);
          return;
        }
        btn.disabled = false;
        btn.textContent = isSessionBooking ? 'Reserve your spot' : ('Pay ' + (state.priceCents ? formatPrice(state.priceCents) : ''));
      }

      // ONE place for the booking-complete event, called from all three
      // completion paths (saved card, manual card, and the combined
      // confirm-screen submit).
      //
      // It used to be three copies of the same block, each wrapped in
      // `if (state.service.priceRange)` -- so a booking whose price we could
      // not read completed happily and was never counted. GA4 then showed
      // fewer bookings than Boulevard and there was nothing to see in the
      // code, because the event simply never fired. An untracked booking is
      // worse than one with a missing value, so this always fires and
      // attaches the value only when it is actually known.
      function trackBookingComplete() {
        var svc = state.service || {};
        var pr = svc.priceRange;
        var payload = {
          currency: 'USD',
          items: [{
            item_id: svc.id,
            item_name: svc.name,
            item_category: 'booking',
            item_variant: state.location && state.location.name,
            quantity: 1,
          }],
        };
        if (pr && typeof pr.min === 'number') {
          payload.value = pr.min / 100;
          payload.items[0].price = pr.min / 100;
        }
        trackGA4Event('book_appointment', payload);
      }

      // Same reasoning for a paid item: never let a bad price stop the event.
      // state.priceCents undefined made `value` NaN, which GA4 drops -- so
      // the sale vanished from reporting even though the money moved.
      function trackPurchaseComplete(itemName) {
        var priced = typeof state.priceCents === 'number' && isFinite(state.priceCents);
        var item = {
          item_id: (state.item && state.item.id) || 'GIFT_CARD',
          item_name: itemName || (state.item && state.item.name) || 'Gift Card',
          item_category: state.bucket ? state.bucket.key : 'gift-cards',
          item_variant: state.location && state.location.name,
          quantity: 1,
        };
        if (priced) item.price = state.priceCents / 100;
        var payload = { transaction_id: state.cartId, currency: 'USD', items: [item] };
        if (priced) payload.value = state.priceCents / 100;
        trackGA4Event('purchase', payload);
      }

      function showSuccess(title, message) {
        trackBookingStep('success');
        // The cart is SPENT. Boulevard refuses any further mutation on it
        // ("completed_at: is already complete"), so anything still on screen
        // offering to change the booking is offering something that cannot
        // work -- and on 2026-09-20 a customer used the practitioner picker
        // after booking, got a silent revert, and paged the channel five
        // times. Marked here, at the one place every success path passes.
        state.cartCompleted = true;
        setBack(null);
        var checkIcon = '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="4 12 9 17 20 6"/></svg>';
        body.innerHTML =
          '<div class="blvd-success">' +
          '<div class="blvd-success__check">' + checkIcon + '</div>' +
          '<h2 class="blvd-title">' + escapeHtml(title) + '</h2>' +
          '<p>' + escapeHtml(message) + '</p>' +
          '<button type="button" class="blvd-btn blvd-btn--primary blvd-btn--done">Done</button>' +
          '</div>';
        body.querySelector('.blvd-btn--done').addEventListener('click', closeModal);
      }

      // Flatiron — the default studio for a bucket-targeted deep link that
      // doesn't name one of its own (an email/ad CTA, or an on-page link
      // that only cares about the bucket). Landing on a real studio instead
      // of the picker gets the visitor to the actual content one screen
      // sooner; the header pill (shown on every screen once a studio is
      // set) is the visible way to switch if Flatiron isn't the right one.
      var DEFAULT_LOCATION_ID = '18a1c756-07ac-4f80-88f5-95cd15ff023b';


      // ================= Flow rebuild: screen 03 =================
      // One screen for what used to be three: the session's own detail, the
      // professional, and the add-ons. The ordering constraint that shapes
      // this: Boulevard only reports an item's add-ons once the item is IN
      // the cart (CartAvailableBookableItem has no addons field), and it only
      // accepts the staff choice at add-time. So the service is added on load
      // with nobody chosen, which yields the add-on list, and a specific
      // professional is then set in place via update-staff — NOT by adding
      // the service a second time, which is what the old swap did.
      function showSessionScreen() {
        setBack(state.itemBackTo || menuScreen());
        renderLoading();
        Promise.all([
          api('item-detail', { cartId: state.cartId, itemId: state.item.id }),
          api('select-service', {
            cartId: state.cartId, serviceId: state.item.id,
            replaceItemId: state.selectedItemId || undefined,
          }),
        ])
          .then(function (res) {
            state.itemDetail = res[0].detail;
            state.service = state.item;
            state.availableAddons = res[1].addons || [];
            state.selectedItemId = res[1].selectedItemId || null;
            state.selectedAddons = [];
            state.sessionAddonPicks = {};
            state.selectedStaffVariantId = null;
            // Same fifth deep-link dimension as the old picker: a matching
            // professional is pre-selected rather than skipping a screen,
            // since here there is no screen left to skip.
            var deepStaffId = state.deepLinkStaffId;
            state.deepLinkStaffId = null;
            if (deepStaffId) {
              var variants = state.itemDetail.staffVariants || [];
              var match = variants.filter(function (v) { return v.staff && v.staff.id === deepStaffId; })[0];
              if (match) state.selectedStaffVariantId = match.id;
            }
            // NOW, not at the menu. Boulevard exposes payment methods per
            // SELECTED ITEM (cart.selectedItems[].availablePaymentMethods),
            // so an empty cart has none by definition -- asking right after
            // the menu, when the cart is deliberately empty, always answered
            // "no saved card" no matter who was logged in. This is the first
            // point a service is actually on the cart, and it runs well
            // before checkout needs the answer.
            refreshSavedCards();
            renderSessionScreen();
          })
          .catch(function (err) {
            if (recoverIfNotBookable(err)) return;
            renderError(err.message, showSessionScreen);
          });
      }

      function currentStaffLabel() {
        var variants = (state.itemDetail && state.itemDetail.staffVariants) || [];
        var picked = variants.filter(function (v) { return v.id === state.selectedStaffVariantId; })[0];
        return picked && picked.staff ? picked.staff.displayName : 'Any available professional';
      }

      // Short form for prose. displayName carries full credentials ("Dora
      // Fiotodimitrakis LAC, LMT, CHHC"), which reads badly mid-sentence.
      // Null when nobody specific is chosen, which is what the callers below
      // branch on.
      function selectedStaffFirstName() {
        if (!state.selectedStaffVariantId) return null;
        var variants = (state.itemDetail && state.itemDetail.staffVariants) || [];
        var v = variants.filter(function (x) { return x.id === state.selectedStaffVariantId; })[0];
        if (!v || !v.staff) return null;
        return v.staff.firstName || v.staff.displayName || null;
      }

      // The practitioner picker — a row showing the current choice that opens
      // the list in place. Extracted from the session screen so the times
      // screen can carry the same control: choosing a practitioner IS a filter
      // on availability, so it belongs beside the availability it filters
      // rather than a screen earlier, where the customer cannot see what it
      // costs them.
      function staffPickerHtml() {
        var variants = (state.itemDetail && state.itemDetail.staffVariants) || [];
        if (!variants.length) return '';
        return '<div class="blvd-picker">' +
          '<button type="button" class="blvd-picker__row" id="blvd-staff-row" aria-expanded="false">' +
          '<span class="blvd-picker__label">Practitioner</span>' +
          '<span class="blvd-picker__value" id="blvd-staff-value">' + escapeHtml(currentStaffLabel()) + '</span>' +
          '<span class="blvd-picker__action">Change</span>' +
          '</button>' +
          '<ul class="blvd-list blvd-picker__list" id="blvd-staff-list" hidden>' +
          '<li><button type="button" class="blvd-option blvd-option--bucket" data-i="any">' +
          '<span class="blvd-option__name">Any available professional</span></button></li>' +
          variants.map(function (v, i) {
            return '<li><button type="button" class="blvd-option blvd-option--bucket" data-i="' + i + '">' +
              staffOptionRowHtml(v.staff) + '</button></li>';
          }).join('') +
          '</ul></div>';
      }

      // `onChanged` runs only after the in-place cart edit actually lands. On
      // the times screen that redraws the whole grid, because a different
      // practitioner has different availability; a failure reverts the label
      // rather than stranding a choice that never stuck.
      //
      // Requires state.selectedItemId, which only the new flow's session path
      // records — hence the times screen shows this picker for that arm only.
      function wireStaffPicker(onChanged) {
        var staffRow = document.getElementById('blvd-staff-row');
        if (!staffRow) return;
        var variants = (state.itemDetail && state.itemDetail.staffVariants) || [];
        var staffList = document.getElementById('blvd-staff-list');
        staffRow.addEventListener('click', function () {
          staffList.hidden = !staffList.hidden;
          staffRow.setAttribute('aria-expanded', String(!staffList.hidden));
        });
        staffList.querySelectorAll('.blvd-option').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var i = btn.getAttribute('data-i');
            var variantId = i === 'any' ? null : variants[Number(i)].id;
            staffList.hidden = true;
            staffRow.setAttribute('aria-expanded', 'false');
            if (state.cartCompleted) {
              // Already booked: say so rather than silently reverting, which
              // is what made this look like the change had been accepted.
              var doneEl = document.getElementById('blvd-staff-value');
              if (doneEl) doneEl.textContent = currentStaffLabel() + ' — already booked';
              return;
            }
            var previous = state.selectedStaffVariantId;
            state.selectedStaffVariantId = variantId;
            var vEl = document.getElementById('blvd-staff-value');
            if (vEl) vEl.textContent = currentStaffLabel();
            api('update-staff', {
              cartId: state.cartId,
              selectedItemId: state.selectedItemId,
              itemStaffVariantId: variantId || undefined,
            })
              .then(function () { if (onChanged) onChanged(); })
              .catch(function () {
                state.selectedStaffVariantId = previous;
                var v2 = document.getElementById('blvd-staff-value');
                if (v2) v2.textContent = currentStaffLabel();
              });
          });
        });
      }

      // The service copy runs ~180 words (welcome text, then insurance,
      // policies and the studio phone list). Printed in full it buries the
      // professional and the add-ons below it, so only the first paragraph
      // is shown and the rest goes behind a native disclosure.
      // Only the opening paragraph shows inline; everything after it collapses
      // behind the policies toggle. `promote` pulls one later paragraph up
      // alongside it -- the Tata Harper copy deliberately leads with the
      // "Welcome to WTHN!" greeting, so without this its partnership
      // paragraph is written to Boulevard but never seen on the screen where
      // the customer actually books.
      function sessionDescriptionHtml(description, promote) {
        if (!description) return '';
        var paras = description.split(/\n\s*\n/).filter(function (p) { return p.trim(); });
        var lead = paras.slice(0, 1);
        var rest = paras.slice(1);
        if (promote) {
          for (var i = 0; i < rest.length; i++) {
            if (promote.test(rest[i])) { lead.push(rest.splice(i, 1)[0]); break; }
          }
        }
        var head = lead.map(function (p) {
          return '<p class="blvd-note" style="white-space:pre-line;margin-top:4px">' + escapeHtml(p) + '</p>';
        }).join('');
        if (!rest.length) return head;
        return head +
          '<details class="blvd-details"><summary>What to expect, policies and insurance</summary>' +
          '<p class="blvd-note" style="white-space:pre-line">' + escapeHtml(rest.join('\n\n')) + '</p>' +
          '</details>';
      }

      function renderSessionScreen() {
        trackBookingStep('session');
        // The times screen sets its back target to THIS function, so coming
        // back has to restore this screen's own. Without it the arrow pointed
        // at the screen you were already on -- a dead end that shipped live.
        setBack(state.itemBackTo || menuScreen());
        var d = state.itemDetail;
        var priceLabel = d.listPriceRange.variable
          ? formatPrice(d.listPriceRange.min) + '–' + formatPrice(d.listPriceRange.max)
          : formatPrice(d.listPriceRange.min);
        var comparePrefix = d.compareAt ? '<s>' + escapeHtml(formatPrice(d.compareAt)) + '</s> ' : '';
        var durMin = d.listDurationRange && d.listDurationRange.min;
        // Boulevard reports this in minutes already (60 / 45), not seconds.
        var durLabel = durMin ? ' · ' + durMin + ' min' : '';
        var variants = d.staffVariants || [];

        var tataLabel = TATA_ITEM_LABELS[d.name];
        var html = '<h2 class="blvd-title">' + escapeHtml(tataLabel || d.name) + '</h2>' +
          '<span class="blvd-price-badge">' + comparePrefix + escapeHtml(priceLabel) + escapeHtml(durLabel) + '</span>' +
          // Last screen before the time picker, so the run-length line is
          // repeated here rather than left behind on the tier picker.
          (tataLabel ? '<p class="blvd-note">' + escapeHtml(TATA_NOTE) + '</p>' : '') +
          sessionDescriptionHtml(d.description, tataLabel ? /NOW FEATURING TATA HARPER/ : null);

        // The practitioner picker used to sit here. It MOVED to the times
        // screen 2026-09-09: a practitioner choice filters availability, and
        // choosing one here meant meeting a thinner calendar a screen later
        // with nothing to explain why. Picking Dora at Flatiron dropped the
        // day from 32 slots to 8 — see staffPickerHtml / renderTimes.

        if (state.availableAddons.length) {
          html += '<h3 class="blvd-subhead">Add to your session</h3><ul class="blvd-list">';
          state.availableAddons.forEach(function (a, i) {
            var price = a.listPriceRange ? a.listPriceRange.min : 0;
            var on = !!state.sessionAddonPicks[i];
            html += '<li><button type="button" class="blvd-addon' + (on ? ' is-selected' : '') + '" data-i="' + i + '">' +
              '<span class="blvd-addon__icon">' + (on ? '✓' : '+') + '</span>' +
              '<span class="blvd-addon__body">' +
              '<span class="blvd-addon__name">' + escapeHtml(a.name) + '</span>' +
              (a.description ? '<span class="blvd-addon__desc">' + escapeHtml(a.description) + '</span>' : '') +
              '</span>' +
              '<span class="blvd-addon__price">+' + escapeHtml(formatPrice(price)) + '</span>' +
              '</button></li>';
          });
          html += '</ul>';
        }

        // Booking for two was REMOVED from this screen 2026-09-11. Boulevard’s
        // own widget never offered it to WTHN customers (its only options are
        // Individual Appointment / Memberships / Packages / Gift Card -- group
        // booking is a Boulevard feature WTHN has switched off), so no one has
        // ever been able to book a +1 online here. It was the widget’s most
        // fragile path -- four separate money or dead-end bugs in one day --
        // and it charged a second session to the booker for someone who got no
        // confirmation and could not cancel.
        //
        // The back end is intact and tested: add-guest and remove-guest in
        // netlify/functions/booking.js, plus booking.remove-guest.test.js and
        // the guest cases in booking.duplicate-purchasables.test.js. The
        // confirmation screen still renders an “Also booking” line off
        // state.hasGuest. Restoring this is a UI change on this screen alone;
        // do that if the front desk says customers ask to book two together.
        html += '<p class="blvd-note blvd-note--policy">A card is required to hold the appointment. You pay at the studio.</p>';
        html += '<div class="blvd-sticky-cta"><button type="button" class="blvd-btn blvd-btn--primary" id="blvd-session-continue">Continue</button></div>';
        body.innerHTML = html;


        // Add-ons stay local until Continue, exactly as the old screen did —
        // so toggling one off needs no cart edit at all.
        body.querySelectorAll('.blvd-addon').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var i = Number(btn.getAttribute('data-i'));
            state.sessionAddonPicks[i] = !state.sessionAddonPicks[i];
            btn.classList.toggle('is-selected', state.sessionAddonPicks[i]);
            btn.querySelector('.blvd-addon__icon').textContent = state.sessionAddonPicks[i] ? '✓' : '+';
            // Same events as the old flow's own add-on screen, so attach rate
            // stays comparable across the two arms.
            if (state.sessionAddonPicks[i]) trackAddon(state.availableAddons[i] && state.availableAddons[i].name);
          });
        });

        document.getElementById('blvd-session-continue').addEventListener('click', function () {
          var picked = Object.keys(state.sessionAddonPicks)
            .filter(function (i) { return state.sessionAddonPicks[i]; })
            .map(function (i) { return state.availableAddons[Number(i)]; });
          function proceed() {
            state.date = todayInTz(state.location.tz);
            showTimes();
          }
          trackBookingStep(picked.length ? 'addons_kept' : 'addons_skipped');
          if (!picked.length) { proceed(); return; }
          renderLoading();
          Promise.all(picked.map(function (a) {
            return api('select-service', { cartId: state.cartId, serviceId: a.id });
          }))
            .then(function () { state.selectedAddons = picked; proceed(); })
            .catch(function (err) { renderError(err.message, renderSessionScreen); });
        });
      }

      // ================= Flow rebuild: screen 05 =================
      // One checkout for what used to be three screens: your info, confirm,
      // and the card. Same API sequence as before — tokenize, attach the
      // card, then reserve+checkout — just gathered behind one submit.
      function goCheckout(backTo, isSessionBooking) {
        showCheckout(backTo, isSessionBooking);
      }

      // Reserve + checkout, then the matching GA4 event and success screen.
      // Deliberately its own copy rather than a refactor of the two existing
      // callers: the old path stays byte-identical while the flag is off.
      function completeCheckout(isSessionBooking) {
        var c = state.contact;
        var args = {
          cartId: state.cartId, email: c.email, firstName: c.firstName,
          lastName: c.lastName, phoneNumber: c.phoneNumber,
        };
        if (isSessionBooking) {
          args.bookableTimeId = state.time.id;
          // Recorded on the booking itself so real Boulevard revenue can be
          // attributed to a flow. GA4 knows the arm, but its booking counts
          // are its own events and ad blockers drop those unevenly; Boulevard
          // is the ground truth and carries no arm of its own. The backend
          // tags only 'new' — never a *_forced value, which is someone
          // testing rather than a random sample. Read-only here: FLOW_ARM is
          // not modified, so the A/B instrumentation is untouched.
          args.flowArm = FLOW_ARM;
        }
        return api(isSessionBooking ? 'confirm' : 'checkout', args).then(function (res) {
          if (isSessionBooking) {
                  trackBookingComplete();
            // Meta first-appointment signal. Fired AFTER the booking, never
            // awaited, wrapped so it cannot reach the customer: they are
            // already booked and nothing about analytics may change what
            // they see. See meta-capi-hook.js for the rest of the reasoning.
            try { sendMetaFirstAppointment(res); } catch (e) { /* never surface */ }
            try { sendKlaviyoBookingCompleted(res, c, 'Appointment', state.cartId); } catch (e) { /* never surface */ }
            showSuccess('You’re booked!', 'A confirmation will be sent to ' + c.email + '.');
            return;
          }
          var itemName = state.item ? state.item.name : 'Gift Card';
          // Packages, memberships and gift cards complete HERE, not through
          // the appointment path -- and until 2026-10-07 this branch fired no
          // completion at all. The recovery flow excludes anyone with zero
          // Booking Completed, so someone who opened the widget and bought a
          // 3-pack still counted as abandoned and would have been emailed
          // "you left a session unbooked" an hour after paying. The event
          // means "finished something in the widget", not "booked an
          // appointment"; Type carries which, so a flow can still single out
          // appointments when it wants to.
          try { sendKlaviyoBookingCompleted(res, c, purchaseTypeLabel(), state.cartId); } catch (e) { /* never surface */ }
          trackPurchaseComplete(itemName);
          showSuccess('You’re all set!', itemName + ' — a receipt will be sent to ' + c.email + '.');
        });
      }


      /**
       * Meta's own browser identifiers, and nothing else.
       *
       * `_fbp` is the pixel's browser cookie; `_fbc` is the click id, which
       * the pixel writes only after someone arrives on an ad link. When
       * somebody lands with ?fbclid= and the pixel has not written _fbc yet,
       * build it in Meta's documented shape rather than lose the attribution.
       *
       * NO email, NO phone, NO name. WTHN is a healthcare provider and an
       * event naming someone a new patient, matched on their contact details,
       * is the pattern behind the health-system pixel litigation. Meta can
       * credit the campaign from these two values; it learns nothing about
       * the person it did not already know.
       */
      function metaBrowserIds() {
        function cookie(name) {
          var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
          return m ? decodeURIComponent(m.pop()) : null;
        }
        var fbp = cookie('_fbp');
        var fbc = cookie('_fbc');
        if (!fbc) {
          try {
            var clid = new URLSearchParams(window.location.search).get('fbclid');
            if (clid) fbc = 'fb.1.' + Date.now() + '.' + clid;
          } catch (e) { /* older browsers: just go without it */ }
        }
        return { fbc: fbc, fbp: fbp };
      }

      // Sent from the SERVER, not with klaviyoTrack, and that is the point.
      // The recovery flow is filtered on "has not completed", so a completion
      // lost to an ad blocker or a closed tab means emailing someone who
      // already booked -- the one failure that makes the whole flow worse than
      // nothing. The started event can afford to be best-effort; this cannot.
      // Same post-booking rules as the Meta call: never awaited, never able to
      // reach the customer.
      // Named from the bucket the customer came through, so a flow or a
      // report can tell an appointment from a package without parsing
      // product names.
      function purchaseTypeLabel() {
        var key = (state.bucket && state.bucket.key) || '';
        if (key === 'buy-a-package' || key === 'first-time-deals') return 'Package';
        if (key === 'become-a-member') return 'Membership';
        if (key === 'gift-cards') return 'Gift Card';
        return 'Purchase';
      }

      function sendKlaviyoBookingCompleted(res, contact, type, cartId) {
        if (!contact || !contact.email) return;
        var price = null;
        try {
          if (state.item && state.item.listPriceRange) price = state.item.listPriceRange.min / 100;
        } catch (e) { /* price is a nicety, never a blocker */ }
        try {
          fetch(API_URL.replace(/\/booking$/, '/klaviyo-hook'), {
            method: 'POST', headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              email: contact.email,
              type: type || 'Appointment',
              // An appointment has its own id; a package or gift card does
              // not, so the spent cart stands in. Either way one completed
              // purchase records exactly one event.
              appointmentId: (res && res.appointmentId) || cartId || null,
              studio: (state.location && state.location.name) || null,
              treatment: (state.item && state.item.name) || null,
              value: price,
            }),
            keepalive: true
          }).catch(function () {});
        } catch (e) { /* never surface */ }
        // Clears the abandoned-booking properties so a later campaign cannot
        // chase someone who has already booked, even if the flow filter is
        // mis-built. Belt and braces, and free.
        klaviyoIdentify({ 'Last Booking Step': 'completed' });
      }

      function sendMetaFirstAppointment(res) {
        var ids = metaBrowserIds();
        if (!ids.fbc && !ids.fbp) return;            // nothing to attribute to
        if (!res || !res.clientId) return;           // nothing to look up
        var body = JSON.stringify({
          clientId: res.clientId,
          appointmentId: res.appointmentId || null,
          fbc: ids.fbc, fbp: ids.fbp,
          eventSourceUrl: window.location.href.split('?')[0],
          // Which service was booked. WTHN defines a first-time client by the
          // "First Time WTHN Client ..." menu item everywhere else, and that is
          // a stronger signal than Boulevard's appointmentCount, which lags the
          // booking. Display name, so it reflects RENAME_ITEM_NAMES.
          serviceName: (state.item && state.item.name) || null
        });
        // keepalive so it survives the success screen replacing the form.
        // Deliberately NOT awaited and NOT chained: a booking is done, and
        // this must never delay or fail the confirmation the customer sees.
        try {
          fetch(API_URL.replace(/\/booking$/, '/meta-capi-hook'), {
            method: 'POST', headers: { 'content-type': 'application/json' },
            body: body, keepalive: true
          }).catch(function () {});
        } catch (e) { /* never surface */ }
      }

      // Own the validation instead of leaning on the browser's `required`.
      // Shipped 2026-09-08 relying on native validation, and customers rang
      // the studios saying they could not book: with nine required fields on
      // one screen, an incomplete card silently blocked the submit. iOS
      // Safari shows no message for that at all, so tapping Confirm did
      // visibly nothing — and because the request never left the browser,
      // there was no server error, no log line and no alert. The form is
      // marked novalidate so this always runs and always says something.
      // contactOnly: validate just the four contact fields, for the promo gate
      // on the merged screen — the code cannot be evaluated without them, but
      // the card is irrelevant at that point.
      // Accepts every expiry form a browser or a phone actually produces:
      //   09/30  09/2030  0930  092030  9/30  09-30  09 / 30  2030-09
      //
      // autocomplete="cc-exp" is SPECIFIED as MM/YYYY, so Safari and Chrome
      // autofill a four-digit year -- which the original MM/YY-only parser
      // rejected, telling the customer that the value their own browser had
      // just filled in was wrong. On a phone that is a dead end rather than
      // an annoyance: the field carries inputmode="numeric", so iOS shows a
      // digits-only keypad with no "/" key and they cannot retype it either.
      //
      // Measured on 2026-09-12, the first full day with one flow serving all
      // traffic: 16% of mobile checkouts hit a validation block against 2.3%
      // of desktop, where a full keyboard lets people recover. Mobile was 67%
      // of sessions and converted checkout -> booked at 31% against desktop's
      // 45%. Reproduced on the live site the same week: "09/2030" -> blocked.
      function parseExpiry(v) {
        var raw = String(v || '').trim();
        var d = raw.replace(/\D/g, '');
        var mm, yy;
        if (/^\d{4}\D\d{1,2}$/.test(raw)) {            // YYYY-MM
          mm = Number(d.slice(4));
          yy = Number(d.slice(2, 4));
        } else if (d.length === 3) {                    // MYY
          mm = Number(d.slice(0, 1)); yy = Number(d.slice(1));
        } else if (d.length === 4) {                    // MMYY
          mm = Number(d.slice(0, 2)); yy = Number(d.slice(2));
        } else if (d.length === 5) {                    // MYYYY
          mm = Number(d.slice(0, 1)); yy = Number(d.slice(3));
        } else if (d.length === 6) {                    // MMYYYY
          mm = Number(d.slice(0, 2)); yy = Number(d.slice(4));
        } else return null;
        return { mm: mm, yy: yy };
      }

      // Shows the customer MM/YY whatever they or their browser supplied, so
      // a four-digit autofill does not sit there looking like a typo.
      function normalizeExpiryField(input) {
        if (!input) return;
        function fmt() {
          var e = parseExpiry(input.value);
          if (!e) return;
          var t = ('0' + e.mm).slice(-2) + '/' + ('0' + e.yy).slice(-2);
          if (input.value !== t) input.value = t;
        }
        // 'change' catches autofill, which does not always fire 'input';
        // 'blur' catches a half-typed value the customer has moved away from.
        input.addEventListener('change', fmt);
        input.addEventListener('blur', fmt);
        input.addEventListener('input', function () {
          // Only reformat once it is unambiguous, or typing "1" would jump to
          // "01/" and fight the customer mid-entry.
          if (input.value.replace(/\D/g, '').length >= 4) fmt();
        });
      }

      function firstCheckoutProblem(form, useSaved, contactOnly) {
        function digits(v) { return (v || '').replace(/\D/g, ''); }
        var checks = [
          ['firstName', function (v) { return v.trim() ? null : 'Enter your first name.'; }],
          ['lastName', function (v) { return v.trim() ? null : 'Enter your last name.'; }],
          ['email', function (v) {
            if (!v.trim()) return 'Enter your email address.';
            return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim()) ? null : 'That email address doesn’t look right.';
          }],
          ['phoneNumber', function (v) {
            if (!digits(v)) return 'Enter your phone number.';
            return digits(v).length >= 10 ? null : 'That phone number is too short.';
          }],
        ];
        if (!contactOnly && !useSaved) {
          checks = checks.concat([
            ['name', function (v) { return v.trim() ? null : 'Enter the name on your card.'; }],
            ['number', function (v) {
              if (!digits(v)) return 'Enter your card number.';
              var n = digits(v).length;
              return n >= 13 && n <= 19 ? null : 'That card number isn’t the right length.';
            }],
            ['exp', function (v) {
              var e = parseExpiry(v);
              if (!e) return 'Enter the expiry as MM/YY.';
              if (!(e.mm >= 1 && e.mm <= 12)) return 'That expiry month isn’t valid.';
              return new Date(2000 + e.yy, e.mm, 1) > new Date() ? null : 'That card looks expired.';
            }],
            ['cvv', function (v) {
              if (!digits(v)) return 'Enter the security code from your card.';
              var n = digits(v).length;
              return n >= 3 && n <= 4 ? null : 'The security code is 3 or 4 digits.';
            }],
            ['zip', function (v) { return v.trim().length >= 5 ? null : 'Enter your billing zip code.'; }],
          ]);
        }
        for (var i = 0; i < checks.length; i++) {
          var el = form.querySelector('[name="' + checks[i][0] + '"]');
          if (!el) continue;
          var problem = checks[i][1](el.value);
          if (problem) return { el: el, message: problem };
        }
        return null;
      }

      // Cancellation terms, stated with the customer's own deadline rather
      // than "24 hours" in the abstract — the same way Boulevard's widget
      // puts a real date and time in front of them.
      function cancellationCopyHtml() {
        if (!state.time || !state.location) return '';
        var deadline = new Date(new Date(state.time.startTime).getTime() - 24 * 3600 * 1000);
        var tz = state.location.tz;
        var day = deadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: tz });
        var clock = deadline.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz, timeZoneName: 'short' });
        return '<h3 class="blvd-subhead">Cancellation policy</h3>' +
          '<p class="blvd-legal">Free cancellation or modification before ' + escapeHtml(day) + ' at ' + escapeHtml(clock) + '. ' +
          'After that, changes to the appointment will result in a charge of $50.00 plus any applicable taxes and fees. ' +
          '<a href="https://wthn.com/pages/faq" target="_blank" rel="noopener">Learn more</a></p>';
      }

      // Consent language for texts and email. Not optional to display: this
      // is the disclosure that makes the SMS opt-in it describes lawful.
      function communicationsCopyHtml() {
        return '<h3 class="blvd-subhead">Communications</h3>' +
          '<p class="blvd-legal">By booking this appointment, you agree to receive texts and emails with appointment ' +
          'reminders, account updates, news, and special offers. Texts will be sent via auto-SMS. Consent is optional. ' +
          'You can unsubscribe from an email anytime by clicking unsubscribe, and opt out of marketing texts anytime by ' +
          'replying NO PROMOS or all text communication by replying STOP. Text HELP for more info. Message frequency ' +
          'may vary. SMS and data rates may apply.</p>';
      }


      // The message has to sit beside the field it is about. Putting it only
      // at the bottom of the form failed on mobile: scrolling to the
      // offending field pushed the message off-screen, so the customer saw
      // the cursor jump with no explanation — measured on a 375x812 viewport
      // 2026-09-08. Anchoring it to the field and scrolling to the message
      // keeps both in view, since they are adjacent.
      function showFieldProblem(problem, summaryEl) {
        // A customer who cannot get past this screen is otherwise INVISIBLE:
        // the request never leaves the browser, so there is no server error,
        // no log line and no alert. That is precisely why the 2026-09-08
        // silent-validation outage was found by a customer phoning a studio
        // rather than by us.
        // NOTE on how to read it: trackBookingStep de-duplicates every step
        // except 'error' per modal open, so this fires AT MOST ONCE per
        // visitor per session no matter how many times they are blocked. The
        // metric is therefore "share of checkouts where someone was blocked
        // at least once" — never a repeat count, and it can never exceed 1.0
        // per checkout.
        trackBookingStep('validation_block', { booking_error: String(problem.message).slice(0, 100) });
        // GA4 carries the message as an event PARAMETER, which is unqueryable
        // until someone registers a custom dimension in GA4 Admin -- and our
        // GA4 token is read-only. That gap is why the expiry dead end showed
        // up as "16% of mobile checkouts are blocked" with no way to see what
        // was blocking them. Beaconing the text to our own backend answers it
        // without waiting on anyone. Logged, never alerted -- see the kind
        // argument on reportClientError.
        reportClientError(
          (problem.el.getAttribute('name') || 'field') + ': ' + problem.message,
          'validation'
        );
        summaryEl.textContent = '';
        summaryEl.className = 'blvd-promo__result';
        var inline = document.getElementById('blvd-field-error');
        if (!inline) {
          inline = document.createElement('p');
          inline.id = 'blvd-field-error';
          inline.className = 'blvd-field-error';
          inline.setAttribute('role', 'alert');
        }
        inline.textContent = problem.message;
        var host = problem.el.closest('label') || problem.el;
        host.parentNode.insertBefore(inline, host.nextSibling);
        Array.prototype.forEach.call(document.querySelectorAll('.blvd-form .is-invalid'),
          function (e) { e.classList.remove('is-invalid'); });
        problem.el.classList.add('is-invalid');
        problem.el.focus({ preventScroll: true });
        // Instant, not smooth. A smooth scroll is still animating when the
        // customer looks, and there is nothing pleasant about watching the
        // page glide toward the thing that is wrong. It also respects
        // prefers-reduced-motion for free.
        inline.scrollIntoView({ block: 'center' });
      }

      function showCheckout(backTo, isSessionBooking) {
        trackBookingStep('checkout');
        // Written to the PROFILE, so a campaign (not just a flow) can say
        // which treatment they were looking at. Also records how far they
        // got, which is the difference between "looked and left" and "was on
        // the payment screen" -- two audiences that deserve different copy.
        klaviyoIdentify({
          'Last Booking Studio': (state.location && state.location.name) || null,
          'Last Booking Treatment': (state.item && state.item.name) || null,
          'Last Booking Step': 'checkout',
          'Last Booking Resume Url': bookingResumeUrl(),
        });
        setBack(backTo);
        var c = state.contact || {};
        // LAST CHANCE to find their card. The fetch normally happens back at
        // select-service, but it is fire-and-forget: someone who moves
        // straight through, or whose request was slow or failed, would arrive
        // here and be asked to type a card Boulevard already holds -- the
        // exact failure the login prompt exists to prevent. On 12 Sep only 19
        // of 52 logged-in sessions had a card found, so this is not
        // hypothetical. Re-renders itself if one turns up; the guard inside
        // refreshSavedCards stops that recursing.
        if (loginSession.cartOwned && !(state.savedCards && state.savedCards.length)) {
          refreshSavedCards(function () { showCheckout(backTo, isSessionBooking); });
        }
        var savedCards = (state.savedCards && state.savedCards.length && !state.forceManualCard) ? state.savedCards : [];

        var addonTotal = 0;
        var addonLines = (state.selectedAddons || []).map(function (a) {
          var price = a.listPriceRange ? a.listPriceRange.min : 0;
          addonTotal += price;
          return '<dt>Add-on</dt><dd>' + escapeHtml(a.name) + ' (+' + escapeHtml(formatPrice(price)) + ')</dd>';
        }).join('');
        var guestLine = state.hasGuest
          ? '<dt>Also booking</dt><dd>' + escapeHtml(state.guestName || 'A guest') + '</dd>'
          : '';
        var basePrice = (isSessionBooking && state.itemDetail && state.itemDetail.listPriceRange)
          ? state.itemDetail.listPriceRange.min
          : (state.priceCents || 0);
        state.confirmBaseTotal = (state.serverSubtotal != null)
          ? state.serverSubtotal
          : (basePrice + addonTotal);

        function totalRowHtml() {
          var total = Math.max(0, state.confirmBaseTotal - (state.appliedDiscount || 0));
          var discountLine = state.appliedDiscount
            ? '<dt>Discount</dt><dd>−' + escapeHtml(formatPrice(state.appliedDiscount)) + '</dd>'
            : '';
          return discountLine +
            '<dt>Total</dt><dd><strong>' + escapeHtml(formatPrice(total)) + '</strong>' +
            (isSessionBooking ? '<span class="blvd-total-note"> due at the studio</span>' : '') + '</dd>' +
            (isSessionBooking ? membershipNoteHtml() : '');
        }

        var summary = '<dt>Studio</dt><dd>' + escapeHtml(state.location.name) + '</dd>';
        if (isSessionBooking) {
          var timeLabel = new Date(state.time.startTime).toLocaleString('en-US', {
            weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: state.location.tz,
          });
          summary += '<dt>Service</dt><dd>' + escapeHtml(state.service.name) + '</dd>' +
            addonLines + guestLine +
            '<dt>When</dt><dd>' + escapeHtml(timeLabel) + '</dd>' +
            '<dt>With</dt><dd>' + escapeHtml(currentStaffLabel()) + '</dd>';
        } else {
          summary += '<dt>Item</dt><dd>' + escapeHtml((state.item && state.item.name) || 'Purchase') + '</dd>';
        }
        summary += '<span id="blvd-total-rows">' + totalRowHtml() + '</span>';

        // CONTINUE THE OLD SERIES, deliberately reusing these two names.
        //
        // payment_saved_card and payment_card_entry used to fire on the
        // separate card SCREENS, which the merged checkout replaced -- so
        // they decayed to zero as the old arm ramped down (105, 73, 30, 4, 0)
        // and the "337 people typed a card, 0 used one on file" comparison
        // became unmeasurable at exactly the moment we needed to show it had
        // moved. Same meaning as before -- was the customer shown a card
        // form, or offered one on file -- so the series stays continuous.
        trackBookingStep(savedCards.length ? 'payment_saved_card' : 'payment_card_entry');
        var cardBlock = savedCards.length
          ? '<div class="blvd-saved-cards">' +
              savedCards.map(function (card, i) {
                return '<label class="blvd-radio">' +
                  '<input type="radio" name="savedcard" value="' + escapeHtml(card.id) + '"' + (i === 0 ? ' checked' : '') + '>' +
                  '<span>' + escapeHtml(card.brand || 'Card') + ' ending ' + escapeHtml(card.last4 || '••••') + '</span></label>';
              }).join('') +
              '<label class="blvd-radio"><input type="radio" name="savedcard" value="__new__">' +
              '<span>Use a different card</span></label>' +
            '</div>' +
            '<div class="blvd-payment-box" id="blvd-new-card" hidden>' + cardFieldsHtml() + '</div>'
          : '<div class="blvd-payment-box">' + cardFieldsHtml() + '</div>';

        var payLabel = isSessionBooking
          ? 'Reserve your spot'
          : ('Pay ' + formatPrice(Math.max(0, state.confirmBaseTotal - (state.appliedDiscount || 0))));
        var lockIcon = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>';

        body.innerHTML =
          '<h2 class="blvd-title">' + (isSessionBooking ? 'Confirm and book' : 'Checkout') + '</h2>' +
          '<div class="blvd-summary-box"><dl class="blvd-summary">' + summary + '</dl></div>' +
          '<form class="blvd-form" id="blvd-checkout-form" novalidate>' +
          '<h3 class="blvd-subhead">Your details</h3>' +
          '<div class="blvd-form-row">' +
          '<label>First name<input type="text" name="firstName" autocomplete="given-name" required placeholder="Jane" value="' + escapeHtml(c.firstName || '') + '"></label>' +
          '<label>Last name<input type="text" name="lastName" autocomplete="family-name" required placeholder="Doe" value="' + escapeHtml(c.lastName || '') + '"></label>' +
          '</div>' +
          '<label>Email<input type="email" name="email" autocomplete="email" required placeholder="jane@example.com" value="' + escapeHtml(c.email || '') + '"></label>' +
          '<label>Phone<input type="tel" name="phoneNumber" autocomplete="tel" required placeholder="(555) 555-5555" value="' + escapeHtml(c.phoneNumber || '') + '"></label>' +
          '<div class="blvd-promo">' +
          '<input type="text" class="blvd-promo__input" id="blvd-promo-code" placeholder="Promo or referral code">' +
          '<button type="button" class="blvd-btn blvd-btn--secondary blvd-promo__apply" id="blvd-promo-apply">Apply</button>' +
          '</div>' +
          '<p class="blvd-promo__result" id="blvd-promo-result"></p>' +
          '<h3 class="blvd-subhead">' + (isSessionBooking ? 'Card to hold the appointment' : 'Payment') + '</h3>' +
          savedCardPromptHtml() +
          cardBlock +
          '<p class="blvd-form-trust">' + lockIcon + ' ' +
          (isSessionBooking
            ? 'Your card is held to confirm the appointment — you’re charged at the studio, not now.'
            : 'Payment details go directly to Boulevard’s secure vault — never stored on our servers.') +
          '</p>' +
          (isSessionBooking ? cancellationCopyHtml() : '') +
          communicationsCopyHtml() +
          '<p class="blvd-promo__result" id="blvd-checkout-error"></p>' +
          '<div class="blvd-submit-row"><button type="submit" class="blvd-btn blvd-btn--primary">' + escapeHtml(payLabel) + '</button></div>' +
          '</form>';

        var form = document.getElementById('blvd-checkout-form');
        var errEl = document.getElementById('blvd-checkout-error');
        // Closes over this screen's own totalRowHtml.
        var redrawTotal = function () {
          var rows = document.getElementById('blvd-total-rows');
          if (rows) rows.innerHTML = totalRowHtml();
        };
        // The contact fields are on THIS screen, so there is nothing to look
        // up on render. Ask once the email is filled in and the form is
        // otherwise complete enough to identify them -- which is exactly when
        // they are looking at the total and reaching for a card. 'change'
        // rather than 'input' so this is one request, not one per keystroke.
        var checkoutForm = document.getElementById('blvd-checkout-form');
        if (checkoutForm && checkoutForm.email) {
          checkoutForm.email.addEventListener('change', function () {
            refreshMembership(checkoutForm, redrawTotal);
          });
        }
        wireSavedCardPrompt(function () { showCheckout(backTo, isSessionBooking); });

        var newCardBox = document.getElementById('blvd-new-card');
        if (newCardBox) {
          form.querySelectorAll('input[name="savedcard"]').forEach(function (r) {
            r.addEventListener('change', function () {
              var useNew = form.querySelector('input[name="savedcard"]:checked').value === '__new__';
              newCardBox.hidden = !useNew;
              newCardBox.querySelectorAll('input').forEach(function (i) { i.required = useNew; });
            });
          });
          newCardBox.querySelectorAll('input').forEach(function (i) { i.required = false; });
        }

        normalizeExpiryField(form.querySelector('[name="exp"]'));

        // The promo input sits inside the checkout form, so Enter there would
        // submit the whole booking. Apply the code instead.
        document.getElementById('blvd-promo-code').addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { e.preventDefault(); document.getElementById('blvd-promo-apply').click(); }
        });

        document.getElementById('blvd-promo-apply').addEventListener('click', function () {
          var codeInput = document.getElementById('blvd-promo-code');
          var result = document.getElementById('blvd-promo-result');
          var code = codeInput.value.trim();
          if (!code) return;
          // No gate. Try the code with whatever we have: the booking type is
          // checked first and answered immediately, and the person is checked
          // in the background. If the answer is inconclusive because we do not
          // know them yet, say nothing and retry on the blur handler below.
          var haveClient = syncContactFromForm(form);
          result.className = 'blvd-promo__result';
          result.textContent = 'Applying…';
          (haveClient ? ensureClientOnCart() : Promise.resolve())
            .then(function () { return api('apply-promo-code', { cartId: state.cartId, code: code }); })
            .then(function (data) { renderPromoResult(data, haveClient, code, result, redrawTotal); })
            .catch(function (err) {
              pendingPromoCode = null;
              result.className = 'blvd-promo__result blvd-promo__result--error';
              result.textContent = err.message || 'That code isn’t valid.';
            });
        });

        // The silent second look. Fires as the customer finishes each contact
        // field; the moment all four are valid and a code is waiting, the code
        // is re-checked against them without being asked to. blur does not
        // bubble, hence capture.
        form.addEventListener('blur', function () {
          if (!pendingPromoCode) return;
          if (!syncContactFromForm(form)) return;
          var code = pendingPromoCode;
          pendingPromoCode = null;
          var result = document.getElementById('blvd-promo-result');
          ensureClientOnCart()
            .then(function () { return api('apply-promo-code', { cartId: state.cartId, code: code }); })
            .then(function (data) { if (result) renderPromoResult(data, true, code, result, redrawTotal); })
            // Silent by design: they did not ask for this, so a failure here
            // must not produce a message about something they are not doing.
            .catch(function () {});
        }, true);

        form.addEventListener('submit', function (e) {
          e.preventDefault();
          // Same guard as the old card screen, and for the same reason: a
          // disabled button does not stop a second real submit event, and
          // three rapid submits once produced three tokenize calls.
          if (state.paymentSubmitting) return;

          state.contact = {
            firstName: form.firstName.value.trim(),
            lastName: form.lastName.value.trim(),
            email: form.email.value.trim(),
            phoneNumber: form.phoneNumber.value.trim(),
          };
          // Parity with the old contact screen: remember these so the next
          // booking on this device opens pre-filled. resetState seeds
          // state.contact from here via loadSavedContact().
          saveContact(state.contact);

          var useSaved = null;
          if (form.querySelector('input[name="savedcard"]:checked')) {
            var v = form.querySelector('input[name="savedcard"]:checked').value;
            if (v !== '__new__') useSaved = v;
          }

          var btn = form.querySelector('.blvd-btn--primary');
          function fail(message) {
            state.paymentSubmitting = false;
            btn.disabled = false;
            btn.textContent = payLabel;
            errEl.className = 'blvd-promo__result blvd-promo__result--error';
            errEl.textContent = message;
          }

          var problem = firstCheckoutProblem(form, useSaved);
          if (problem) { showFieldProblem(problem, errEl); return; }
          // AFTER validation, deliberately. Fired before it, an empty-form
          // submit counted as "typed a card" -- and 9% of checkouts hit a
          // validation block, so the one number meant to show returning
          // clients stopped retyping would have been inflated by the people
          // who never typed anything at all.
          trackBookingStep(useSaved ? 'paid_with_saved_card' : 'paid_with_typed_card');
          var stale = document.getElementById('blvd-field-error');
          if (stale) stale.remove();
          // Derived from parseExpiry, NOT from the field's text. The
          // normalizer rewrites the box to MM/YY on change/blur, but that is
          // a display courtesy -- a browser that autofills without firing
          // either event would otherwise have left "09/2030" here and sent
          // exp_year = 2000 + 2030 = 4030. Correctness cannot ride on an
          // event that may not fire.
          var exp = useSaved ? null : parseExpiry(form.exp.value);

          state.paymentSubmitting = true;
          btn.disabled = true;
          btn.textContent = 'Processing…';
          errEl.textContent = '';

          var attachCard = useSaved
            ? api('use-saved-card', { cartId: state.cartId, paymentMethodId: useSaved })
            : fetch(TOKENIZE_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  card: {
                    name: form.name.value.trim(),
                    number: form.number.value.replace(/\s+/g, ''),
                    cvv: form.cvv.value.trim(),
                    exp_month: exp.mm,
                    exp_year: 2000 + exp.yy,
                    address_postal_code: form.zip.value.trim(),
                  },
                }),
              })
                .then(function (res) { return res.json(); })
                .then(function (tokenData) {
                  if (!tokenData.success) throw new Error('Card could not be processed. Please check your details.');
                  return api('add-payment-method', { cartId: state.cartId, token: tokenData.token })
                    .catch(function () { throw new Error('That card couldn’t be charged. Please check your details and try again.'); });
                });

          attachCard
            .then(function () { return completeCheckout(isSessionBooking); })
            .then(function () { state.paymentSubmitting = false; })
            .catch(function (err) {
              // A lapsed session is the one failure that cannot just be
              // reported. The handle has a 30-minute life and someone can
              // easily spend that choosing a time, so it expires exactly
              // here: they press the button and Boulevard answers "please log
              // in again". Saying only that was a dead end -- the saved card
              // that had just failed was still offered, the prompt that would
              // let them log in was hidden (we still believed they were
              // signed in), and the button stayed live, so they could press
              // it forever and get the same sentence back.
              if (/log in again/i.test(err.message || '')) {
                var f = document.getElementById('blvd-checkout-form');
                if (f) {
                  var get = function (n) { return f[n] ? f[n].value.trim() : ''; };
                  state.contact = {
                    firstName: get('firstName'), lastName: get('lastName'),
                    email: get('email'), phoneNumber: get('phoneNumber'),
                  };
                }
                loginSession.loggedIn = false;
                loginSession.sessionId = null;
                loginSession.savedCards = [];
                clearSavedSession();
                state.sessionId = null;
                state.savedCards = [];
                state.paymentSubmitting = false;
                // Redraw so the unusable saved card is gone and "Use your
                // card on file" is back -- the thing the message asks for.
                showCheckout(backTo, isSessionBooking);
                var el = document.getElementById('blvd-checkout-error');
                if (el) {
                  el.className = 'blvd-promo__result blvd-promo__result--error';
                  el.textContent = 'Your sign-in expired. Enter your card, or log in again to use the one on file.';
                }
                return;
              }
              // Stay on this screen. Everything typed is still here, which
              // is the whole point of one screen instead of three.
              fail(err.message || 'Something went wrong. Please try again.');
            });
        });
      }

      // RETURNING-CLIENT PROMPT. Measured 5-11 Sep: 337 people typed a full
      // card number and ZERO used one on file. Boulevard rejects checkout on
      // a pay-at-studio session with no card attached
      // (CART_MISSING_ITEM_PAYMENT_METHOD), so the card field cannot be
      // removed -- recognising the customer is the only lever there is, and
      // it was fine print at the bottom of the screen where they had already
      // given up. Rendered on the studio screen, the menu AND the merged
      // start screen, because a deep link skips the studio screen and 408 of
      // that week's opens arrived that way. A shortcut, not a gate.
      var rememberedBarTracked = false;
      function returningPromptHtml() {
        // ONLY hidden once actually logged in. Deliberately not hidden for
        // someone whose contact details we happen to have saved: booking as
        // a guest stores a name and email, and stores NO card -- so that
        // customer still faces the full card entry this prompt exists to
        // remove. Contact details are the cheap half; the card is the half
        // people abandon over.
        if (loginSession.cartOwned) return '';
        // REMEMBERED, not logged in for this cart. A month is long enough that
        // silently resuming someone else's session on a shared phone is the
        // real risk of the 30-day window, so the widget says whose session it
        // is and offers one tap out of it. It is also honest about what is
        // still missing: the card needs the code, because the code is what
        // owns the cart the card sits on.
        if (loginSession.loggedIn) {
          if (!rememberedBarTracked) { rememberedBarTracked = true; trackBookingStep('login_remembered'); }
          var who = loginSession.firstName ? escapeHtml(loginSession.firstName) : 'you';
          // Same id as the unknown bar's button, so the studio-first wiring,
          // the pendingLogin deferral and the login_prompt_clicked baseline
          // all apply unchanged. The action is here and not only at checkout
          // because the membership voucher changes the PRICES on the menu --
          // which is the half Sylvia was charged for on 18 Sep.
          return '<div class="blvd-returning-bar blvd-returning-bar--known">' +
            '<span>Welcome back, <strong>' + who + '</strong> — ' +
            '<button type="button" class="blvd-inline-link" id="blvd-login-start">' +
            'use your saved card</button></span>' +
            '<button type="button" id="blvd-not-you">Not you?</button></div>';
        }
        // One bar instead of a button plus a paragraph. Copy is Spencer's,
        // verbatim. Same id, so the existing wiring and login_prompt_clicked
        // tracking are untouched and the 13.5% baseline stays comparable --
        // which is what will show whether this wording reads better or worse
        // than the "your card is saved" line it replaced.
        // Names the payoff instead of promising "a personalized experience".
        // The member who complained on 18 Sep was being asked to type a card
        // AND pay for a session her membership covers -- both of which ride on
        // being logged in -- so those two words are the whole reason to press
        // this. "Log in" is marked up as the action so it reads as one.
        return '<button type="button" class="blvd-returning-bar" id="blvd-login-start">' +
          '<strong>Returning client?</strong> <u>Log in</u> — your card and membership are already saved</button>';
      }

      // Saved cards live on the CART, not on the login. Logging in at the
      // start of the flow -- which is where the prompt now is -- happens
      // before a cart for the chosen studio exists, so the login came back
      // with nothing and the customer was recognised, greeted by name, and
      // still handed a blank card form (found in testing 2026-09-12).
      //
      // Called once a service is on the cart -- Boulevard exposes payment
      // methods per selected ITEM, so an empty cart genuinely has none.
      // Best-effort: a failure just means the card form, which is exactly
      // what happened before any of this.
      // Five mutually exclusive outcomes, each its own EVENT NAME rather than
      // a parameter on one name -- GA4 parameters cannot be queried until
      // someone registers a custom dimension, and our GA4 token is read-only,
      // so a parameter here would be as invisible as the thing it is meant to
      // explain. Names are countable tomorrow morning.
      //
      // The one that matters is saved_cards_missing_but_on_file: Boulevard
      // says this client holds a card and the cart is not offering it. That is
      // a returning customer being handed a blank form they did not need. The
      // others say the gap is something else -- an expired 30-minute session,
      // an upstream error, or a client who genuinely has no card.
      function refreshSavedCards(onFound) {
        if (!loginSession.cartOwned || !state.cartId || !state.sessionId) return;
        if (state.savedCards && state.savedCards.length) return;
        api('cart-payment-methods', { cartId: state.cartId, sessionId: state.sessionId })
          .then(function (data) {
            state.savedCards = loginSession.savedCards = (data && data.cards) || [];
            if (data && data.vouchers && data.vouchers.length) state.cartVouchers = data.vouchers;
            if (state.savedCards.length) {
              trackBookingStep('saved_cards_found_on_cart');
              if (onFound) onFound();
              return;
            }
            var why = (data && data.reason) || 'unknown';
            if (why === 'no-session') trackBookingStep('saved_cards_no_session');
            else if (why === 'error') trackBookingStep('saved_cards_error');
            else if (data && data.onFile) {
              // Boulevard holds a card for this client and the cart is not
              // offering it. Two very different causes hid behind one event
              // until 17 Sep, which is why 30 of these in three days could
              // not be acted on:
              //
              //   cart_replaced  -- this is NOT the cart the login code
              //     owned, so the card was never going to be visible. Ours
              //     to fix: something rebuilt the cart instead of moving it.
              //   on_owned_cart  -- it IS the owned cart and Boulevard still
              //     will not offer the card. Nothing in this repo can fix
              //     that; it means the stored card is unusable (expired, or
              //     held somewhere the cart cannot reach) and hasCardOnFile
              //     is telling us less than it appears to.
              //
              // Names are 27 characters on purpose: GA4 truncates an event
              // name at 40 and the prefix takes 13, which is how
              // saved_cards_missing_but_on_file became the unreadable
              // saved_cards_missing_but_on_ in the reports.
              if (data.ownedCart === false) trackBookingStep('cards_missing_cart_replaced');
              else if (data.ownedCart === true) trackBookingStep('cards_missing_on_owned_cart');
              // Sessions minted before the owned cart id was sealed in carry
              // no answer either way -- keep counting them under the old name
              // rather than filing them under a cause we did not establish.
              else trackBookingStep('saved_cards_missing_but_on_file');
            }
            else trackBookingStep('saved_cards_none_on_file');
          })
          .catch(function () {});
      }

      function wireReturningPrompt(backTo) {
        var notYou = document.getElementById('blvd-not-you');
        if (notYou) {
          notYou.addEventListener('click', function () {
            trackBookingStep('login_not_you');
            clearSavedSession();
            loginSession.sessionId = null;
            loginSession.loggedIn = false;
            loginSession.cartOwned = false;
            loginSession.firstName = '';
            loginSession.savedCards = [];
            state.sessionId = null;
            state.savedCards = [];
            rememberedBarTracked = false;
            backTo();
          });
        }
        var el = document.getElementById('blvd-login-start');
        if (!el) return;
        el.addEventListener('click', function () {
          trackBookingStep('login_prompt_clicked');
          // THE STUDIO HAS TO COME FIRST. Boulevard hands a client's saved
          // card and membership voucher only to the cart their login code
          // owned, and a cart can never change studio afterwards --
          // cartSetLocation answers CART_LOCATION_ALREADY_SET, and the code
          // cannot be replayed onto a second cart either (both confirmed
          // live, 18 and 20 Sep). So logging in from the studio picker, where
          // no studio is known yet, owned a cart at the fallback studio and
          // lost the card the moment they chose a different one. Measured
          // 9/19-9/20: 60 sessions where Boulevard held a card the cart would
          // not offer.
          //
          // So: remember that they want to log in, let them pick the studio
          // they are already looking at, and open the login as soon as that
          // studio's cart exists. One extra tap, on the screen they are on,
          // and the cart is built in the right place to begin with.
          if (!state.location) {
            state.pendingLogin = true;
            var bar = el.closest('.blvd-returning-bar') || el;
            bar.innerHTML = '<strong>Choose your studio below</strong> — then we\u2019ll log you in';
            trackBookingStep('login_needs_studio');
            return;
          }
          showLogin(backTo, backTo);
        });
      }

      function savedCardPromptHtml() {
        // Why cart OWNERSHIP and not the sign-in token: this prompt shipped
        // and was rolled back the same day (2026-09-09) because Boulevard
        // refuses the passwordless token for every client-scoped query
        // (myCreditCards, myAppointments) with a 302 to /login -- so a
        // logged-in client never got their card and was asked for one anyway.
        // Four real clients hit it. Ownership is how Boulevard's own widget
        // does it: the cart carries the stored cards and vouchers, so the
        // promise is one we can keep.
        if (loginSession.cartOwned) return '';
        // A BUTTON, not a text link. This sits right above the card fields
        // exactly so it is seen before anyone starts typing, but as 14px
        // underlined fine print it read as a footnote and got scrolled past
        // on the way to the card form -- measured 1385px down the confirm
        // screen, which on a phone is the moment someone is already reaching
        // for their wallet. Reuses the existing secondary button style, so
        // it carries the same weight as any other real choice on the screen.
        return '<button type="button" class="blvd-btn blvd-btn--secondary blvd-returning__btn" id="blvd-card-login">' +
          'Been here before? Use your saved card</button>' +
          '<p class="blvd-returning blvd-returning__or">or enter a card below</p>';
      }

      // `rerender` redraws whichever card screen called this, so a successful
      // login comes straight back to it — and by then state.savedCards is
      // populated, so the redraw lands on the saved-card choice instead of the
      // empty form. Cancelling returns to the same place.
      function wireSavedCardPrompt(rerender) {
        var el = document.getElementById('blvd-card-login');
        if (!el) return;
        el.addEventListener('click', function () {
          // On the merged checkout screen state.contact is only written at
          // submit, so leaving for the login screen without this would throw
          // away anything already typed and hand back an empty form.
          var form = document.getElementById('blvd-checkout-form');
          if (form) {
            var get = function (n) { return form[n] ? form[n].value.trim() : ''; };
            state.contact = {
              firstName: get('firstName'), lastName: get('lastName'),
              email: get('email'), phoneNumber: get('phoneNumber'),
            };
          }
          showLogin(rerender, rerender);
        });
      }

      function cardFieldsHtml() {
        return '<label>Name on card<input type="text" name="name" autocomplete="cc-name" required placeholder="Jane Doe"></label>' +
          '<label>Card number<input type="text" name="number" inputmode="numeric" autocomplete="cc-number" placeholder="1234 1234 1234 1234" required></label>' +
          '<div class="blvd-form-row">' +
          '<label>Expiry<input type="text" name="exp" autocomplete="cc-exp" placeholder="MM/YY" inputmode="numeric" required></label>' +
          '<label>CVV<input type="text" name="cvv" inputmode="numeric" autocomplete="cc-csc" placeholder="123" required></label>' +
          '</div>' +
          '<label>Billing zip<input type="text" name="zip" autocomplete="postal-code" inputmode="numeric" placeholder="10012" required></label>';
      }


      // iOS Chrome reports a stale DYNAMIC viewport in the moments right
      // after load — which is exactly when a deep link opens this modal. When
      // it does, `height: 100dvh` resolves to the TALL viewport (chrome
      // hidden), so the overlay extends behind the browser's bottom toolbar
      // and the sticky Continue/Book button ends up underneath it, where taps
      // land on the toolbar instead. Switching apps and back forces a
      // recalculation, which is why it appears to fix itself — reported live
      // 2026-09-08 from a deeplink-generator link.
      //
      // visualViewport is the actually-visible box and is not subject to that
      // staleness, so drive the height from it and leave 100dvh in the CSS as
      // the fallback for browsers without the API. It also shrinks when the
      // keyboard opens, which keeps the button above the keyboard on the
      // contact and card screens rather than behind it.
      var viewportSyncBound = false;
      function applyViewportHeight() {
        var vv = window.visualViewport;
        if (!vv || !overlay) return;
        // A backgrounded / not-yet-rendered tab reports height 0, and one of
        // the listeners below fires precisely then (visibilitychange). Writing
        // that through would collapse the modal to nothing and take booking
        // down on the spot, so an implausible measurement is ignored rather
        // than trusted — the last good height stays until a real one arrives.
        if (!(vv.height > 1)) return;
        overlay.style.height = vv.height + 'px';
        // Height alone was not enough (reported 2026-09-11: "Reserve your
        // spot" sometimes unclickable, hidden behind the browser's bottom
        // chrome). The overlay is position:fixed with inset:0, so its TOP is
        // pinned to the LAYOUT viewport -- which on iOS includes the strip
        // behind the URL bar and toolbar. Sizing it to the visible height
        // while its top stays on the layout viewport just moves the error to
        // the bottom edge: the last 40-80px of the modal, and the sticky
        // submit row living there, sit under the toolbar where taps hit the
        // browser instead of the button.
        //
        // offsetTop is how far the visible viewport has moved down from the
        // layout one, so setting it here lands the overlay exactly on what
        // the user can actually see. bottom must go to auto or inset:0 keeps
        // fighting for the bottom edge.
        overlay.style.top = vv.offsetTop + 'px';
        overlay.style.bottom = 'auto';
      }
      function startViewportSync() {
        if (!window.visualViewport) return;
        applyViewportHeight();
        // A deep link opens this modal during page load, before the browser
        // has settled its chrome — measuring only once there is measuring at
        // the worst possible moment. Re-apply across the first second so the
        // final value is the settled one.
        requestAnimationFrame(applyViewportHeight);
        setTimeout(applyViewportHeight, 150);
        setTimeout(applyViewportHeight, 600);
        if (viewportSyncBound) return;
        window.visualViewport.addEventListener('resize', applyViewportHeight);
        window.visualViewport.addEventListener('scroll', applyViewportHeight);
        // Belt and braces: some iOS builds move the chrome without emitting a
        // visualViewport resize. These cover the same change from other angles,
        // and visibilitychange in particular is the exact gesture that was
        // reported as the workaround — switching apps and coming back — so
        // that path now self-heals instead of depending on luck.
        window.addEventListener('resize', applyViewportHeight);
        window.addEventListener('orientationchange', applyViewportHeight);
        window.addEventListener('pageshow', applyViewportHeight);
        document.addEventListener('visibilitychange', applyViewportHeight);
        viewportSyncBound = true;
      }
      function stopViewportSync() {
        if (!window.visualViewport) return;
        window.visualViewport.removeEventListener('resize', applyViewportHeight);
        window.visualViewport.removeEventListener('scroll', applyViewportHeight);
        window.removeEventListener('resize', applyViewportHeight);
        window.removeEventListener('orientationchange', applyViewportHeight);
        window.removeEventListener('pageshow', applyViewportHeight);
        document.removeEventListener('visibilitychange', applyViewportHeight);
        viewportSyncBound = false;
        // Hand the geometry back to the stylesheet so a later open starts clean.
        if (overlay) {
          overlay.style.height = '';
          overlay.style.top = '';
          overlay.style.bottom = '';
        }
      }

      function openModal(locationId, bucketKey, treatment, itemId, staffId, condition, landing) {
        resetState();
        stepsSeen = {};
        trackGA4Event('book_now_click');
        trackBookingStep('arm_' + FLOW_ARM);
        defaultBanner.hidden = true;
        // Second deep-link dimension, alongside locationId: which of the 5
        // curated buckets to land on (see init()'s data-blvd-bucket). Valid
        // keys are book-a-session / buy-a-package / become-a-member /
        // first-time-deals / gift-cards — anything else is just ignored by
        // showMenu()'s lookup, same as an unrecognized locationId falls back
        // to showLocations(). A bucket with no locationId of its own defaults
        // to Flatiron rather than making the visitor pick a studio first —
        // see DEFAULT_LOCATION_ID above.
        state.deepLinkBucket = bucketKey || null;
        // Third and fourth dimensions — see enterBucket()'s deepTreatment/
        // deepItemId handling.
        state.deepLinkTreatment = treatment || null;
        state.deepLinkItemId = itemId || null;
          state.deepLinkStaffId = staffId || null;
        // Sixth dimension — not consumed/cleared anywhere the way the other
        // four are, since it isn't used to drive any screen; it just needs
        // to still be set when showMenu() calls referralSource() a moment
        // from now. See parseBlvdTarget()/referralSource()'s comments.
        state.deepLinkCondition = condition || null;
        if (bucketKey && !locationId) {
          locationId = DEFAULT_LOCATION_ID;
          state.locationWasDefaulted = true;
        }
        renderPill();
        overlay.classList.add('is-open');
        startViewportSync();
        overlay.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        // Focus was left on the page behind, so opening this said nothing to
        // a screen reader and a keyboard user had to tab the whole page to
        // reach it. Focus the dialog itself rather than its first control:
        // aria-label reads the dialog, and the first control is "Your
        // appointments", which is not what someone opening this wants.
        modalEl.focus();
        if (!locationId) {
          // `landing === false` means the caller is about to render its own
          // screen. Without it, "Your appointments" opened the modal, this
          // showLocations() fired its async locations fetch, and that fetch
          // resolved AFTER showMyAppointments() had drawn — clobbering it with
          // the studio picker. Verified on the sandbox 2026-09-09.
          if (landing !== false) showLocations();
          return;
        }
        // Deep-linking is now trivial and safe: it's our own DOM state, so
        // "change studio" is just the header pill re-rendering showMenu() —
        // none of the server-side stickiness that made this unsafe in the
        // iframe.
        setBack(null);
        renderLoading();
        getLocationsCached().then(function (locations) {
          // Boulevard's API returns full URNs (urn:blvd:Location:...) but
          // the theme's existing BOOK NOW buttons carry the bare id (see
          // text-columns-with-images.liquid's data-blvd-location-id field)
          // — match on either form rather than assuming which one a given
          // button was set up with.
          var loc = locations.filter(function (l) { return l.id === locationId || l.id.endsWith(locationId); })[0];
          if (loc) {
            state.location = loc;
            showMenu();
          } else {
            showLocations();
          }
        }).catch(function (err) { renderError(err.message, function () { openModal(locationId, bucketKey); }); });
      }

      function closeModal() {
        stopViewportSync();
        overlay.classList.remove('is-open');
        overlay.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        closeSwitcher();
      }

      function init() {
        // REVERTED 2026-09-10 to Boulevard's hosted widget. Online bookings
        // ran 40% below baseline for 12 days (48.9/day vs 81.9), and the
        // biggest structural gap -- returning clients cannot use the card
        // Boulevard already holds, because every client-scoped query 302s --
        // is a vendor bug we do not control. Front desk was fielding
        // complaints about re-entering cards and duplicate bookings.
        //
        // The custom widget is left intact and inert. Set this to false to
        // bring it straight back; everything it does still works.
        // Normally inert while the iframe is switched on -- EXCEPT that it
        // still owns item= links, which the iframe cannot express at all.
        // Nothing here is visible until one of those links is clicked (or the
        // page loads on such a hash), so the iframe stays the widget for all
        // other traffic.
        var IFRAME_OWNS_MOST = !!window.BLVD_USE_IFRAME;
        if (IFRAME_OWNS_MOST && !document.querySelector('a[href^="#blvd"][href*="item="]')
            && !(location.hash.indexOf('#blvd') === 0 && /[?&]item=/.test(location.hash))) {
          return;
        }
        overlay = document.getElementById('blvd-modal-overlay');
        modalEl = overlay.querySelector('.blvd-modal');
        body = document.getElementById('blvd-modal-body');
        backBtn = overlay.querySelector('.blvd-header__back');
        closeBtn = overlay.querySelector('.blvd-header__close');
        pillBtn = overlay.querySelector('.blvd-header__pill');
        switcherEl = overlay.querySelector('.blvd-switcher');
        defaultBanner = document.getElementById('blvd-default-banner');

        // Two ways a page can carry deep-link targets on a BOOK NOW/JOIN NOW
        // link: the data-blvd-location-id/data-blvd-bucket attributes (used
        // by text-columns-with-images.liquid, the only section with custom
        // fields for this), or query params tacked onto the href itself —
        // #blvd?location=<id>&bucket=<key> — which works from ANY section's
        // plain "button link" field with no schema changes, since every
        // section already lets an editor type an arbitrary URL there.
        var links = document.querySelectorAll('a[href^="#blvd"]');
        Array.prototype.forEach.call(links, function (link) {
          // Disjoint from the iframe's set: while the iframe is on we take
          // ONLY the item= links it cannot express.
          if (IFRAME_OWNS_MOST && !window.BLVD_CUSTOM_FOR(link.getAttribute('href'))) return;
          link.addEventListener('click', function (e) {
            e.preventDefault();
            var target = parseBlvdTarget(link.getAttribute('href'));
            var locationId = target.locationId || link.getAttribute('data-blvd-location-id');
            var bucketKey = target.bucketKey || link.getAttribute('data-blvd-bucket');
            openModal(locationId, bucketKey, target.treatment, target.itemId, target.staffId, target.condition);
          });
        });

        // A third way to arrive at a target: the URL itself already carries
        // #blvd?... on page load — an email or ad landing directly on a
        // specific bucket/studio, with no on-page button click involved.
        // Cleared from the URL immediately so a reload or back-navigation
        // doesn't reopen the modal on its own.
        if (location.hash.indexOf('#blvd') === 0
            && !(IFRAME_OWNS_MOST && !window.BLVD_CUSTOM_FOR(location.hash))) {
          var incoming = parseBlvdTarget(location.hash);
          history.replaceState(null, '', location.pathname + location.search);
          openModal(incoming.locationId, incoming.bucketKey, incoming.treatment, incoming.itemId, incoming.staffId, incoming.condition);
        }

        pillBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          toggleSwitcher();
        });
        // A click inside the switcher (e.g. picking a studio) can replace
        // switcherEl's innerHTML to show the confirm step — which detaches
        // the clicked element mid-bubble, so the document listener below
        // would see a target no longer "contained" by switcherEl and treat
        // it as an outside click, wiping the confirm panel it just
        // rendered. Stop it from ever reaching that listener at all.
        switcherEl.addEventListener('click', function (e) { e.stopPropagation(); });
        document.addEventListener('click', function (e) {
          if (!switcherEl.hidden && !switcherEl.contains(e.target) && e.target !== pillBtn) {
            closeSwitcher();
          }
        });

        closeBtn.addEventListener('click', closeModal);

        // "Your appointments" — in-widget for a logged-in client; sends them
        // through login first otherwise. Previously this was a hard link out
        // to blvd.app in a new tab, which dropped the client out of the flow.
        var myApptsBtn = document.getElementById('blvd-my-appts');
        if (myApptsBtn) {
          myApptsBtn.addEventListener('click', function () {
            // A new tab, not this one: someone can press this mid-booking and
            // a same-tab navigation would throw away their cart. Boulevard's
            // own widget links here in the same tab; we have a flow to protect
            // that it does not.
            if (!ACCOUNT_NATIVE) {
              window.open(BLVD_ACCOUNT_URL, '_blank', 'noopener');
              return;
            }
            // landing:false — open the shell only, so openModal does not race
            // its own studio-picker fetch against the screen we want.
            if (!overlay.classList.contains('is-open')) {
              openModal(null, null, null, null, null, null, false);
            }
            showMyAppointments();
          });
        }

        overlay.addEventListener('click', function (e) {
          if (e.target === overlay) closeModal();
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && overlay.classList.contains('is-open')) {
            // Escape while the native date picker is open used to close the
            // WHOLE booking modal: the browser dismisses its own picker, the
            // keydown still reaches document, and this closed everything
            // behind it -- so someone who opened the date picker and thought
            // better of it lost their whole booking. There is no API that
            // reports whether a native picker is open, but focus sitting on
            // the date input is the case that matters, and blurring it
            // dismisses the picker. Escape a second time (focus is elsewhere
            // by then) closes the modal as before, which is the usual
            // nested-dismiss behaviour.
            var onDatePicker = document.activeElement &&
              document.activeElement.classList &&
              document.activeElement.classList.contains('blvd-date-input');
            if (onDatePicker) { document.activeElement.blur(); return; }
            closeModal();
          }
          // aria-modal="true" tells assistive tech the rest of the page is
          // inert, but it does nothing to the tab order -- so Tab walked out
          // of the dialog and into a page the user cannot see, with no way
          // back but Shift+Tab through all of it. Wrap at both ends.
          if (e.key !== 'Tab' || !overlay.classList.contains('is-open')) return;
          var canFocus = [].filter.call(
            modalEl.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])'),
            function (el) { return el.offsetParent !== null; }
          );
          if (!canFocus.length) return;
          var first = canFocus[0];
          var last = canFocus[canFocus.length - 1];
          // Coming from the dialog itself (focused on open) Shift+Tab would
          // otherwise land on the page behind.
          if (e.shiftKey && (document.activeElement === first || document.activeElement === modalEl)) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        });
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
      } else {
        init();
      }
      // Last act: tells theme.liquid's watchdog the widget is alive. Without
      // it a file that loads but throws leaves every Book Now link inert and
      // nothing notices -- onerror only fires for a request that failed.
      window.BLVD_WIDGET_READY = true;
    })();
